import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRightCircleIcon, ChevronDownIcon, ChevronUpIcon, CubeIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import Button from './Button';
import { Select } from './Input';
import SearchableSelect from './SearchableSelect';
import PeseurPonctuelClientModal from './PeseurPonctuelClientModal';
import PeseurQuickProductModal from './PeseurQuickProductModal';
import NumericInput from './NumericInput';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useTarifConfig, mapTsOptionsForSelect } from '../hooks/useTarifConfig';
import { upsertTsMetaInNotes, SUPPLEMENTARY_TAX_OPTIONS, getSupplementaryTaxOption, getSupplementaryTaxTotal } from '../utils/supplementaryTax';
import { formatWeight, formatDateTime, formatCurrency } from '../utils/helpers';
import { getApiErrorMessage } from '../utils/apiErrorMessages';
import {
  trucksAPI,
  driversAPI,
  transporteursAPI,
  productsAPI,
  loadsAPI,
  ddpsAPI,
  clientsAPI,
  systemConfigAPI
} from '../services/api';

const WEIGHING_FORCE_DRIVER_PONCTUAL_KEY = 'weighing.forceDriverOnPonctualTare';
const DEFERRED_GATE_PLATE_MIN_CHARS = 4;

/** Client company behind a DDP (operator = client user on behalf of that client). */
function getClientIdForDdp(ddp) {
  const op = ddp?.operator;
  if (!op) return null;
  return op.clientProfile?.id || op.assignedClient?.id || null;
}

/** Display name for search / dropdown (company first, then operator person name). */
function getClientDisplayNameForDdp(ddp) {
  const op = ddp?.operator;
  if (!op) return '';
  const company =
    op.clientProfile?.companyName?.trim() || op.assignedClient?.companyName?.trim() || '';
  if (company) return company;
  const person = [op.firstName, op.lastName].filter(Boolean).join(' ').trim();
  if (person) return person;
  return (op.username && String(op.username).trim()) || '';
}

/** One-line DDP label (same shape as SearchableSelect options in this modal). */
function formatDdpReadOnlyLabel(ddp, t) {
  if (!ddp) return '';
  const vesselLabel = ddp.navire || ddp.vesselName || '-';
  const manutentionLabel = ddp.manutentionNumber || '-';
  const productLabel = ddp.productsSummary || ddp.productName || t('weighing.noProduct');
  const transactionLabel = ddp.transactionType
    ? t(`loads.transactionTypes.${String(ddp.transactionType).toLowerCase()}`)
    : '';
  const base = `${vesselLabel} - ${manutentionLabel} - ${productLabel}${transactionLabel ? ` - ${transactionLabel}` : ''}`;
  const clientName = getClientDisplayNameForDdp(ddp);
  return clientName ? `${base} (${clientName})` : base;
}

function getTodayDateInputValue() {
  return new Date().toISOString().split('T')[0];
}

function getCurrentTimeInputValue() {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function toIsoFromDateAndTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const t = (timeStr && String(timeStr).trim()) || '00:00';
  return `${dateStr}T${t.length === 5 ? t : '00:00'}:00`;
}

/** Local calendar date + clock time from a Date instance. */
function dateToInputParts(d) {
  if (!d || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return { dateStr: `${y}-${mo}-${da}`, timeStr: `${hh}:${mm}` };
}

/** Add hours to a local date+time; returns new date/time input strings. */
function addHoursToDateTimeParts(dateStr, timeStr, hoursToAdd) {
  if (!dateStr) return { dateStr: dateStr || '', timeStr: timeStr || '' };
  const parts = (timeStr && String(timeStr).trim()) || '00:00';
  const [hRaw, mRaw = '0'] = parts.split(':');
  const h = Number(hRaw);
  const m = Number(mRaw);
  const d = new Date(
    Number(dateStr.slice(0, 4)),
    Number(dateStr.slice(5, 7)) - 1,
    Number(dateStr.slice(8, 10)),
    Number.isFinite(h) ? h : 0,
    Number.isFinite(m) ? m : 0,
    0,
    0
  );
  d.setHours(d.getHours() + (Number(hoursToAdd) || 0));
  const out = dateToInputParts(d);
  return out || { dateStr: dateStr, timeStr: parts };
}

/** Normalize plate for matching walk-in input to load.truck.plateNumber */
function normalizePlateForMatch(p) {
  return String(p || '')
    .replace(/[\s-]/g, '')
    .toUpperCase();
}

function isDeferredOpenLoadRow(l) {
  return Boolean(l && l.status === 'ACCEPTED' && !l.ddpId && !l.productId);
}

/** After GET /loads/:id, merge into the peseur in-progress list so UI does not show stale tare. */
function patchInProgressLoadRow(setInProgressLoads, load) {
  if (!load?.id) return;
  setInProgressLoads((prev) => {
    const i = prev.findIndex((row) => row.id === load.id);
    if (i < 0) return prev;
    const next = [...prev];
    next[i] = { ...next[i], ...load };
    return next;
  });
}

/** Tare label for second pass: load row kg (weighing record) wins over form/truck defaults. */
function formatContinuePassTareDisplay(loadRow, emptyWeightFormTon, formatWeightFn) {
  const kgFromLoad =
    loadRow?.emptyWeight != null && loadRow.emptyWeight !== ''
      ? Number(loadRow.emptyWeight)
      : NaN;
  if (Number.isFinite(kgFromLoad) && kgFromLoad > 0) {
    return formatWeightFn(kgFromLoad, 'ton');
  }
  const normalized = String(emptyWeightFormTon || '')
    .replace(/\s/g, '')
    .replace(',', '.');
  const rawFormTonnes = parseFloat(normalized);
  if (Number.isFinite(rawFormTonnes) && rawFormTonnes > 0) {
    return formatWeightFn(rawFormTonnes * 1000, 'ton');
  }
  return '—';
}

/** Load row has no tare weight stored on the load (kg); truck default does not count. */
function loadMissingRecordedTareWeight(l) {
  const ew = l?.emptyWeight;
  if (ew == null || ew === '') return true;
  const n = Number(ew);
  return !Number.isFinite(n) || n <= 0;
}

function isSelectableDdpStatus(status) {
  return String(status || '').trim().toLowerCase() === 'accepted';
}

const PESEUR_LAST_WEIGHBRIDGE_STORAGE_KEY = 'iportcloud:peseurLastWeighbridgeId';

function readStoredPeseurWeighbridgeId() {
  try {
    const v = window.localStorage?.getItem(PESEUR_LAST_WEIGHBRIDGE_STORAGE_KEY);
    return v && String(v).trim() ? String(v).trim() : '';
  } catch {
    return '';
  }
}

function writeStoredPeseurWeighbridgeId(id) {
  try {
    const s = id && String(id).trim();
    if (s) window.localStorage?.setItem(PESEUR_LAST_WEIGHBRIDGE_STORAGE_KEY, s);
    else window.localStorage?.removeItem(PESEUR_LAST_WEIGHBRIDGE_STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
}

/** Port / weighbridge / mole for peseur: single assignment, or last stored id if still valid, else first listed. */
function resolvePeseurWeighbridgeFields(wbs) {
  if (!Array.isArray(wbs) || wbs.length === 0) {
    return { portId: '', weighbridgeId: '', moleId: '' };
  }
  if (wbs.length === 1) {
    const wb = wbs[0];
    return {
      portId: wb.portId || wb.port?.id || '',
      weighbridgeId: wb.id || '',
      moleId: wb.moleId || wb.mole?.id || ''
    };
  }
  const lastId = readStoredPeseurWeighbridgeId();
  const match = lastId ? wbs.find((w) => w.id === lastId) : null;
  const wb = match || wbs[0];
  return {
    portId: wb.portId || wb.port?.id || '',
    weighbridgeId: wb.id || '',
    moleId: wb.moleId || wb.mole?.id || ''
  };
}

const AddTicketModal = ({
  isOpen,
  onClose,
  onSuccess,
  selectedTransporteurId,
  continueLoadId: continueLoadIdProp,
  continueFromLoadTareFirst = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isManagerOrAdmin, isOperator, isPeseur, getOperatorClient, getPeseurWeighbridges } = useAuth();
  const toast = useToast();
  const { tarifConfig } = useTarifConfig();
  const resolvedTsOptions = tarifConfig?.tsOptions || null;
  const tsSelectOptions = mapTsOptionsForSelect(resolvedTsOptions) ||
    SUPPLEMENTARY_TAX_OPTIONS.map((opt) => ({ value: opt.value, label: `${t(opt.labelKey)} (${opt.hourlyNetAmount.toLocaleString('fr-FR')} FCFA/h)` }));

  const [formData, setFormData] = useState(() => {
    const wbs = isPeseur() ? getPeseurWeighbridges() : [];
    const wb = wbs.length === 1 ? wbs[0] : null;
    const td = getTodayDateInputValue();
    const tt = getCurrentTimeInputValue();
    const loadedFromTare = addHoursToDateTimeParts(td, tt, 2);
    return {
      ddpId: '', // Required: DDP selection
      transporteurId: selectedTransporteurId || '',
      clientId: '', // Optional: selected client for this load
      truckId: '',
      driverId: '', // Driver selection when truck has multiple drivers
      productId: '',
      portId: wb?.portId || wb?.port?.id || '',
      weighbridgeId: wb?.id || '',
      moleId: wb?.moleId || wb?.mole?.id || '',
      emptyWeight: '',
      loadedWeight: '',
      transactionType: 'EXPORT',
      notes: '',
      scheduleDate: isOperator() ? '' : td,
      scheduleTime: isOperator() ? '' : tt,
      weighingPass: isPeseur() ? 'TARE' : 'FULL',
      continueLoadId: '',
      tareWeighDate: td,
      tareWeighTime: tt,
      loadedWeighDate: loadedFromTare.dateStr,
      loadedWeighTime: loadedFromTare.timeStr,
      peseurPonctuelMode: false,
      truckPlateNumber: '',
      /** STANDARD | DEFERRED_TARE — peseur: tare without DDP, bind client/DDP on second pass */
      creationFlow: 'STANDARD',
      driverFullName: '',
      driverPhone: ''
    };
  });

  const [inProgressLoads, setInProgressLoads] = useState([]);
  /** After first fetch for this modal open; avoids hiding "Type de passage" before we know if 2e pesées exist. */
  const [inProgressLoadsFetched, setInProgressLoadsFetched] = useState(false);
  /** Latest GET /loads/:id for continue second pass (avoids stale inProgressLoads tare). */
  const [continueLoadSnapshot, setContinueLoadSnapshot] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Dropdown data
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]); // Drivers assigned to selected truck
  const [products, setProducts] = useState([]);
  const [ddps, setDdps] = useState([]);
  
  const [loadingData, setLoadingData] = useState({
    ddps: false,
    trucks: false,
    drivers: false,
    products: false
  });

  const truckFetchSeqRef = useRef(0);
  const [ddpDetailsExpanded, setDdpDetailsExpanded] = useState(false);
  const [taxesAuPoidsExpanded, setTaxesAuPoidsExpanded] = useState(false);
  const [tsOptionCode, setTsOptionCode] = useState('');
  const [tsHours, setTsHours] = useState('0');

  const [clientBillingInfo, setClientBillingInfo] = useState(null);
  const [ponctuelClients, setPonctuelClients] = useState([]);
  const [loadingPonctuelClients, setLoadingPonctuelClients] = useState(false);
  /** Admin default true: require driver name/phone on ponctual first-pass tare. */
  const [forceDriverOnPonctualTare, setForceDriverOnPonctualTare] = useState(true);
  const [showPonctuelCreateModal, setShowPonctuelCreateModal] = useState(false);
  const [showQuickProductModal, setShowQuickProductModal] = useState(false);
  /** When set to `${truckId}:${loadId}`, the "pending 2nd pass" nudge for that truck+load is hidden until the truck changes. */
  const [secondPassNudgeDismissedKey, setSecondPassNudgeDismissedKey] = useState(null);
  /** Second pass is deferred-tare row (no DDP/product yet) — bind via bindAndWeigh */
  const [secondPassIsDeferred, setSecondPassIsDeferred] = useState(false);
  const [deferredClients, setDeferredClients] = useState([]);
  const [deferredPlateLookup, setDeferredPlateLookup] = useState('');
  const [deferredPlateLookupRefreshKey, setDeferredPlateLookupRefreshKey] = useState(0);
  const [deferredLookupLoading, setDeferredLookupLoading] = useState(false);
  /** Open deferred-tare loads matching matricule type-ahead (from GET /loads/open-tare-by-plate?partial=true). */
  const [deferredTareMatches, setDeferredTareMatches] = useState([]);
  /** REQUESTED loads matching matricule when no open tare exists at gate. */
  const [requestedGateMatches, setRequestedGateMatches] = useState([]);
  const [requestedGateLookupLoading, setRequestedGateLookupLoading] = useState(false);
  const [selectedGateRequestedLoadId, setSelectedGateRequestedLoadId] = useState('');
  /** Peseur: first screen is plate lookup (open deferred tare) before the full form. */
  const [peseurEntryGatePassed, setPeseurEntryGatePassed] = useState(
    () => !isPeseur() || Boolean(continueLoadIdProp)
  );
  const [peseurGatePlateMemory, setPeseurGatePlateMemory] = useState('');
  const [peseurGateNoMatch, setPeseurGateNoMatch] = useState(false);
  /** Fallback matches from truck records when no open deferred tare is found for typed plate. */
  const [knownGateTruckMatches, setKnownGateTruckMatches] = useState([]);
  const [knownGateTruckLoading, setKnownGateTruckLoading] = useState(false);
  const [knownGateSelectedTruckId, setKnownGateSelectedTruckId] = useState('');
  /** '__NEW__' means driver changed for this operation (manual/new). */
  const [knownGateSelectedDriverId, setKnownGateSelectedDriverId] = useState('');
  /** Persist chosen gate truck/driver into the DDP step without accidental overwrite. */
  const [gateDdpCarrySelection, setGateDdpCarrySelection] = useState(null);
  const knownGateSelectedTruckIdRef = useRef('');
  const knownGateSelectedDriverIdRef = useRef('');
  const [skipSingleDriverAutopickOnce, setSkipSingleDriverAutopickOnce] = useState(false);
  const [showKnownGateDriverModal, setShowKnownGateDriverModal] = useState(false);
  const [knownGateDriverForm, setKnownGateDriverForm] = useState({ firstName: '', lastName: '', phone: '' });
  const [knownGateDriverError, setKnownGateDriverError] = useState('');
  const [knownGateDriverSaving, setKnownGateDriverSaving] = useState(false);
  const [showClientTruckCreateModal, setShowClientTruckCreateModal] = useState(false);
  const [showClientDriverCreateModal, setShowClientDriverCreateModal] = useState(false);
  const [clientTruckCreateSaving, setClientTruckCreateSaving] = useState(false);
  const [clientDriverCreateSaving, setClientDriverCreateSaving] = useState(false);
  const [clientTruckCreateError, setClientTruckCreateError] = useState('');
  const [clientDriverCreateError, setClientDriverCreateError] = useState('');
  const [clientTruckForm, setClientTruckForm] = useState({ plateNumber: '' });
  const [clientDriverForm, setClientDriverForm] = useState({ firstName: '', lastName: '', phone: '' });
  /** Debounced: typed identity in “Add driver” matches an existing Driver (global). */
  const [clientDriverIdentityMatch, setClientDriverIdentityMatch] = useState(null);
  const [clientDriverIdentityMatchLoading, setClientDriverIdentityMatchLoading] = useState(false);
  const [knownGateDriverIdentityMatch, setKnownGateDriverIdentityMatch] = useState(null);
  const [knownGateDriverIdentityMatchLoading, setKnownGateDriverIdentityMatchLoading] = useState(false);
  const [insufficientPrepaidConfirm, setInsufficientPrepaidConfirm] = useState({
    open: false,
    message: '',
    variant: 'insufficient'
  });
  /** After gate: limit DDP list to PAYING or CREDIT when chosen from no-match actions. */
  const [peseurGateBillingFilter, setPeseurGateBillingFilter] = useState(null);
  /** True after "Client ponctuel" on entry gate — hide redundant ponctuel checkbox on the form. */
  const [ponctuelModeFromPeseurGate, setPonctuelModeFromPeseurGate] = useState(false);
  const peseurEntryGatePassedRef = useRef(peseurEntryGatePassed);
  const continueLoadIdPropRef = useRef(continueLoadIdProp);
  const deferredPlateLookupTimerRef = useRef(null);
  const deferredPlateLookupSeqRef = useRef(0);
  const emptyWeightInputRef = useRef(null);
  const loadedWeightInputRef = useRef(null);
  const productSelectRef = useRef(null);
  const tareWeightFocusKeyRef = useRef('');
  const secondPassLoadedWeightFocusKeyRef = useRef('');
  const prevClientIdForFocusRef = useRef('');
  const prevProductIdForFocusRef = useRef('');
  const insufficientPrepaidConfirmResolverRef = useRef(null);

  useEffect(() => {
    peseurEntryGatePassedRef.current = peseurEntryGatePassed;
  }, [peseurEntryGatePassed]);
  useEffect(() => {
    continueLoadIdPropRef.current = continueLoadIdProp;
  }, [continueLoadIdProp]);
  useEffect(() => {
    knownGateSelectedTruckIdRef.current = knownGateSelectedTruckId;
  }, [knownGateSelectedTruckId]);
  useEffect(() => {
    knownGateSelectedDriverIdRef.current = knownGateSelectedDriverId;
  }, [knownGateSelectedDriverId]);

  useEffect(() => {
    if (!isOpen || !showClientDriverCreateModal) {
      setClientDriverIdentityMatch(null);
      setClientDriverIdentityMatchLoading(false);
      return undefined;
    }
    const firstName = String(clientDriverForm.firstName || '').trim();
    const lastName = String(clientDriverForm.lastName || '').trim();
    const phone = String(clientDriverForm.phone || '').trim();
    const digits = phone.replace(/\D/g, '');
    if (!firstName || !lastName || digits.length < 7) {
      setClientDriverIdentityMatch(null);
      setClientDriverIdentityMatchLoading(false);
      return undefined;
    }
    let cancelled = false;
    setClientDriverIdentityMatchLoading(true);
    const timer = setTimeout(() => {
      driversAPI
        .match({ firstName, lastName, phone })
        .then((res) => {
          if (cancelled) return;
          const d = res?.data?.match === true ? res.data.driver : null;
          setClientDriverIdentityMatch(d || null);
        })
        .catch(() => {
          if (!cancelled) setClientDriverIdentityMatch(null);
        })
        .finally(() => {
          if (!cancelled) setClientDriverIdentityMatchLoading(false);
        });
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    isOpen,
    showClientDriverCreateModal,
    clientDriverForm.firstName,
    clientDriverForm.lastName,
    clientDriverForm.phone
  ]);

  useEffect(() => {
    if (!isOpen || !showKnownGateDriverModal) {
      setKnownGateDriverIdentityMatch(null);
      setKnownGateDriverIdentityMatchLoading(false);
      return undefined;
    }
    const firstName = String(knownGateDriverForm.firstName || '').trim();
    const lastName = String(knownGateDriverForm.lastName || '').trim();
    const phone = String(knownGateDriverForm.phone || '').trim();
    const digits = phone.replace(/\D/g, '');
    if (!firstName || !lastName || digits.length < 7) {
      setKnownGateDriverIdentityMatch(null);
      setKnownGateDriverIdentityMatchLoading(false);
      return undefined;
    }
    let cancelled = false;
    setKnownGateDriverIdentityMatchLoading(true);
    const timer = setTimeout(() => {
      driversAPI
        .match({ firstName, lastName, phone })
        .then((res) => {
          if (cancelled) return;
          const d = res?.data?.match === true ? res.data.driver : null;
          setKnownGateDriverIdentityMatch(d || null);
        })
        .catch(() => {
          if (!cancelled) setKnownGateDriverIdentityMatch(null);
        })
        .finally(() => {
          if (!cancelled) setKnownGateDriverIdentityMatchLoading(false);
        });
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    isOpen,
    showKnownGateDriverModal,
    knownGateDriverForm.firstName,
    knownGateDriverForm.lastName,
    knownGateDriverForm.phone
  ]);

  const requestInsufficientPrepaidBypassConfirmation = (message, variant = 'insufficient') =>
    new Promise((resolve) => {
      if (insufficientPrepaidConfirmResolverRef.current) {
        insufficientPrepaidConfirmResolverRef.current(false);
      }
      insufficientPrepaidConfirmResolverRef.current = resolve;
      setInsufficientPrepaidConfirm({
        open: true,
        message: String(message || ''),
        variant: variant === 'zero' ? 'zero' : 'insufficient'
      });
    });

  const resolveInsufficientPrepaidBypassConfirmation = (confirmed) => {
    const resolver = insufficientPrepaidConfirmResolverRef.current;
    insufficientPrepaidConfirmResolverRef.current = null;
    setInsufficientPrepaidConfirm({ open: false, message: '', variant: 'insufficient' });
    if (typeof resolver === 'function') resolver(Boolean(confirmed));
  };

  useEffect(() => {
    if (!isOpen || !isPeseur()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await systemConfigAPI.getByKey(WEIGHING_FORCE_DRIVER_PONCTUAL_KEY);
        if (!cancelled) setForceDriverOnPonctualTare(Boolean(res?.data?.value));
      } catch (e) {
        if (!cancelled && e?.response?.status === 404) {
          setForceDriverOnPonctualTare(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isPeseur]);

  // Load initial data
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      setDdpDetailsExpanded(false);
      setSecondPassNudgeDismissedKey(null);

      if (isPeseur() && !continueLoadIdProp) {
        setPeseurEntryGatePassed(false);
        setPonctuelModeFromPeseurGate(false);
        setPeseurGateNoMatch(false);
        setPeseurGatePlateMemory('');
        setSelectedGateRequestedLoadId('');
        setGateDdpCarrySelection(null);
        setKnownGateTruckMatches([]);
        setKnownGateSelectedTruckId('');
        setKnownGateSelectedDriverId('');
        setSkipSingleDriverAutopickOnce(false);
        setPeseurGateBillingFilter(null);
        setDeferredPlateLookup('');
        setDeferredTareMatches([]);
        setRequestedGateMatches([]);
        setRequestedGateLookupLoading(false);
        deferredPlateLookupSeqRef.current += 1;
      } else {
        setPeseurEntryGatePassed(true);
        setPonctuelModeFromPeseurGate(false);
        setSelectedGateRequestedLoadId('');
      }

      // Peseur: sync port / weighbridge / mole (single bridge, or last-used / first when several)
      if (isPeseur()) {
        const wbFields = resolvePeseurWeighbridgeFields(getPeseurWeighbridges());
        setFormData((prev) => ({ ...prev, ...wbFields }));
      }
      // Preselected transporteur: load its trucks when modal opens
      if (selectedTransporteurId) {
        setFormData(prev => ({ ...prev, transporteurId: selectedTransporteurId }));
        loadTrucksByTransporteur(selectedTransporteurId, null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialize modal state only when opening
  }, [isOpen, continueLoadIdProp]);

  useEffect(() => {
    if (!isOpen || !isPeseur()) {
      setInProgressLoads([]);
      setInProgressLoadsFetched(false);
      return;
    }
    let cancelled = false;
    setInProgressLoadsFetched(false);
    loadsAPI
      .getAll({ secondPassPending: 'true', limit: 100, excludeDelivered: 'true' })
      .then((res) => {
        if (!cancelled) setInProgressLoads(res.data?.loads || []);
      })
      .catch(() => {
        if (!cancelled) setInProgressLoads([]);
      })
      .finally(() => {
        if (!cancelled) setInProgressLoadsFetched(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isPeseur]);

  /** No pending 2e pesée in the site list → only tare makes sense; clear an accidental Marchandise selection. */
  useEffect(() => {
    if (!isOpen || !isPeseur() || !inProgressLoadsFetched) return;
    const needsPassChoice =
      Boolean(continueLoadIdProp) ||
      Boolean(String(formData.continueLoadId || '').trim()) ||
      (Array.isArray(inProgressLoads) && inProgressLoads.length > 0);
    if (!needsPassChoice && formData.weighingPass === 'MARCHANDISE') {
      setFormData((prev) => ({ ...prev, weighingPass: 'TARE', continueLoadId: '' }));
    }
  }, [
    isOpen,
    isPeseur,
    inProgressLoadsFetched,
    inProgressLoads,
    continueLoadIdProp,
    formData.continueLoadId,
    formData.weighingPass
  ]);

  useEffect(() => {
    if (!isOpen || !isPeseur() || formData.weighingPass !== 'MARCHANDISE' || !formData.continueLoadId) {
      setSecondPassIsDeferred(false);
      return;
    }
    const fromList = inProgressLoads.find((l) => l.id === formData.continueLoadId);
    if (fromList && !fromList.ddpId && !fromList.productId) {
      setSecondPassIsDeferred(true);
      return;
    }
    let cancelled = false;
    loadsAPI
      .getById(formData.continueLoadId)
      .then((res) => {
        const l = res?.data?.load;
        if (cancelled || !l) return;
        setSecondPassIsDeferred(!l.ddpId && !l.productId && l.status === 'ACCEPTED');
      })
      .catch(() => {
        if (!cancelled) setSecondPassIsDeferred(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isPeseur, formData.weighingPass, formData.continueLoadId, inProgressLoads]);

  useEffect(() => {
    if (!isOpen || !isPeseur() || !secondPassIsDeferred || formData.creationFlow !== 'DEFERRED_TARE') {
      setDeferredClients([]);
      return;
    }
    let cancelled = false;
    Promise.all([
      clientsAPI.getAll({ billingType: 'PAYING', limit: 300, activeOnly: true }),
      clientsAPI.getAll({ billingType: 'CREDIT', limit: 300, activeOnly: true }),
      clientsAPI.getAll({ billingType: 'PONCTUEL', limit: 300, activeOnly: true })
    ])
      .then(([payingRes, creditRes, ponctuelRes]) => {
        if (cancelled) return;
        const merged = [
          ...(payingRes?.data?.clients || []),
          ...(creditRes?.data?.clients || []),
          ...(ponctuelRes?.data?.clients || [])
        ];
        const byId = new Map();
        merged.forEach((c) => {
          if (c?.id) byId.set(c.id, c);
        });
        setDeferredClients(Array.from(byId.values()));
      })
      .catch(() => {
        if (!cancelled) setDeferredClients([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isPeseur, secondPassIsDeferred, formData.creationFlow]);

  /** Debounced type-ahead for open deferred-tare loads (matricule + driver on truck). */
  useEffect(() => {
    if (!isOpen || !isPeseur()) {
      return undefined;
    }
    const q = String(deferredPlateLookup || '').trim();
    const MIN_LEN = DEFERRED_GATE_PLATE_MIN_CHARS;
    if (q.length < MIN_LEN) {
      deferredPlateLookupSeqRef.current += 1;
      setDeferredTareMatches([]);
      setRequestedGateMatches([]);
      setRequestedGateLookupLoading(false);
      setDeferredLookupLoading(false);
      setKnownGateTruckMatches([]);
      setKnownGateTruckLoading(false);
      setKnownGateSelectedTruckId('');
      setKnownGateSelectedDriverId('');
      setSkipSingleDriverAutopickOnce(false);
      if (!peseurEntryGatePassedRef.current && !continueLoadIdPropRef.current) {
        setPeseurGateNoMatch(false);
      }
      return undefined;
    }
    if (deferredPlateLookupTimerRef.current) {
      clearTimeout(deferredPlateLookupTimerRef.current);
    }
    deferredPlateLookupTimerRef.current = setTimeout(() => {
      const requestId = ++deferredPlateLookupSeqRef.current;
      setDeferredLookupLoading(true);
      setKnownGateTruckLoading(false);
      loadsAPI
        .openTareByPlate(q, { partial: 'true' })
        .then((res) => {
          if (requestId !== deferredPlateLookupSeqRef.current) return;
          const list = res.data?.loads || [];
          setDeferredTareMatches(list);
          const onEntryGate = !peseurEntryGatePassedRef.current && !continueLoadIdPropRef.current;
          if (onEntryGate) {
            setPeseurGateNoMatch(list.length === 0);
            if (list.length > 0) {
              setRequestedGateMatches([]);
              setRequestedGateLookupLoading(false);
              setKnownGateTruckMatches([]);
              setKnownGateSelectedTruckId('');
              setKnownGateSelectedDriverId('');
            } else {
              setKnownGateTruckLoading(true);
              setRequestedGateLookupLoading(true);
              trucksAPI
                .getAll({ search: q, limit: 12, status: 'active' })
                .then((truckRes) => {
                  if (requestId !== deferredPlateLookupSeqRef.current) return;
                  const normalizedQ = normalizePlateForMatch(q);
                  const rows = (truckRes?.data?.trucks || []).filter((truck) =>
                    String(truck?.status || '').toLowerCase() === 'active' &&
                    normalizePlateForMatch(truck?.plateNumber).includes(normalizedQ)
                  );
                  setKnownGateTruckMatches(rows);
                  if (rows.length > 0) {
                    const prevTruckId = String(knownGateSelectedTruckIdRef.current || '').trim();
                    const prevDriverId = String(knownGateSelectedDriverIdRef.current || '').trim();
                    const selectedTruck =
                      rows.find((truck) => String(truck?.id || '') === prevTruckId) || rows[0];
                    const selectedTruckDriverPool = [
                      ...(selectedTruck?.drivers || []),
                      ...((selectedTruck?.assignedDrivers || []).map((row) => row?.driver).filter(Boolean))
                    ];
                    const selectedTruckDriverIds = new Set(
                      selectedTruckDriverPool.map((d) => String(d?.id || '').trim()).filter(Boolean)
                    );
                    const keepPrevDriver =
                      String(selectedTruck?.id || '') === prevTruckId &&
                      (prevDriverId === '__NEW__' || selectedTruckDriverIds.has(prevDriverId));
                    const firstDriverId =
                      selectedTruckDriverPool[0]?.id ||
                      selectedTruck?.drivers?.[0]?.id ||
                      selectedTruck?.assignedDrivers?.[0]?.driver?.id ||
                      '';
                    setKnownGateSelectedTruckId(String(selectedTruck?.id || '').trim());
                    setKnownGateSelectedDriverId(keepPrevDriver ? prevDriverId : (firstDriverId || '__NEW__'));
                  } else {
                    setKnownGateSelectedTruckId('');
                    setKnownGateSelectedDriverId('');
                    setSkipSingleDriverAutopickOnce(false);
                  }
                })
                .catch(() => {
                  if (requestId !== deferredPlateLookupSeqRef.current) return;
                  setKnownGateTruckMatches([]);
                  setKnownGateSelectedTruckId('');
                  setKnownGateSelectedDriverId('');
                  setSkipSingleDriverAutopickOnce(false);
                })
                .finally(() => {
                  if (requestId === deferredPlateLookupSeqRef.current) {
                    setKnownGateTruckLoading(false);
                  }
                });
              loadsAPI
                .requestedByPlate(q, { partial: 'true' })
                .then((requestedRes) => {
                  if (requestId !== deferredPlateLookupSeqRef.current) return;
                  const requested = requestedRes?.data?.loads || [];
                  setRequestedGateMatches(Array.isArray(requested) ? requested : []);
                })
                .catch(() => {
                  if (requestId !== deferredPlateLookupSeqRef.current) return;
                  setRequestedGateMatches([]);
                })
                .finally(() => {
                  if (requestId === deferredPlateLookupSeqRef.current) {
                    setRequestedGateLookupLoading(false);
                  }
                });
            }
          }
        })
        .catch(() => {
          if (requestId !== deferredPlateLookupSeqRef.current) return;
          setDeferredTareMatches([]);
          setRequestedGateMatches([]);
          setRequestedGateLookupLoading(false);
          setKnownGateTruckLoading(false);
          setKnownGateTruckMatches([]);
          setKnownGateSelectedTruckId('');
          setKnownGateSelectedDriverId('');
          setSkipSingleDriverAutopickOnce(false);
          if (!peseurEntryGatePassedRef.current && !continueLoadIdPropRef.current) {
            setPeseurGateNoMatch(true);
          }
        })
        .finally(() => {
          if (requestId === deferredPlateLookupSeqRef.current) {
            setDeferredLookupLoading(false);
          }
        });
    }, 320);
    return () => {
      if (deferredPlateLookupTimerRef.current) {
        clearTimeout(deferredPlateLookupTimerRef.current);
      }
    };
  }, [deferredPlateLookup, isOpen, isPeseur, deferredPlateLookupRefreshKey]);

  useEffect(() => {
    setSecondPassNudgeDismissedKey(null);
  }, [formData.truckId, formData.truckPlateNumber, formData.clientId]);

  useEffect(() => {
    if (!isOpen || !continueLoadIdProp || !isPeseur()) return;
    setContinueLoadSnapshot(null);

    if (continueFromLoadTareFirst) {
      const base = new Date();
      const tParts = dateToInputParts(base);
      const tDate = tParts?.dateStr ?? getTodayDateInputValue();
      const tTime = tParts?.timeStr ?? getCurrentTimeInputValue();
      const loadedParts = addHoursToDateTimeParts(tDate, tTime, 2);
      deferredPlateLookupSeqRef.current += 1;
      setFormData((prev) => ({
        ...prev,
        weighingPass: 'TARE',
        continueLoadId: continueLoadIdProp,
        creationFlow: 'STANDARD',
        peseurPonctuelMode: false,
        emptyWeight: '',
        loadedWeight: '',
        scheduleDate: tDate,
        scheduleTime: tTime,
        tareWeighDate: tDate,
        tareWeighTime: tTime,
        loadedWeighDate: loadedParts.dateStr,
        loadedWeighTime: loadedParts.timeStr
      }));
      let cancelled = false;
      loadsAPI
        .getById(continueLoadIdProp)
        .then((res) => {
          const l = res.data?.load;
          if (cancelled || !l) return;
          setContinueLoadSnapshot(l);
          patchInProgressLoadRow(setInProgressLoads, l);
          if (String(l.status || '').toUpperCase() !== 'ACCEPTED' || !loadMissingRecordedTareWeight(l)) {
            const resolvedDdpIdFallback = l.ddpId || l.ddp?.id || '';
            const baseFb =
              l.tareWeighedAt != null
                ? new Date(l.tareWeighedAt)
                : l.loadDate != null
                  ? new Date(l.loadDate)
                  : null;
            const tPartsFb = baseFb && !Number.isNaN(baseFb.getTime()) ? dateToInputParts(baseFb) : null;
            const tDateFb = tPartsFb?.dateStr ?? getTodayDateInputValue();
            const tTimeFb = tPartsFb?.timeStr ?? getCurrentTimeInputValue();
            const loadedPartsFb = addHoursToDateTimeParts(tDateFb, tTimeFb, 2);
            setFormData((prev) => ({
              ...prev,
              weighingPass: 'MARCHANDISE',
              continueLoadId: l.id,
              creationFlow: isDeferredOpenLoadRow(l) ? 'DEFERRED_TARE' : prev.creationFlow,
              ddpId: resolvedDdpIdFallback,
              peseurPonctuelMode: isDeferredOpenLoadRow(l) ? false : !resolvedDdpIdFallback,
              truckPlateNumber: resolvedDdpIdFallback
                ? ''
                : String(l.truck?.plateNumber || prev.truckPlateNumber || '').trim().toUpperCase(),
              truckId: l.truckId || '',
              transporteurId: l.transporteurId || '',
              clientId: l.clientId || '',
              productId: l.productId || '',
              driverId:
                l.driverId || l.driverIdDriver || l.driverEntity?.id || l.driver?.id || prev.driverId || '',
              portId: l.portId || '',
              weighbridgeId: l.weighbridgeId || '',
              moleId: l.moleId || '',
              emptyWeight: l.emptyWeight != null ? String(Number(l.emptyWeight) / 1000) : prev.emptyWeight,
              loadedWeight: '',
              transactionType: l.transactionType || prev.transactionType,
              scheduleDate: tDateFb,
              scheduleTime: tTimeFb,
              tareWeighDate: tDateFb,
              tareWeighTime: tTimeFb,
              loadedWeighDate: loadedPartsFb.dateStr,
              loadedWeighTime: loadedPartsFb.timeStr
            }));
            return;
          }
          const resolvedDdpId = l.ddpId || l.ddp?.id || '';
          setFormData((prev) => ({
            ...prev,
            weighingPass: 'TARE',
            continueLoadId: l.id,
            creationFlow: isDeferredOpenLoadRow(l) ? 'DEFERRED_TARE' : 'STANDARD',
            peseurPonctuelMode: isDeferredOpenLoadRow(l) ? false : !resolvedDdpId,
            ddpId: resolvedDdpId,
            truckPlateNumber: '',
            truckId: l.truckId || prev.truckId || '',
            transporteurId: l.transporteurId || prev.transporteurId || '',
            clientId: l.clientId || prev.clientId || '',
            productId: l.productId || prev.productId || '',
            driverId:
              l.driverId || l.driverIdDriver || l.driverEntity?.id || l.driver?.id || prev.driverId || '',
            portId: l.portId || prev.portId || '',
            weighbridgeId: l.weighbridgeId || prev.weighbridgeId || '',
            moleId: l.moleId || prev.moleId || '',
            emptyWeight: '',
            loadedWeight: '',
            transactionType: l.transactionType || prev.transactionType,
            scheduleDate: tDate,
            scheduleTime: tTime,
            tareWeighDate: tDate,
            tareWeighTime: tTime,
            loadedWeighDate: loadedParts.dateStr,
            loadedWeighTime: loadedParts.timeStr
          }));
          setDeferredTareMatches([]);
          setDeferredPlateLookup(String(l.truck?.plateNumber || '').trim().toUpperCase());
          setPonctuelModeFromPeseurGate(false);
          loadInitialData();
          loadTrucksByTransporteur(
            String(l.transporteurId || '').trim(),
            String(l.clientId || '').trim(),
            String(l.truckId || '').trim()
          );
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }

    setFormData((prev) => ({
      ...prev,
      weighingPass: 'MARCHANDISE',
      continueLoadId: continueLoadIdProp,
    }));
    let cancelled = false;
    loadsAPI
      .getById(continueLoadIdProp)
      .then((res) => {
        const l = res.data?.load;
        if (cancelled || !l) return;
        setContinueLoadSnapshot(l);
        patchInProgressLoadRow(setInProgressLoads, l);
        const resolvedDdpId = l.ddpId || l.ddp?.id || '';
        const base =
          l.tareWeighedAt != null
            ? new Date(l.tareWeighedAt)
            : l.loadDate != null
              ? new Date(l.loadDate)
              : null;
        const tParts = base && !Number.isNaN(base.getTime()) ? dateToInputParts(base) : null;
        const tDate = tParts?.dateStr ?? getTodayDateInputValue();
        const tTime = tParts?.timeStr ?? getCurrentTimeInputValue();
        const loadedParts = addHoursToDateTimeParts(tDate, tTime, 2);
        setFormData((prev) => ({
          ...prev,
          weighingPass: 'MARCHANDISE',
          continueLoadId: l.id,
          creationFlow: isDeferredOpenLoadRow(l) ? 'DEFERRED_TARE' : prev.creationFlow,
          ddpId: resolvedDdpId,
          peseurPonctuelMode: isDeferredOpenLoadRow(l) ? false : !resolvedDdpId,
          truckPlateNumber: resolvedDdpId
            ? ''
            : String(l.truck?.plateNumber || prev.truckPlateNumber || '').trim().toUpperCase(),
          truckId: l.truckId || '',
          transporteurId: l.transporteurId || '',
          clientId: l.clientId || '',
          productId: l.productId || '',
          driverId: l.driverId || l.driverIdDriver || l.driverEntity?.id || l.driver?.id || prev.driverId || '',
          portId: l.portId || '',
          weighbridgeId: l.weighbridgeId || '',
          moleId: l.moleId || '',
          emptyWeight: l.emptyWeight != null ? String(Number(l.emptyWeight) / 1000) : prev.emptyWeight,
          loadedWeight: '',
          transactionType: l.transactionType || prev.transactionType,
          scheduleDate: tDate,
          scheduleTime: tTime,
          tareWeighDate: tDate,
          tareWeighTime: tTime,
          loadedWeighDate: loadedParts.dateStr,
          loadedWeighTime: loadedParts.timeStr
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, continueLoadIdProp, isPeseur, continueFromLoadTareFirst]);

  useEffect(() => {
    if (!isOpen || !isPeseur()) return;
    if (continueFromLoadTareFirst) return;
    const requestToTareSelected =
      String(selectedGateRequestedLoadId || '').trim() !== '' &&
      String(formData.continueLoadId || '').trim() === String(selectedGateRequestedLoadId || '').trim();
    if (requestToTareSelected) return;
    const inferredSecondPass =
      Boolean(continueLoadIdProp) || Boolean(String(formData.continueLoadId || '').trim());
    if (inferredSecondPass && formData.weighingPass !== 'MARCHANDISE') {
      setFormData((prev) => ({ ...prev, weighingPass: 'MARCHANDISE' }));
    }
  }, [
    isOpen,
    isPeseur,
    continueLoadIdProp,
    continueFromLoadTareFirst,
    formData.continueLoadId,
    formData.weighingPass,
    selectedGateRequestedLoadId
  ]);

  useEffect(() => {
    if (!isOpen || !isPeseur() || formData.weighingPass !== 'MARCHANDISE' || !formData.continueLoadId) {
      return;
    }
    let cancelled = false;
    loadsAPI
      .getById(formData.continueLoadId)
      .then((res) => {
        const l = res.data?.load;
        if (cancelled || !l) return;
        setContinueLoadSnapshot(l);
        patchInProgressLoadRow(setInProgressLoads, l);
        setFormData((prev) => {
          const resolvedDdpId = l.ddpId || l.ddp?.id || prev.ddpId || '';
          const base =
            l.tareWeighedAt != null
              ? new Date(l.tareWeighedAt)
              : l.loadDate != null
                ? new Date(l.loadDate)
                : null;
          const tParts = base && !Number.isNaN(base.getTime()) ? dateToInputParts(base) : null;
          const tDate = tParts?.dateStr ?? prev.tareWeighDate;
          const tTime = tParts?.timeStr ?? prev.tareWeighTime;
          const loadedParts = addHoursToDateTimeParts(tDate, tTime, 2);
          return {
            ...prev,
            creationFlow: isDeferredOpenLoadRow(l) ? 'DEFERRED_TARE' : prev.creationFlow,
            ddpId: resolvedDdpId,
            peseurPonctuelMode: isDeferredOpenLoadRow(l) ? false : !resolvedDdpId,
            truckPlateNumber: resolvedDdpId
              ? ''
              : String(l.truck?.plateNumber || prev.truckPlateNumber || '').trim().toUpperCase(),
            truckId: l.truckId || prev.truckId,
            transporteurId: l.transporteurId || prev.transporteurId,
            clientId: l.clientId || prev.clientId,
            productId: l.productId || prev.productId,
            driverId: l.driverId || l.driverIdDriver || l.driverEntity?.id || l.driver?.id || prev.driverId || '',
            portId: l.portId || prev.portId,
            weighbridgeId: l.weighbridgeId || prev.weighbridgeId,
            moleId: l.moleId || prev.moleId,
            emptyWeight: l.emptyWeight != null ? String(Number(l.emptyWeight) / 1000) : prev.emptyWeight,
            transactionType: l.transactionType || prev.transactionType,
            scheduleDate: tDate,
            scheduleTime: tTime,
            tareWeighDate: tDate,
            tareWeighTime: tTime,
            loadedWeighDate: loadedParts.dateStr,
            loadedWeighTime: loadedParts.timeStr
          };
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.weighingPass, formData.continueLoadId, isPeseur]);

  // Update transporteurId when selectedTransporteurId changes
  useEffect(() => {
    if (isOpen) {
      if (isOperator()) {
        // For clients, automatically set their assigned client
        const operatorClient = getOperatorClient();
        if (operatorClient && operatorClient.id) {
          setFormData(prev => ({ ...prev, clientId: operatorClient.id }));
        }
      } else if (selectedTransporteurId && !isPeseur()) {
        setFormData(prev => ({ ...prev, transporteurId: selectedTransporteurId }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auth helpers are intentionally treated as stable for modal sync
  }, [isOpen, selectedTransporteurId]);

  // Load trucks from the selected DDP client context; transporteur is inferred from the chosen truck.
  useEffect(() => {
    if (formData.clientId) {
      const cid = formData.clientId && String(formData.clientId).trim() ? formData.clientId.trim() : null;
      loadTrucksByTransporteur('', cid);
    } else {
      truckFetchSeqRef.current += 1;
      setTrucks([]);
      setFormData(prev => ({ ...prev, truckId: '', transporteurId: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload trucks only when selected client changes
  }, [formData.clientId]);

  useEffect(() => {
    if (!isOpen || !isPeseur() || !formData.peseurPonctuelMode) {
      return;
    }
    let cancelled = false;
    setLoadingPonctuelClients(true);
    clientsAPI
      .getAll({ billingType: 'PONCTUEL', limit: 200, activeOnly: true })
      .then((res) => {
        if (!cancelled) {
          setPonctuelClients(res.data?.clients || []);
        }
      })
      .catch(() => {
        if (!cancelled) setPonctuelClients([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPonctuelClients(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isPeseur, formData.peseurPonctuelMode]);

  useEffect(() => {
    if (!isOpen || !formData.clientId || (!isPeseur() && !isManagerOrAdmin())) {
      setClientBillingInfo(null);
      return;
    }
    const cid = String(formData.clientId).trim();
    if (!cid) {
      setClientBillingInfo(null);
      return;
    }
    let cancelled = false;
    clientsAPI
      .getBillingStatus(cid)
      .then((data) => {
        if (!cancelled) setClientBillingInfo(data);
      })
      .catch(() => {
        if (!cancelled) setClientBillingInfo(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- role helpers from auth are stable enough for this UI fetch
  }, [isOpen, formData.clientId]);

  // Load drivers when truck is selected
  useEffect(() => {
    if (formData.truckId && trucks.length > 0) {
      const selectedTruck = trucks.find(truck => truck.id === formData.truckId);
      if (selectedTruck) {
        const byId = new Map();
        (selectedTruck.drivers || []).forEach((driver) => {
          if (driver?.id) byId.set(driver.id, driver);
        });
        (selectedTruck.assignedDrivers || []).forEach((dt) => {
          const driver = dt?.driver;
          if (driver?.id && !byId.has(driver.id)) {
            byId.set(driver.id, driver);
          }
        });
        const truckDrivers = Array.from(byId.values());
        setDrivers(truckDrivers);

        const carriedDriverId = String(gateDdpCarrySelection?.driverId || '').trim();
        setFormData((prev) => {
          const prevDriverId = String(prev.driverId || '').trim();
          const validPrev = truckDrivers.some((d) => d.id === prevDriverId);
          const validCarry = carriedDriverId && truckDrivers.some((d) => d.id === carriedDriverId);
          const preferredDriverId = validPrev ? prevDriverId : (validCarry ? carriedDriverId : '');

          // Keep explicit selection first; only auto-pick when truly unambiguous and no chosen driver.
          if (truckDrivers.length === 1) {
            if (preferredDriverId) {
              if (skipSingleDriverAutopickOnce) setSkipSingleDriverAutopickOnce(false);
              return { ...prev, driverId: preferredDriverId };
            }
            if (skipSingleDriverAutopickOnce) {
              setSkipSingleDriverAutopickOnce(false);
              return { ...prev, driverId: '' };
            }
            return { ...prev, driverId: truckDrivers[0].id };
          }

          if (truckDrivers.length > 1) {
            if (skipSingleDriverAutopickOnce) setSkipSingleDriverAutopickOnce(false);
            return { ...prev, driverId: preferredDriverId };
          }

          return { ...prev, driverId: '' };
        });
      } else {
        setDrivers([]);
      }
    } else {
      setDrivers([]);
      setFormData(prev => ({ ...prev, driverId: '' }));
    }
  }, [formData.truckId, trucks, skipSingleDriverAutopickOnce, gateDdpCarrySelection]);

  // Auto-populate empty weight when a registered truck is selected.
  // Ponctuel (walk-in) uses plate only — truckId stays empty; do not clear tare weight in that flow.
  // Continue second pass: tare is stored on the load row — never replace with truck master emptyWeight.
  useEffect(() => {
    const continueSecondPass =
      Boolean(continueLoadIdProp) ||
      Boolean(String(formData.continueLoadId || '').trim() && formData.weighingPass === 'MARCHANDISE');
    if (continueSecondPass) {
      return;
    }
    if (formData.truckId && trucks.length > 0) {
      const selectedTruck = trucks.find(truck => truck.id === formData.truckId);
      if (selectedTruck && selectedTruck.emptyWeight) {
        setFormData(prev => ({ 
          ...prev, 
          emptyWeight: (selectedTruck.emptyWeight / 1000).toString()
        }));
        // Clear any existing empty weight error since it's now auto-populated
        if (errors.emptyWeight) {
          setErrors(prev => ({ ...prev, emptyWeight: '' }));
        }
      }
    } else if (
      !formData.peseurPonctuelMode &&
      !(isPeseur() && formData.creationFlow === 'DEFERRED_TARE' && formData.weighingPass === 'TARE')
    ) {
      setFormData(prev => ({ ...prev, emptyWeight: '' }));
    }
  }, [
    continueLoadIdProp,
    formData.continueLoadId,
    formData.truckId,
    formData.peseurPonctuelMode,
    formData.creationFlow,
    formData.weighingPass,
    trucks,
    errors.emptyWeight,
    isPeseur
  ]);

  const selectedDdp = useMemo(
    () => ddps.find((ddp) => ddp.id === formData.ddpId) || null,
    [ddps, formData.ddpId]
  );

  const allowedDdpProductIds = useMemo(() => (
    Array.isArray(selectedDdp?.lineItems)
      ? [...new Set(selectedDdp.lineItems.map((line) => line.productId).filter(Boolean))]
      : []
  ), [selectedDdp]);

  const filteredProducts = useMemo(() => {
    if (allowedDdpProductIds.length === 0) return products;
    return products.filter((product) => allowedDdpProductIds.includes(product.id));
  }, [products, allowedDdpProductIds]);
  const shouldShowProductSelector = allowedDdpProductIds.length !== 1;

  const productSelectOptions = useMemo(() => {
    const base = formData.peseurPonctuelMode ? products : filteredProducts;
    const selectedId = formData.productId;
    if (!selectedId || base.some((p) => p.id === selectedId)) return base;
    const selected = products.find((p) => p.id === selectedId);
    return selected ? [selected, ...base] : base;
  }, [formData.peseurPonctuelMode, formData.productId, products, filteredProducts]);

  /** DDPs available for binding in deferred 2e pesée (all non-completed). */
  const filteredDdpsDeferred = useMemo(() => {
    return ddps.filter((d) => isSelectableDdpStatus(d?.status));
  }, [ddps]);

  const ddpsForSearchableSelect = useMemo(() => {
    return ddps.filter((d) => isSelectableDdpStatus(d?.status));
  }, [ddps]);

  useEffect(() => {
    if (!selectedDdp) return;
    setFormData((prev) => {
      const nextClientId = getClientIdForDdp(selectedDdp) || prev.clientId || '';
      const nextProductId = allowedDdpProductIds.includes(prev.productId)
        ? prev.productId
        : (allowedDdpProductIds[0] || '');
      const nextWeighbridgeId = selectedDdp.weighbridgeId || prev.weighbridgeId;
      const carryTruckId = String(gateDdpCarrySelection?.truckId || '').trim();
      const carryTransporteurId = String(gateDdpCarrySelection?.transporteurId || '').trim();
      const carryDriverId = String(gateDdpCarrySelection?.driverId || '').trim();
      return {
        ...prev,
        clientId: nextClientId,
        productId: nextProductId,
        truckId: carryTruckId || prev.truckId || '',
        transporteurId: carryTransporteurId || prev.transporteurId || '',
        driverId: carryDriverId || prev.driverId || '',
        portId: selectedDdp.portId || prev.portId,
        weighbridgeId: nextWeighbridgeId,
        transactionType: selectedDdp.transactionType || prev.transactionType,
        scheduleDate: isOperator() ? (prev.scheduleDate || '') : (prev.scheduleDate || getTodayDateInputValue()),
        scheduleTime: isOperator() ? (prev.scheduleTime || '') : (prev.scheduleTime || getCurrentTimeInputValue())
      };
    });
    if (isPeseur() && selectedDdp.weighbridgeId) {
      writeStoredPeseurWeighbridgeId(selectedDdp.weighbridgeId);
    }
    setDdpDetailsExpanded(true);
  }, [selectedDdp, allowedDdpProductIds, isPeseur, isOperator, gateDdpCarrySelection]);

  const loadInitialData = async () => {
    setLoadingData((prev) => ({ ...prev, ddps: true, products: true }));
    const [ddpOutcome, productsOutcome] = await Promise.allSettled([
      ddpsAPI.getMy(),
      productsAPI.getAll(),
    ]);

    if (ddpOutcome.status === 'fulfilled') {
      const allDdps = Array.isArray(ddpOutcome.value) ? ddpOutcome.value : [];
      const visibleDdps = isPeseur()
        ? allDdps
        : allDdps.filter((ddp) => isSelectableDdpStatus(ddp?.status));
      setDdps(visibleDdps);
    } else {
      console.error('Error loading DDPs:', ddpOutcome.reason);
      setDdps([]);
      toast.error(t('weighing.messages.failedToLoadFormData'));
    }

    if (productsOutcome.status === 'fulfilled') {
      const res = productsOutcome.value;
      const pdata = res?.data;
      setProducts(pdata?.products ? pdata.products : []);
    } else {
      console.error('Error loading products:', productsOutcome.reason);
      setProducts([]);
    }

    setLoadingData((prev) => ({ ...prev, ddps: false, products: false }));
  };

  const loadTrucksByTransporteur = async (transporteurId, clientIdForFilter, preferredTruckId = '') => {
    if (!transporteurId && !clientIdForFilter) {
      truckFetchSeqRef.current += 1;
      setTrucks([]);
      return;
    }
    const seq = ++truckFetchSeqRef.current;
    try {
      setLoadingData(prev => ({ ...prev, trucks: true }));
      const params = { limit: 500 };
      const cid = clientIdForFilter && String(clientIdForFilter).trim() ? String(clientIdForFilter).trim() : null;
      const tid = transporteurId && String(transporteurId).trim() ? String(transporteurId).trim() : null;
      if (cid) params.clientId = cid;
      if (tid) params.transporteurId = tid;
      params.status = 'active';
      const response = await trucksAPI.getAll(params);
      let trucks = response.data?.trucks || [];
      const selectedTruckId = String(preferredTruckId || formData.truckId || gateDdpCarrySelection?.truckId || '').trim();
      if (selectedTruckId && !trucks.some((truck) => String(truck?.id || '') === selectedTruckId)) {
        try {
          const selectedRes = await trucksAPI.getById(selectedTruckId);
          const selectedTruck = selectedRes?.data?.truck;
          if (selectedTruck?.id && String(selectedTruck.status || '').toLowerCase() === 'active') {
            trucks = [...trucks, selectedTruck];
          }
        } catch {
          // Ignore fallback fetch failure; keep original list.
        }
      }
      if (seq !== truckFetchSeqRef.current) return;
      setTrucks(trucks);
    } catch (error) {
      if (seq !== truckFetchSeqRef.current) return;
      console.error('Error loading trucks:', error);
      toast.error(t('weighing.messages.failedToFetchTrucks'));
      setTrucks([]);
    } finally {
      if (seq === truckFetchSeqRef.current) {
        setLoadingData(prev => ({ ...prev, trucks: false }));
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'emptyWeight' && !isPeseur()) {
      return;
    }
    if (name === 'loadedWeight' && isOperator()) {
      return;
    }

    if (name === 'creationFlow') {
      if (value === 'DEFERRED_TARE') {
        setPeseurGateBillingFilter(null);
      }
      setFormData((prev) => ({
        ...prev,
        creationFlow: value,
        ...(value === 'DEFERRED_TARE'
          ? {
              ddpId: '',
              truckId: '',
              transporteurId: '',
              productId: '',
              clientId: '',
              driverId: '',
              peseurPonctuelMode: false
            }
          : {}),
        ...(value === 'STANDARD' ? { driverFullName: '', driverPhone: '' } : {})
      }));
      if (errors.creationFlow) setErrors((prev) => ({ ...prev, creationFlow: '' }));
      return;
    }

    if (name === 'weighingPass') {
      setFormData((prev) => {
        const next = {
          ...prev,
          weighingPass: value,
          continueLoadId: value === 'MARCHANDISE' ? prev.continueLoadId : '',
          ...(value === 'TARE' ? { loadedWeight: '' } : {})
        };
        if (value === 'MARCHANDISE') {
          const lp = addHoursToDateTimeParts(prev.tareWeighDate, prev.tareWeighTime, 2);
          next.loadedWeighDate = lp.dateStr;
          next.loadedWeighTime = lp.timeStr;
        }
        return next;
      });
      if (errors.weighingPass) setErrors((prev) => ({ ...prev, weighingPass: '' }));
      if (value === 'TARE') {
        setErrors((prev) => ({ ...prev, loadedWeight: '' }));
        setTsOptionCode('');
        setTsHours('0');
        setTaxesAuPoidsExpanded(false);
      }
      return;
    }

    if (name === 'tareWeighDate' || name === 'tareWeighTime') {
      setFormData((prev) => {
        const tDate = name === 'tareWeighDate' ? value : prev.tareWeighDate;
        const tTime = name === 'tareWeighTime' ? value : prev.tareWeighTime;
        const next = {
          ...prev,
          [name]: value,
          scheduleDate: tDate,
          scheduleTime: tTime
        };
        return next;
      });
      setErrors((prev) => {
        const n = { ...prev };
        if (n.tareWeighDate) delete n.tareWeighDate;
        if (n.tareWeighTime) delete n.tareWeighTime;
        if (n.scheduleDate) delete n.scheduleDate;
        if (n.scheduleTime) delete n.scheduleTime;
        return n;
      });
      return;
    }

    if (name === 'scheduleDate' || name === 'scheduleTime') {
      setFormData((prev) => {
        const next = { ...prev, [name]: value };
        if (!isPeseur() && prev.weighingPass === 'FULL') {
          if (name === 'scheduleDate') {
            next.tareWeighDate = value;
            next.loadedWeighDate = value;
          } else {
            next.tareWeighTime = value;
            next.loadedWeighTime = value;
          }
        }
        return next;
      });
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    if (name === 'clientId') {
      setFormData((prev) => {
        const next = { ...prev, clientId: value };
        const deferredSecondBind =
          isPeseur() &&
          prev.creationFlow === 'DEFERRED_TARE' &&
          prev.weighingPass === 'MARCHANDISE' &&
          String(prev.continueLoadId || '').trim();
        if (deferredSecondBind && prev.clientId !== value) {
          next.ddpId = '';
          next.productId = '';
        }
        return next;
      });
      if (errors.clientId) {
        setErrors((prev) => ({ ...prev, clientId: '' }));
      }
      return;
    }

    if (name === 'ddpId' && value) {
      setFormData((prev) => ({ ...prev, ddpId: value, peseurPonctuelMode: false, truckPlateNumber: '' }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
      return;
    }

    if (name === 'weighbridgeId' && isPeseur()) {
      const wb = getPeseurWeighbridges().find((w) => w.id === value);
      setFormData((prev) => ({
        ...prev,
        weighbridgeId: value,
        portId: wb?.portId || wb?.port?.id || prev.portId,
        moleId: wb?.moleId || wb?.mole?.id || prev.moleId
      }));
      if (value) writeStoredPeseurWeighbridgeId(value);
      if (errors.weighbridgeId) setErrors((prev) => ({ ...prev, weighbridgeId: '' }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const renderPonctuelClientDropdownFooter = (buttonLabelKey, { disabled = false } = {}) =>
    ({ closeDropdown }) => (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full justify-center"
        onClick={() => {
          closeDropdown();
          setShowPonctuelCreateModal(true);
        }}
        disabled={disabled}
      >
        <UserPlusIcon className="h-4 w-4 mr-1.5" />
        {t(buttonLabelKey)}
      </Button>
    );

  const handleQuickProductCreated = (newProduct) => {
    if (!newProduct?.id) return;
    setProducts((prev) => {
      const exists = prev.some((p) => p.id === newProduct.id);
      const next = exists
        ? prev.map((p) => (p.id === newProduct.id ? newProduct : p))
        : [...prev, newProduct];
      return next.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    });
    setFormData((prev) => ({ ...prev, productId: newProduct.id }));
    setErrors((prev) => ({ ...prev, productId: '' }));
  };

  const handlePonctuelClientCreated = async (newClient) => {
    setLoadingPonctuelClients(true);
    try {
      const [ponRes, payingRes, creditRes] = await Promise.all([
        clientsAPI.getAll({ billingType: 'PONCTUEL', limit: 200, activeOnly: true }),
        clientsAPI.getAll({ billingType: 'PAYING', limit: 300, activeOnly: true }),
        clientsAPI.getAll({ billingType: 'CREDIT', limit: 300, activeOnly: true })
      ]);
      setPonctuelClients(ponRes.data?.clients || []);
      const merged = [
        ...(payingRes?.data?.clients || []),
        ...(creditRes?.data?.clients || []),
        ...(ponRes?.data?.clients || [])
      ];
      const byId = new Map();
      merged.forEach((c) => {
        if (c?.id) byId.set(c.id, c);
      });
      if (newClient?.id) {
        byId.set(newClient.id, newClient);
      }
      setDeferredClients(Array.from(byId.values()));
      if (newClient?.id) {
        setFormData((prev) => ({ ...prev, clientId: newClient.id, ddpId: '', productId: '' }));
        setErrors((prev) => ({ ...prev, clientId: '' }));
      }
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoadingPonctuelClients(false);
    }
  };

  const effectiveEmptyWeightTon = useMemo(() => {
    const continueId = String(formData.continueLoadId || '').trim();
    if (continueId) {
      const loadRow =
        continueLoadSnapshot?.id === continueId
          ? continueLoadSnapshot
          : inProgressLoads.find((l) => String(l?.id || '') === continueId);
      const kgFromLoad = Number(loadRow?.emptyWeight);
      if (Number.isFinite(kgFromLoad) && kgFromLoad > 0) {
        return kgFromLoad / 1000;
      }
    }
    const raw = String(formData.emptyWeight || '').replace(',', '.').trim();
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
    return NaN;
  }, [formData.emptyWeight, formData.continueLoadId, continueLoadSnapshot, inProgressLoads]);

  const loadedBelowTareWarning = (() => {
    if (!formData.loadedWeight || !Number.isFinite(effectiveEmptyWeightTon)) return null;
    const loadedNum = parseFloat(formData.loadedWeight);
    const emptyNum = effectiveEmptyWeightTon;
    if (!Number.isFinite(loadedNum) || !Number.isFinite(emptyNum) || loadedNum >= emptyNum) return null;
    return t('weighing.validations.loadedWeightGreater');
  })();

  const pickDeferredOpenTareFromGate = (load) => {
    if (!load?.id) return;
    const resolvedDdpId = load.ddpId || load.ddp?.id || '';
    const base =
      load.tareWeighedAt != null
        ? new Date(load.tareWeighedAt)
        : load.loadDate != null
          ? new Date(load.loadDate)
          : null;
    const tParts = base && !Number.isNaN(base.getTime()) ? dateToInputParts(base) : null;
    const tDate = tParts?.dateStr ?? getTodayDateInputValue();
    const tTime = tParts?.timeStr ?? getCurrentTimeInputValue();
    const loadedParts = addHoursToDateTimeParts(tDate, tTime, 2);
    deferredPlateLookupSeqRef.current += 1;
    setPeseurGateBillingFilter(null);
    setSelectedGateRequestedLoadId('');
    setPeseurEntryGatePassed(true);
    setPeseurGateNoMatch(false);
    setRequestedGateMatches([]);
    setKnownGateTruckMatches([]);
    setKnownGateSelectedTruckId('');
    setKnownGateSelectedDriverId('');
    setSkipSingleDriverAutopickOnce(false);
    setFormData((prev) => ({
      ...prev,
      weighingPass: 'MARCHANDISE',
      continueLoadId: load.id,
      creationFlow: isDeferredOpenLoadRow(load) ? 'DEFERRED_TARE' : 'STANDARD',
      peseurPonctuelMode: !resolvedDdpId,
      ddpId: resolvedDdpId,
      truckPlateNumber: resolvedDdpId
        ? ''
        : String(load.truck?.plateNumber || prev.truckPlateNumber || '').trim().toUpperCase(),
      truckId: load.truckId || prev.truckId || '',
      transporteurId: load.transporteurId || prev.transporteurId || '',
      clientId: load.clientId || prev.clientId || '',
      productId: load.productId || prev.productId || '',
      driverId: load.driverId || load.driverIdDriver || load.driverEntity?.id || load.driver?.id || prev.driverId || '',
      portId: load.portId || prev.portId || '',
      weighbridgeId: load.weighbridgeId || prev.weighbridgeId || '',
      moleId: load.moleId || prev.moleId || '',
      emptyWeight: load.emptyWeight != null ? String(Number(load.emptyWeight) / 1000) : prev.emptyWeight,
      loadedWeight: '',
      transactionType: load.transactionType || prev.transactionType,
      scheduleDate: tDate,
      scheduleTime: tTime,
      tareWeighDate: tDate,
      tareWeighTime: tTime,
      loadedWeighDate: loadedParts.dateStr,
      loadedWeighTime: loadedParts.timeStr,
    }));
    setDeferredTareMatches([]);
    setDeferredPlateLookup(String(load.truck?.plateNumber || '').trim().toUpperCase());
    setPonctuelModeFromPeseurGate(false);
  };

  const pickRequestedLoadFromGate = (load) => {
    if (!load?.id) return;
    const base = new Date();
    const tParts = dateToInputParts(base);
    const tDate = tParts?.dateStr ?? getTodayDateInputValue();
    const tTime = tParts?.timeStr ?? getCurrentTimeInputValue();
    const loadedParts = addHoursToDateTimeParts(tDate, tTime, 2);
    deferredPlateLookupSeqRef.current += 1;
    setPeseurGateBillingFilter(null);
    setSelectedGateRequestedLoadId(load.id);
    setPeseurEntryGatePassed(true);
    setPeseurGateNoMatch(false);
    setKnownGateTruckMatches([]);
    setKnownGateSelectedTruckId('');
    setKnownGateSelectedDriverId('');
    setSkipSingleDriverAutopickOnce(false);
    setRequestedGateMatches([]);
    setFormData((prev) => ({
      ...prev,
      weighingPass: 'TARE',
      continueLoadId: load.id,
      creationFlow: 'STANDARD',
      peseurPonctuelMode: false,
      ddpId: load.ddpId || load.ddp?.id || '',
      truckPlateNumber: '',
      truckId: load.truckId || prev.truckId || '',
      transporteurId: load.transporteurId || prev.transporteurId || '',
      clientId: load.clientId || prev.clientId || '',
      productId: load.productId || prev.productId || '',
      driverId: load.driverId || load.driverIdDriver || load.driverEntity?.id || load.driver?.id || prev.driverId || '',
      portId: load.portId || prev.portId || '',
      weighbridgeId: load.weighbridgeId || prev.weighbridgeId || '',
      moleId: load.moleId || prev.moleId || '',
      emptyWeight:
        load.emptyWeight != null && !Number.isNaN(Number(load.emptyWeight))
          ? String(Number(load.emptyWeight) / 1000)
          : '',
      loadedWeight: '',
      transactionType: load.transactionType || prev.transactionType,
      scheduleDate: tDate,
      scheduleTime: tTime,
      tareWeighDate: tDate,
      tareWeighTime: tTime,
      loadedWeighDate: loadedParts.dateStr,
      loadedWeighTime: loadedParts.timeStr
    }));
    setDeferredTareMatches([]);
    setDeferredPlateLookup(String(load.truck?.plateNumber || '').trim().toUpperCase());
    setPonctuelModeFromPeseurGate(false);
    loadInitialData();
    loadTrucksByTransporteur(
      String(load.transporteurId || '').trim(),
      String(load.clientId || '').trim(),
      String(load.truckId || '').trim()
    );
  };

  const handlePeseurGateProceedDdpByBilling = (billingType) => {
    const rememberedPlate = String(deferredPlateLookup || '').trim();
    if (rememberedPlate) {
      setPeseurGatePlateMemory(rememberedPlate);
    }
    const selectedTruckId = String(
      knownGateSelectedTruckIdRef.current || knownGateSelectedTruckId || ''
    ).trim();
    const selectedDriverId = String(
      knownGateSelectedDriverIdRef.current || knownGateSelectedDriverId || ''
    ).trim();
    const selectedTruck =
      knownGateTruckMatches.find((truck) => String(truck?.id || '').trim() === selectedTruckId) || null;
    setGateDdpCarrySelection(
      selectedTruckId
        ? {
            truckId: selectedTruckId,
            driverId: selectedDriverId && selectedDriverId !== '__NEW__' ? selectedDriverId : '',
            transporteurId: selectedTruck?.transporteurs?.[0]?.id || '',
            truckPlateNumber: String(selectedTruck?.plateNumber || '').trim()
          }
        : null
    );
    setPeseurGateNoMatch(false);
    setSelectedGateRequestedLoadId('');
    setPeseurEntryGatePassed(true);
    setPonctuelModeFromPeseurGate(false);
    setDeferredPlateLookup('');
    setPeseurGateBillingFilter(billingType);
    setSkipSingleDriverAutopickOnce(knownGateSelectedDriverId === '__NEW__');
    setFormData((prev) => ({
      ...applyKnownGateTruckPrefill({
        ...prev,
      creationFlow: 'STANDARD',
      weighingPass: 'TARE',
      continueLoadId: '',
      ddpId: '',
      peseurPonctuelMode: false,
      truckId: '',
      transporteurId: '',
      clientId: '',
      productId: '',
      driverId: '',
      truckPlateNumber: '',
      driverFullName: '',
      driverPhone: '',
      emptyWeight: '',
      loadedWeight: ''
      }, 'DDP')
    }));
    loadInitialData();
  };

  const handlePeseurGateProceedPonctuel = () => {
    const plate = String(deferredPlateLookup || '').trim();
    if (plate) {
      setPeseurGatePlateMemory(plate);
    }
    setPeseurGateNoMatch(false);
    setSelectedGateRequestedLoadId('');
    setGateDdpCarrySelection(null);
    setPeseurEntryGatePassed(true);
    setPonctuelModeFromPeseurGate(true);
    setDeferredPlateLookup('');
    setPeseurGateBillingFilter(null);
    setSkipSingleDriverAutopickOnce(false);
    setFormData((prev) => ({
      ...applyKnownGateTruckPrefill({
        ...prev,
      peseurPonctuelMode: true,
      creationFlow: 'STANDARD',
      weighingPass: 'TARE',
      continueLoadId: '',
      ddpId: '',
      truckId: '',
      transporteurId: '',
      clientId: '',
      productId: '',
      driverId: '',
      truckPlateNumber: plate,
      driverFullName: '',
      driverPhone: '',
      emptyWeight: '',
      loadedWeight: ''
      }, 'PONCTUEL')
    }));
    setDdps([]);
    setTrucks([]);
  };
  const handleBackToPeseurEntryGate = () => {
    const plate = String(formData.truckPlateNumber || peseurGatePlateMemory || deferredPlateLookup || '').trim();
    setPeseurEntryGatePassed(false);
    setPeseurGateNoMatch(true);
    setPonctuelModeFromPeseurGate(false);
    setSelectedGateRequestedLoadId('');
    setGateDdpCarrySelection(null);
    setPeseurGateBillingFilter(null);
    setKnownGateSelectedTruckId('');
    setKnownGateSelectedDriverId('');
    setSkipSingleDriverAutopickOnce(false);
    setDeferredPlateLookup(plate);
    setDeferredPlateLookupRefreshKey((prev) => prev + 1);
    setFormData((prev) => ({
      ...prev,
      creationFlow: 'STANDARD',
      weighingPass: 'TARE',
      continueLoadId: '',
      ddpId: '',
      peseurPonctuelMode: false,
      truckId: '',
      transporteurId: '',
      clientId: '',
      productId: '',
      driverId: '',
      driverFullName: '',
      driverPhone: '',
      emptyWeight: '',
      loadedWeight: ''
    }));
    setDdps([]);
    setTrucks([]);
  };

  const validateForm = () => {
    const newErrors = {};
    const ponctuelPeseur = isPeseur() && formData.peseurPonctuelMode;
    const deferredTareStep1 =
      isPeseur() &&
      formData.creationFlow === 'DEFERRED_TARE' &&
      formData.weighingPass === 'TARE' &&
      !String(formData.continueLoadId || '').trim();
    const selectedBillingTypeUpper = String(
      clientBillingInfo?.billingType ||
      deferredClients.find((c) => c?.id === String(formData.clientId || '').trim())?.billingType ||
      ponctuelClients.find((c) => c?.id === String(formData.clientId || '').trim())?.billingType ||
      ''
    ).toUpperCase();
    const selectedClientIsPonctuelForValidation = selectedBillingTypeUpper === 'PONCTUEL';
    const deferredMarchBind =
      isPeseur() &&
      formData.creationFlow === 'DEFERRED_TARE' &&
      formData.weighingPass === 'MARCHANDISE' &&
      Boolean(String(formData.continueLoadId || '').trim()) &&
      secondPassIsDeferred;
    const marchSkipHeaders =
      isPeseur() && formData.weighingPass === 'MARCHANDISE' && formData.continueLoadId;

    if (ponctuelPeseur) {
      if (formData.weighingPass === 'MARCHANDISE' && !formData.clientId?.trim()) {
        newErrors.clientId = t('weighing.ponctuel.clientRequired');
      }
      if (!formData.truckPlateNumber?.trim()) {
        newErrors.truckPlateNumber = t('weighing.ponctuel.plateRequired');
      }
      if (formData.weighingPass === 'TARE') {
        const dName = String(formData.driverFullName || '').trim();
        const dPhone = String(formData.driverPhone || '').trim();
        if (forceDriverOnPonctualTare) {
          if (!dName) {
            newErrors.driverFullName = t('weighing.deferred.driverFullNameRequired');
          }
          if (!dPhone) {
            newErrors.driverPhone = t('weighing.deferred.driverPhoneRequired');
          }
        } else if (dName || dPhone) {
          if (!dName) {
            newErrors.driverFullName = t('weighing.deferred.driverFullNameRequired');
          }
          if (!dPhone) {
            newErrors.driverPhone = t('weighing.deferred.driverPhoneRequired');
          }
        }
      }
    } else if (deferredTareStep1) {
      if (!formData.truckPlateNumber?.trim()) {
        newErrors.truckPlateNumber = t('weighing.ponctuel.plateRequired');
      }
      if (!formData.driverFullName?.trim()) {
        newErrors.driverFullName = t('weighing.deferred.driverFullNameRequired');
      }
      if (!formData.driverPhone?.trim()) {
        newErrors.driverPhone = t('weighing.deferred.driverPhoneRequired');
      }
      if (
        getPeseurWeighbridges().length > 1 &&
        !String(formData.weighbridgeId || '').trim()
      ) {
        newErrors.weighbridgeId = t('weighing.validations.weighbridgeRequired');
      }
    } else {
      if (
        isPeseur() &&
        formData.weighingPass === 'MARCHANDISE' &&
        !String(formData.continueLoadId || '').trim()
      ) {
        newErrors.continueLoadId = t('weighing.validations.continueLoadRequired');
      }
      if (!marchSkipHeaders && !formData.ddpId && !selectedClientIsPonctuelForValidation) {
        newErrors.ddpId = t('weighing.validations.ddpRequired');
      }

      if (!marchSkipHeaders && !formData.truckId) {
        newErrors.truckId = t('weighing.validations.truckRequired');
      }
    }

    // Validate driver selection if truck has multiple drivers
    if (!ponctuelPeseur && !deferredTareStep1 && !marchSkipHeaders && formData.truckId && drivers.length > 1 && !formData.driverId) {
      newErrors.driverId = t('weighing.validations.driverRequired');
    }

    if (
      !deferredTareStep1 &&
      !(ponctuelPeseur && formData.weighingPass === 'TARE') &&
      !formData.productId
    ) {
      newErrors.productId = t('weighing.validations.productRequired');
    }

    if (deferredMarchBind) {
      if (!String(formData.clientId || '').trim()) {
        newErrors.clientId = t('weighing.deferred.clientRequired');
      }
      if (!selectedClientIsPonctuelForValidation && !String(formData.ddpId || '').trim()) {
        newErrors.ddpId = t('weighing.validations.ddpRequired');
      }
      if (!String(formData.productId || '').trim()) {
        newErrors.productId = t('weighing.validations.productRequired');
      }
    }

    if (!isPeseur()) {
      if (!formData.portId) {
        newErrors.portId = t('weighing.validations.portRequired');
      }
      if (!formData.weighbridgeId) {
        newErrors.weighbridgeId = t('weighing.validations.weighbridgeRequired');
      }
    } else if (
      isPeseur() &&
      formData.peseurPonctuelMode &&
      getPeseurWeighbridges().length > 1 &&
      !formData.weighbridgeId
    ) {
      newErrors.weighbridgeId = t('weighing.validations.weighbridgeRequired');
    }

    // Schedule / pesée instant: operators & admins use schedule; peseurs use tare (+ loaded when applicable)
    if (isPeseur()) {
      if (formData.weighingPass === 'TARE') {
        if (!formData.tareWeighDate?.trim()) {
          newErrors.tareWeighDate = t('weighing.validations.scheduleDateRequired');
        }
        if (!formData.tareWeighTime?.trim()) {
          newErrors.tareWeighTime = t('common.requiredField', { defaultValue: 'This field is required' });
        }
      }
      if (formData.weighingPass === 'MARCHANDISE') {
        if (!formData.loadedWeighDate?.trim()) {
          newErrors.loadedWeighDate = t('weighing.validations.scheduleDateRequired');
        }
        if (!formData.loadedWeighTime?.trim()) {
          newErrors.loadedWeighTime = t('common.requiredField', { defaultValue: 'This field is required' });
        }
      }
    } else if (isOperator() || isManagerOrAdmin()) {
      if (!formData.scheduleDate) {
        newErrors.scheduleDate = t('weighing.validations.scheduleDateRequired');
      }
      if (!formData.scheduleTime) {
        newErrors.scheduleTime = t('common.requiredField', { defaultValue: 'This field is required' });
      }
    }
    
    // Weight validations - MANDATORY for Admin/Manager; peseur two-step modes vary
    const hasEmptyWeight = formData.emptyWeight && String(formData.emptyWeight).trim() !== '';
    const hasLoadedWeight = formData.loadedWeight && String(formData.loadedWeight).trim() !== '';
    /** Match UI: tare-only pass shows empty weight only — never require loaded weight here. */
    const peseurTareOnlyWeights =
      isPeseur() && formData.weighingPass === 'TARE' && (deferredTareStep1 || formData.creationFlow === 'STANDARD');

    if (isManagerOrAdmin() || isPeseur()) {
      if (peseurTareOnlyWeights) {
        if (!hasEmptyWeight) {
          newErrors.emptyWeight =
            !formData.truckId && !formData.peseurPonctuelMode && !deferredTareStep1
              ? t('weighing.validations.emptyWeightTruckRequired')
              : t('weighing.validations.emptyWeightRequired');
        } else if (isNaN(formData.emptyWeight) || parseFloat(formData.emptyWeight) <= 0) {
          newErrors.emptyWeight = t('weighing.validations.validWeightRequired');
        }
      } else if (
        isPeseur() &&
        formData.weighingPass === 'MARCHANDISE' &&
        formData.continueLoadId &&
        (formData.peseurPonctuelMode || formData.ddpId || deferredMarchBind)
      ) {
        if (!hasLoadedWeight) {
          newErrors.loadedWeight = t('weighing.validations.loadedWeightRequired');
        } else if (isNaN(formData.loadedWeight) || parseFloat(formData.loadedWeight) < 0) {
          newErrors.loadedWeight = t('weighing.validations.validWeightRequired');
        }
        if (hasEmptyWeight && hasLoadedWeight) {
          const emptyT = parseFloat(formData.emptyWeight);
          const loadedT = parseFloat(formData.loadedWeight);
          if (loadedT <= emptyT) {
            newErrors.loadedWeight = t('weighing.validations.loadedWeightGreater');
          }
        }
      } else {
        if (!hasEmptyWeight) {
          newErrors.emptyWeight = !formData.truckId
            ? t('weighing.validations.emptyWeightTruckRequired')
            : t('weighing.validations.emptyWeightRequired');
        } else if (isNaN(formData.emptyWeight) || parseFloat(formData.emptyWeight) < 0) {
          newErrors.emptyWeight = t('weighing.validations.validWeightRequired');
        }

        if (!hasLoadedWeight) {
          newErrors.loadedWeight = t('weighing.validations.loadedWeightRequired');
        } else if (isNaN(formData.loadedWeight) || parseFloat(formData.loadedWeight) < 0) {
          newErrors.loadedWeight = t('weighing.validations.validWeightRequired');
        }

        if (hasEmptyWeight && hasLoadedWeight) {
          const emptyWeight = parseFloat(formData.emptyWeight);
          const loadedWeight = parseFloat(formData.loadedWeight);
          if (loadedWeight <= emptyWeight) {
            newErrors.loadedWeight = t('weighing.validations.loadedWeightGreater');
          }
        }
      }
    }
    // For clients: No weight validation required (they create load requests)

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isPeseur() && !continueLoadIdProp && !peseurEntryGatePassed) {
      return;
    }

    if (!validateForm()) {
      toast.error(t('weighing.validations.checkHighlightedFields'));
      return;
    }

    setLoading(true);
    try {
      const parsePrepaidAmounts = (payload, fallbackMessage) => {
        const estimatedRaw = payload?.amountTtc;
        const availableRaw = payload?.availableBalance;
        if (Number.isFinite(Number(estimatedRaw)) && Number.isFinite(Number(availableRaw))) {
          return {
            estimated: Number(estimatedRaw),
            available: Number(availableRaw)
          };
        }
        const match = String(fallbackMessage || '').match(
          /Insufficient prepaid balance for this weighing\.\s*Estimated total:\s*(\d+)\s*(?:;|\.)\s*available:\s*(\d+)\.?/i
        );
        if (match) {
          return {
            estimated: Number(match[1]),
            available: Number(match[2])
          };
        }
        return null;
      };

      const buildInsufficientBalanceMessage = (payload, fallbackMessage) => {
        const parsed = parsePrepaidAmounts(payload, fallbackMessage);
        if (!parsed) return fallbackMessage;
        return t('weighing.messages.insufficientPrepaidBalance', {
          estimated: formatCurrency(parsed.estimated),
          available: formatCurrency(parsed.available)
        });
      };

      const runLoadMutationWithInsufficientPrepaidConfirm = async (executeRequest) => {
        try {
          return await executeRequest(false);
        } catch (firstError) {
          const firstData = firstError?.response?.data;
          const firstMessage =
            (typeof firstData?.detail === 'string' && firstData.detail.trim()) ||
            (typeof firstData?.message === 'string' && firstData.message) ||
            t('weighing.messages.failedToCreateTicket');
          const isConfirmableInsufficientPrepaid =
            isPeseur() &&
            (firstData?.code === 'PAYING_INSUFFICIENT_BALANCE_CONFIRM_REQUIRED' ||
              firstData?.code === 'PAYING_ZERO_BALANCE_CONFIRM_REQUIRED') &&
            firstData?.canBypass === true;
          if (!isConfirmableInsufficientPrepaid) {
            throw firstError;
          }
          const isZeroPrepaidConfirm = firstData?.code === 'PAYING_ZERO_BALANCE_CONFIRM_REQUIRED';
          const balanceMessage = isZeroPrepaidConfirm
            ? t('weighing.messages.zeroPrepaidConfirmBody', {
                estimated: formatCurrency(Number(firstData?.amountTtc) || 0),
                available: formatCurrency(Number(firstData?.availableBalance) || 0)
              })
            : buildInsufficientBalanceMessage(firstData, firstMessage);
          const shouldProceed = await requestInsufficientPrepaidBypassConfirmation(
            balanceMessage,
            isZeroPrepaidConfirm ? 'zero' : 'insufficient'
          );
          if (!shouldProceed) {
            setErrors({
              general: balanceMessage,
              billingErrorCode: String(firstData?.code || '')
            });
            toast.error(balanceMessage);
            return null;
          }
          return await executeRequest(true);
        }
      };

      /** Round 1: tare only, no DDP (peseur deferred flow). */
      const deferredTareRound1Submit =
        isPeseur() &&
        formData.creationFlow === 'DEFERRED_TARE' &&
        formData.weighingPass === 'TARE' &&
        !String(formData.continueLoadId || '').trim();
      if (deferredTareRound1Submit) {
        const tIso = toIsoFromDateAndTime(formData.tareWeighDate, formData.tareWeighTime);
        const res = await loadsAPI.peseurDeferredTare({
          truckPlateNumber: String(formData.truckPlateNumber || '').trim(),
          driverFullName: String(formData.driverFullName || '').trim(),
          driverPhone: String(formData.driverPhone || '').trim(),
          emptyWeight: parseFloat(formData.emptyWeight) * 1000,
          weighbridgeId: String(formData.weighbridgeId || '').trim(),
          transactionType: formData.transactionType,
          loadDate: tIso || undefined,
          tareWeighedAt: tIso || undefined,
          notes: formData.notes ? String(formData.notes).trim() : undefined
        });
        if (isPeseur() && String(formData.weighbridgeId || '').trim()) {
          writeStoredPeseurWeighbridgeId(formData.weighbridgeId);
        }
        toast.success(res?.data?.message || t('weighing.messages.deferredTareSaved'));
        onSuccess?.();
        handleClose();
        return;
      }

      /** Ponctuel walk-in: tare first without client (same backend as deferred tare). With client → POST /loads. */
      const ponctuelWalkInTareSubmit =
        isPeseur() &&
        formData.peseurPonctuelMode &&
        formData.weighingPass === 'TARE' &&
        !String(formData.clientId || '').trim();
      if (ponctuelWalkInTareSubmit) {
        const tIso = toIsoFromDateAndTime(formData.tareWeighDate, formData.tareWeighTime);
        const dName = String(formData.driverFullName || '').trim();
        const dPhone = String(formData.driverPhone || '').trim();
        const useCapturedDriver = Boolean(dName && dPhone);
        const res = await loadsAPI.peseurDeferredTare({
          truckPlateNumber: String(formData.truckPlateNumber || '').trim(),
          driverFullName: useCapturedDriver ? dName : 'Ponctuel',
          driverPhone: useCapturedDriver ? dPhone : '—',
          emptyWeight: parseFloat(formData.emptyWeight) * 1000,
          weighbridgeId: String(formData.weighbridgeId || '').trim(),
          transactionType: formData.transactionType,
          loadDate: tIso || undefined,
          tareWeighedAt: tIso || undefined,
          notes: formData.notes ? String(formData.notes).trim() : undefined
        });
        if (isPeseur() && String(formData.weighbridgeId || '').trim()) {
          writeStoredPeseurWeighbridgeId(formData.weighbridgeId);
        }
        toast.success(res?.data?.message || t('weighing.messages.deferredTareSaved'));
        onSuccess?.();
        handleClose();
        return;
      }

      // Prepare data for API call (transporteur is required; client optional for traceability)
      const submitData = {
        ...formData
      };
      if (isPeseur() && formData.peseurPonctuelMode) {
        submitData.ddpId = null;
        submitData.truckId = '';
        submitData.transporteurId = '';
        submitData.truckPlateNumber = String(formData.truckPlateNumber || '').trim();
      }
      delete submitData.peseurPonctuelMode;
      if (formData.clientId) {
        submitData.clientId = formData.clientId;
      } else if (isOperator() && getOperatorClient()?.id) {
        submitData.clientId = getOperatorClient().id;
      }

      // Peseur DDP flow: harden truck/transporteur carry-over from entry gate.
      if (isPeseur() && !formData.peseurPonctuelMode && String(submitData.ddpId || '').trim()) {
        const carryTruckId = String(gateDdpCarrySelection?.truckId || '').trim();
        const carryTransporteurId = String(gateDdpCarrySelection?.transporteurId || '').trim();
        if (!String(submitData.truckId || '').trim() && carryTruckId) {
          submitData.truckId = carryTruckId;
        }
        if (!String(submitData.transporteurId || '').trim()) {
          submitData.transporteurId = carryTransporteurId;
        }
        if (String(submitData.truckId || '').trim() && !String(submitData.transporteurId || '').trim()) {
          let selectedTruck = trucks.find((truck) => truck.id === submitData.truckId);
          if (!selectedTruck) {
            try {
              const selectedRes = await trucksAPI.getById(submitData.truckId);
              selectedTruck = selectedRes?.data?.truck || null;
            } catch (e) {
              // Ignore lookup failure and keep fallback behavior below.
            }
          }
          const selectedClientId = String(submitData.clientId || '').trim();
          const matchingTransporteur =
            (selectedTruck?.transporteurs || []).find((transporteur) =>
              selectedClientId &&
              (transporteur?.clients || []).some((client) => client.id === selectedClientId)
            ) ||
            selectedTruck?.transporteurs?.[0];
          if (matchingTransporteur?.id) {
            submitData.transporteurId = matchingTransporteur.id;
          }
        }
      }

      // loadDate: peseurs use tare instant; operators/managers use schedule
      if (isPeseur()) {
        const tIso = toIsoFromDateAndTime(formData.tareWeighDate, formData.tareWeighTime);
        if (tIso) {
          submitData.loadDate = tIso;
        } else if (formData.weighingPass === 'MARCHANDISE') {
          const lIso = toIsoFromDateAndTime(formData.loadedWeighDate, formData.loadedWeighTime);
          if (lIso) submitData.loadDate = lIso;
        }
      } else if ((isOperator() || isManagerOrAdmin()) && formData.scheduleDate) {
        const timeValue = formData.scheduleTime || '00:00';
        submitData.loadDate = `${formData.scheduleDate}T${timeValue}:00`;
      }

      const cleanNotes = formData.notes ? String(formData.notes).trim() : '';
      const rawTsHours = String(tsHours ?? '').trim();
      const parsedTsHours = rawTsHours === '' ? 0 : Number(rawTsHours.replace(',', '.'));
      const hasTsInput =
        Boolean(tsOptionCode) ||
        (rawTsHours !== '' && (!Number.isFinite(parsedTsHours) || parsedTsHours !== 0));

      if (hasTsInput) {
        if (!tsOptionCode) {
          setErrors((prev) => ({ ...prev, general: t('loads.ts.validation.optionRequired') }));
          toast.error(t('loads.ts.validation.optionRequired'));
          setLoading(false);
          return;
        }
        if (!Number.isFinite(parsedTsHours) || parsedTsHours < 0) {
          setErrors((prev) => ({ ...prev, general: t('loads.ts.validation.hoursInvalid') }));
          toast.error(t('loads.ts.validation.hoursInvalid'));
          setLoading(false);
          return;
        }

        const option = getSupplementaryTaxOption(tsOptionCode, resolvedTsOptions);
        submitData.notes = upsertTsMetaInNotes(cleanNotes, {
          optionCode: tsOptionCode,
          hours: parsedTsHours,
          totalAmount: getSupplementaryTaxTotal(tsOptionCode, parsedTsHours, resolvedTsOptions),
          description: option ? (option.description || t(option.labelKey)) : ''
        });
      } else {
        submitData.notes = cleanNotes || null;
      }

      delete submitData.scheduleDate;
      delete submitData.scheduleTime;
      delete submitData.weighingPass;
      delete submitData.continueLoadId;
      delete submitData.tareWeighDate;
      delete submitData.tareWeighTime;
      delete submitData.loadedWeighDate;
      delete submitData.loadedWeighTime;
      if (!(isPeseur() && formData.peseurPonctuelMode)) {
        delete submitData.truckPlateNumber;
      }

      if (
        isPeseur() &&
        !formData.peseurPonctuelMode &&
        String(submitData.ddpId || '').trim() &&
        !String(submitData.truckId || '').trim()
      ) {
        const hardError = t('weighing.validations.truckTransporteurRequired', {
          defaultValue: 'Truck is required'
        });
        setErrors((prev) => ({ ...prev, general: hardError }));
        toast.error(hardError);
        setLoading(false);
        return;
      }

      const peseurTwoStepDdpSubmit = isPeseur() && !formData.peseurPonctuelMode && formData.ddpId;
      const requestLoadIdForTare = String(selectedGateRequestedLoadId || '').trim();
      const peseurRequestedToTareSubmit =
        isPeseur() &&
        String(formData.continueLoadId || '').trim() === requestLoadIdForTare &&
        Boolean(requestLoadIdForTare);
      const peseurContinueAcceptedAwaitingTareSubmit =
        isPeseur() &&
        formData.weighingPass === 'TARE' &&
        Boolean(String(formData.continueLoadId || '').trim()) &&
        !peseurRequestedToTareSubmit &&
        !formData.peseurPonctuelMode;
      const peseurMarchandiseSecondPassSubmit =
        isPeseur() &&
        formData.weighingPass === 'MARCHANDISE' &&
        Boolean(String(formData.continueLoadId || '').trim());
      const peseurDeferredBindSubmit =
        peseurMarchandiseSecondPassSubmit &&
        formData.creationFlow === 'DEFERRED_TARE' &&
        secondPassIsDeferred;
      const peseurTareOnlySubmit =
        isPeseur() &&
        formData.weighingPass === 'TARE' &&
        (peseurTwoStepDdpSubmit || formData.peseurPonctuelMode);

      if (peseurMarchandiseSecondPassSubmit) {
        if (peseurDeferredBindSubmit) {
          const response = await runLoadMutationWithInsufficientPrepaidConfirm((allowBypass) =>
            loadsAPI.bindAndWeigh(formData.continueLoadId, {
              ddpId: selectedClientIsPonctuel ? undefined : String(formData.ddpId || '').trim(),
              clientId: String(formData.clientId || '').trim(),
              productId: String(formData.productId || '').trim(),
              loadedWeight: parseFloat(formData.loadedWeight) * 1000,
              loadedWeighedAt: toIsoFromDateAndTime(formData.loadedWeighDate, formData.loadedWeighTime),
              transporteurId: String(formData.transporteurId || '').trim() || undefined,
              allowInsufficientPrepaidBalance: allowBypass
            })
          );
          if (!response) return;
          const bw = response?.data?.billingWarnings;
          if (bw?.warnCreditOverLimit) {
            toast.warning(t('clients.billing.afterTicketCreditWarning'));
          }
          if (bw?.warnPayingInsufficientBalanceBypassed) {
            toast.warning(t('weighing.messages.prepaidBypassUsed'));
          }
          toast.success(t('weighing.messages.deferredBindComplete'));
          onSuccess?.();
          handleClose();
          return;
        }
        const response = await runLoadMutationWithInsufficientPrepaidConfirm((allowBypass) =>
          loadsAPI.update(formData.continueLoadId, {
            loadedWeight: parseFloat(formData.loadedWeight) * 1000,
            loadedWeighedAt: toIsoFromDateAndTime(formData.loadedWeighDate, formData.loadedWeighTime),
            notes: submitData.notes,
            allowInsufficientPrepaidBalance: allowBypass
          })
        );
        if (!response) return;
        const bw = response?.data?.billingWarnings;
        if (bw?.warnCreditOverLimit) {
          toast.warning(t('clients.billing.afterTicketCreditWarning'));
        }
        if (bw?.warnPayingInsufficientBalanceBypassed) {
          toast.warning(t('weighing.messages.prepaidBypassUsed'));
        }
        toast.success(t('weighing.messages.secondPassUpdated'));
        onSuccess?.();
        handleClose();
        return;
      }

      if (peseurContinueAcceptedAwaitingTareSubmit) {
        const continueId = String(formData.continueLoadId || '').trim();
        const response = await runLoadMutationWithInsufficientPrepaidConfirm((allowBypass) =>
          loadsAPI.update(continueId, {
            emptyWeight: parseFloat(formData.emptyWeight) * 1000,
            tareWeighedAt: toIsoFromDateAndTime(formData.tareWeighDate, formData.tareWeighTime),
            notes: submitData.notes,
            allowInsufficientPrepaidBalance: allowBypass
          })
        );
        if (!response) return;
        const bw = response?.data?.billingWarnings;
        if (bw?.warnCreditOverLimit) {
          toast.warning(t('clients.billing.afterTicketCreditWarning'));
        }
        if (bw?.warnPayingInsufficientBalanceBypassed) {
          toast.warning(t('weighing.messages.prepaidBypassUsed'));
        }
        if (isPeseur() && String(formData.weighbridgeId || '').trim()) {
          writeStoredPeseurWeighbridgeId(formData.weighbridgeId);
        }
        toast.success(t('loads.crud.loadUpdated'));
        onSuccess?.();
        handleClose();
        return;
      }

      if (peseurRequestedToTareSubmit) {
        if (requestLoadIdForTare) {
          const latestLoadRes = await loadsAPI.getById(requestLoadIdForTare);
          const latestStatus = String(latestLoadRes?.data?.load?.status || '').toUpperCase();
          if (latestStatus === 'REQUESTED') {
            await loadsAPI.updateStatus(requestLoadIdForTare, 'ACCEPTED');
          }
        }
        const response = await runLoadMutationWithInsufficientPrepaidConfirm((allowBypass) =>
          loadsAPI.update(requestLoadIdForTare, {
            emptyWeight: parseFloat(formData.emptyWeight) * 1000,
            tareWeighedAt: toIsoFromDateAndTime(formData.tareWeighDate, formData.tareWeighTime),
            notes: submitData.notes,
            allowInsufficientPrepaidBalance: allowBypass
          })
        );
        if (!response) return;
        const bw = response?.data?.billingWarnings;
        if (bw?.warnCreditOverLimit) {
          toast.warning(t('clients.billing.afterTicketCreditWarning'));
        }
        if (bw?.warnPayingInsufficientBalanceBypassed) {
          toast.warning(t('weighing.messages.prepaidBypassUsed'));
        }
        toast.success(t('weighing.messages.requestConvertedToTareSuccess'));
        onSuccess?.();
        handleClose();
        return;
      }

      if (isManagerOrAdmin() || isPeseur()) {
        if (isPeseur() && formData.peseurPonctuelMode) {
          submitData.emptyWeight = parseFloat(formData.emptyWeight);
          if (peseurTareOnlySubmit) {
            submitData.loadedWeight = null;
            submitData.tareWeighedAt = toIsoFromDateAndTime(formData.tareWeighDate, formData.tareWeighTime);
          } else {
            submitData.loadedWeight = parseFloat(formData.loadedWeight);
            const tIso = toIsoFromDateAndTime(formData.tareWeighDate, formData.tareWeighTime);
            const lIso = toIsoFromDateAndTime(formData.loadedWeighDate, formData.loadedWeighTime);
            if (tIso) submitData.tareWeighedAt = tIso;
            if (lIso) submitData.loadedWeighedAt = lIso;
          }
        } else if (peseurTareOnlySubmit) {
          submitData.emptyWeight = parseFloat(formData.emptyWeight) * 1000;
          submitData.loadedWeight = null;
          submitData.tareWeighedAt = toIsoFromDateAndTime(formData.tareWeighDate, formData.tareWeighTime);
        } else {
          submitData.emptyWeight = parseFloat(formData.emptyWeight) * 1000;
          submitData.loadedWeight = parseFloat(formData.loadedWeight) * 1000;
          const tIso = toIsoFromDateAndTime(formData.tareWeighDate, formData.tareWeighTime);
          const lIso = toIsoFromDateAndTime(formData.loadedWeighDate, formData.loadedWeighTime);
          if (tIso) submitData.tareWeighedAt = tIso;
          if (lIso) submitData.loadedWeighedAt = lIso;
        }
      } else {
        submitData.emptyWeight = null;
        submitData.loadedWeight = null;
      }

      const response = await runLoadMutationWithInsufficientPrepaidConfirm((allowBypass) =>
        loadsAPI.create({
          ...submitData,
          allowInsufficientPrepaidBalance: allowBypass
        })
      );
      if (!response) return;
      const bw = response?.data?.billingWarnings;
      if (bw?.warnCreditOverLimit) {
        toast.warning(t('clients.billing.afterTicketCreditWarning'));
      }
      if (bw?.warnPayingInsufficientBalanceBypassed) {
        toast.warning(t('weighing.messages.prepaidBypassUsed'));
      }
      if (isPeseur() && String(formData.weighbridgeId || '').trim()) {
        writeStoredPeseurWeighbridgeId(formData.weighbridgeId);
      }
      toast.success(t('weighing.messages.ticketCreatedSuccess'));
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Create ticket error:', error);
      const data = error.response?.data;
      let errorMessage = getApiErrorMessage(error, 'weighing.messages.failedToCreateTicket');
      const prepaidAmounts =
        (Number.isFinite(Number(data?.amountTtc)) && Number.isFinite(Number(data?.availableBalance)))
          ? { estimated: Number(data.amountTtc), available: Number(data.availableBalance) }
          : (() => {
            const match = String(errorMessage).match(
              /Insufficient prepaid balance for this weighing\.\s*Estimated total:\s*(\d+)\s*(?:;|\.)\s*available:\s*(\d+)\.?/i
            );
            if (!match) return null;
            return { estimated: Number(match[1]), available: Number(match[2]) };
          })();
      if (
        prepaidAmounts &&
        (data?.code === 'PAYING_INSUFFICIENT_BALANCE' ||
          data?.code === 'PAYING_INSUFFICIENT_BALANCE_CONFIRM_REQUIRED')
      ) {
        errorMessage = t('weighing.messages.insufficientPrepaidBalance', {
          estimated: formatCurrency(prepaidAmounts.estimated),
          available: formatCurrency(prepaidAmounts.available)
        });
      }
      setErrors({
        general: errorMessage,
        billingErrorCode: typeof data?.code === 'string' ? data.code : ''
      });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      const td = getTodayDateInputValue();
      const tt = getCurrentTimeInputValue();
      const loadedFromTare = addHoursToDateTimeParts(td, tt, 2);
      const baseForm = {
        ddpId: '',
        transporteurId: '',
        clientId: '',
        truckId: '',
        driverId: '',
        productId: '',
        portId: '',
        weighbridgeId: '',
        moleId: '',
        emptyWeight: '',
        loadedWeight: '',
        transactionType: 'EXPORT',
        notes: '',
        scheduleDate: isOperator() ? '' : td,
        scheduleTime: isOperator() ? '' : tt,
        weighingPass: isPeseur() ? 'TARE' : 'FULL',
        continueLoadId: '',
        tareWeighDate: td,
        tareWeighTime: tt,
        loadedWeighDate: loadedFromTare.dateStr,
        loadedWeighTime: loadedFromTare.timeStr,
        peseurPonctuelMode: false,
        truckPlateNumber: '',
        creationFlow: 'STANDARD',
        driverFullName: '',
        driverPhone: ''
      };
      setFormData(() => {
        if (isPeseur()) {
          return { ...baseForm, ...resolvePeseurWeighbridgeFields(getPeseurWeighbridges()) };
        }
        return baseForm;
      });
      setSecondPassIsDeferred(false);
      setDeferredClients([]);
      setDeferredPlateLookup('');
      setDeferredLookupLoading(false);
      setErrors({});
      setInProgressLoads([]);
      setContinueLoadSnapshot(null);
      setTrucks([]);
      setDrivers([]);
      setDdpDetailsExpanded(false);
      setTsOptionCode('');
      setTsHours('0');
      setShowPonctuelCreateModal(false);
      setShowQuickProductModal(false);
      setSecondPassNudgeDismissedKey(null);
      setPeseurGateNoMatch(false);
      setPeseurGateBillingFilter(null);
      setDeferredTareMatches([]);
      setPonctuelModeFromPeseurGate(false);
      setPeseurEntryGatePassed(!isPeseur() || Boolean(continueLoadIdProp));
      onClose();
    }
  };

  const renderOpenClientDetailsLink = () => {
    const cid = String(formData.clientId || '').trim();
    if (!cid) return null;
    return (
      <button
        type="button"
        disabled={loading}
        className="mt-2 block text-sm font-semibold underline decoration-2 underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => {
          navigate('/clients', { state: { openClientId: cid } });
          handleClose();
        }}
      >
        {t('weighing.openClientDetailsLink')}
      </button>
    );
  };

  const calculateNetWeight = () => {
    if (Number.isFinite(effectiveEmptyWeightTon) && formData.loadedWeight) {
      const emptyWeight = effectiveEmptyWeightTon;
      const loadedWeight = parseFloat(formData.loadedWeight);
      if (!isNaN(emptyWeight) && !isNaN(loadedWeight)) {
        return (loadedWeight - emptyWeight).toFixed(2);
      }
    }
    return '0.00';
  };

  const selectedClientRecord = useMemo(() => {
    const cid = String(formData.clientId || '').trim();
    if (!cid) return null;
    return (
      deferredClients.find((c) => c?.id === cid) ||
      ponctuelClients.find((c) => c?.id === cid) ||
      null
    );
  }, [formData.clientId, deferredClients, ponctuelClients]);
  const selectedClientBillingType = String(
    clientBillingInfo?.billingType || selectedClientRecord?.billingType || ''
  ).toUpperCase();
  const selectedClientIsPonctuel = selectedClientBillingType === 'PONCTUEL';
  const isDdpSelected = Boolean(formData.ddpId) || selectedClientIsPonctuel;
  const formUnlockedForPeseurPonctuel = isPeseur() && formData.peseurPonctuelMode;
  /** Ponctuel tare-only: no client/product in UI; tare is stored like deferred tare until 2e pesée. */
  const hidePonctuelClientAndProductForTare =
    isPeseur() && formData.peseurPonctuelMode && formData.weighingPass === 'TARE';
  const formUnlockedForContinueSecondPass =
    isPeseur() &&
    formData.weighingPass === 'MARCHANDISE' &&
    Boolean(String(formData.continueLoadId || '').trim());
  const formUnlockedForDeferredTareStep1 =
    isPeseur() &&
    formData.creationFlow === 'DEFERRED_TARE' &&
    formData.weighingPass === 'TARE' &&
    !String(formData.continueLoadId || '').trim();
  const formUnlockedForDeferredSecondPassBind =
    isPeseur() &&
    formData.creationFlow === 'DEFERRED_TARE' &&
    formData.weighingPass === 'MARCHANDISE' &&
    Boolean(String(formData.continueLoadId || '').trim()) &&
    secondPassIsDeferred;
  const deferredSecondPassContextReady =
    !formUnlockedForDeferredSecondPassBind ||
    (Boolean(String(formData.clientId || '').trim()) &&
      (selectedClientIsPonctuel || Boolean(String(formData.ddpId || '').trim())) &&
      Boolean(String(formData.productId || '').trim()));
  const continueSecondPassMainUnlocked =
    formUnlockedForContinueSecondPass &&
    (!formUnlockedForDeferredSecondPassBind || deferredSecondPassContextReady);
  const formSectionUnlocked =
    isDdpSelected ||
    formUnlockedForPeseurPonctuel ||
    continueSecondPassMainUnlocked ||
    formUnlockedForDeferredTareStep1 ||
    (formUnlockedForDeferredSecondPassBind && deferredSecondPassContextReady);

  const focusLoadedWeightField = useCallback(() => {
    const input = loadedWeightInputRef.current;
    if (!input || input.disabled) return false;
    input.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    input.focus({ preventScroll: true });
    if (typeof input.select === 'function') input.select();
    return true;
  }, []);

  const focusProductSelectField = useCallback(() => {
    productSelectRef.current?.focus?.({ open: true });
  }, []);

  const isProductSelectorVisible = useCallback(() => {
    if (hidePonctuelClientAndProductForTare) return false;
    if (!shouldShowProductSelector) return false;
    if (formUnlockedForDeferredSecondPassBind) {
      const cid = String(formData.clientId || '').trim();
      if (!cid) return false;
      return selectedClientIsPonctuel || Boolean(String(formData.ddpId || '').trim());
    }
    return true;
  }, [
    hidePonctuelClientAndProductForTare,
    shouldShowProductSelector,
    formUnlockedForDeferredSecondPassBind,
    formData.clientId,
    formData.ddpId,
    selectedClientIsPonctuel
  ]);

  const routeFocusAfterClientSelection = useCallback(() => {
    const productId = String(formData.productId || '').trim();
    const isMarchandise = formData.weighingPass === 'MARCHANDISE';
    if (productId && isMarchandise && formSectionUnlocked) {
      if (focusLoadedWeightField()) return;
    }
    if (!productId && isProductSelectorVisible()) {
      focusProductSelectField();
    }
  }, [
    formData.productId,
    formData.weighingPass,
    formSectionUnlocked,
    focusLoadedWeightField,
    focusProductSelectField,
    isProductSelectorVisible
  ]);

  useEffect(() => {
    if (!isOpen) {
      prevClientIdForFocusRef.current = '';
      prevProductIdForFocusRef.current = '';
      return;
    }
    const cid = String(formData.clientId || '').trim();
    const prevCid = prevClientIdForFocusRef.current;
    if (!cid) {
      prevClientIdForFocusRef.current = '';
      return;
    }
    const clientChanged = prevCid !== cid;
    prevClientIdForFocusRef.current = cid;
    if (!clientChanged) return;
    if (hidePonctuelClientAndProductForTare) return;
    if (formUnlockedForContinueSecondPass) return;

    const timer = window.setTimeout(routeFocusAfterClientSelection, 100);
    return () => window.clearTimeout(timer);
  }, [
    isOpen,
    formData.clientId,
    hidePonctuelClientAndProductForTare,
    formUnlockedForContinueSecondPass,
    routeFocusAfterClientSelection
  ]);

  useEffect(() => {
    if (!isOpen) {
      prevProductIdForFocusRef.current = '';
      return;
    }
    const pid = String(formData.productId || '').trim();
    const cid = String(formData.clientId || '').trim();
    if (!pid) {
      prevProductIdForFocusRef.current = '';
      return;
    }
    const prevPid = prevProductIdForFocusRef.current;
    const productChanged = prevPid !== pid;
    prevProductIdForFocusRef.current = pid;
    if (!productChanged || !cid) return;
    if (formData.weighingPass !== 'MARCHANDISE' || !formSectionUnlocked) return;

    const timer = window.setTimeout(() => {
      focusLoadedWeightField();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [
    isOpen,
    formData.clientId,
    formData.productId,
    formData.weighingPass,
    formSectionUnlocked,
    focusLoadedWeightField
  ]);

  useEffect(() => {
    if (!isOpen || !isPeseur()) {
      tareWeightFocusKeyRef.current = '';
      secondPassLoadedWeightFocusKeyRef.current = '';
      return;
    }
    const continueId = String(formData.continueLoadId || continueLoadIdProp || '').trim();
    const shouldFocusTareWeight =
      formSectionUnlocked &&
      formData.weighingPass === 'TARE';
    if (!shouldFocusTareWeight) {
      tareWeightFocusKeyRef.current = '';
    } else {
      const tareFocusKey = `${continueId || 'NEW'}:TARE`;
      if (tareWeightFocusKeyRef.current !== tareFocusKey) {
        tareWeightFocusKeyRef.current = tareFocusKey;
        const tareTimer = window.setTimeout(() => {
          const tareInput = emptyWeightInputRef.current;
          if (!tareInput || tareInput.disabled) return;
          tareInput.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          tareInput.focus({ preventScroll: true });
          if (typeof tareInput.select === 'function') tareInput.select();
        }, 80);
        return () => window.clearTimeout(tareTimer);
      }
    }

    const continueIdForLoaded = String(formData.continueLoadId || continueLoadIdProp || '').trim();
    const shouldFocusLoadedWeight =
      formSectionUnlocked &&
      formData.weighingPass === 'MARCHANDISE' &&
      Boolean(continueIdForLoaded);
    if (!shouldFocusLoadedWeight) {
      secondPassLoadedWeightFocusKeyRef.current = '';
      return;
    }
    const focusKey = `${continueIdForLoaded}:MARCHANDISE`;
    if (secondPassLoadedWeightFocusKeyRef.current === focusKey) return;
    secondPassLoadedWeightFocusKeyRef.current = focusKey;

    const applyFocus = () => {
      const input = loadedWeightInputRef.current;
      if (!input) return;
      input.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      input.focus({ preventScroll: true });
      if (typeof input.select === 'function') input.select();
    };

    const timer = window.setTimeout(applyFocus, 80);
    return () => window.clearTimeout(timer);
  }, [
    isOpen,
    isPeseur,
    formSectionUnlocked,
    formData.weighingPass,
    formData.continueLoadId,
    continueLoadIdProp,
  ]);
  const tsParsedForPreview = Number(String(tsHours ?? '').replace(',', '.'));
  const hasInlineTsPreview =
    Boolean(tsOptionCode) && Number.isFinite(tsParsedForPreview) && tsParsedForPreview >= 0;
  const tsInlineTotal = hasInlineTsPreview
    ? getSupplementaryTaxTotal(tsOptionCode, tsParsedForPreview, resolvedTsOptions)
    : 0;
  const resolvedTarifPoids = useMemo(() => (
    tarifConfig?.tarifPoids || {
      weightCeilingTon: 20,
      ouvrable: { minimumHt: 4499, rateHtPerTon: 153 },
      special: { minimumHt: 9000, rateHtPerTon: 302 }
    }
  ), [tarifConfig?.tarifPoids]);
  const taxesAuPoidsPreview = useMemo(() => {
    const loadedTon = parseFloat(formData.loadedWeight);
    const emptyTon = effectiveEmptyWeightTon;

    let netTon = 0;
    if (Number.isFinite(loadedTon) && Number.isFinite(emptyTon) && loadedTon > emptyTon) {
      netTon = loadedTon - emptyTon;
    }

    const safeNetTon = Number.isFinite(netTon) && netTon > 0 ? netTon : 0;
    const dateRef =
      isPeseur() && formData.tareWeighDate
        ? new Date(`${formData.tareWeighDate}T${formData.tareWeighTime || '00:00'}:00`)
        : formData.scheduleDate
          ? new Date(`${formData.scheduleDate}T${formData.scheduleTime || '00:00'}:00`)
          : new Date();
    const day = dateRef.getDay();
    const hour = dateRef.getHours();
    const isWeekday = day >= 1 && day <= 5;
    const isWorkingHour = hour >= 8 && hour < 16;
    const isOuvrable = isWeekday && isWorkingHour;

    const tarif = isOuvrable ? resolvedTarifPoids.ouvrable : resolvedTarifPoids.special;
    const ceilingTon = Number.isFinite(Number(resolvedTarifPoids.weightCeilingTon))
      ? Number(resolvedTarifPoids.weightCeilingTon)
      : 20;

    const amountHt = safeNetTon <= 0
      ? 0
      : (safeNetTon <= ceilingTon
        ? Math.round(Number(tarif.minimumHt || 0))
        : Math.round(safeNetTon * Number(tarif.rateHtPerTon || 0)));

    const tvaRateNum = Number.isFinite(Number(tarifConfig?.tvaRate)) && Number(tarifConfig.tvaRate) >= 0
      ? Number(tarifConfig.tvaRate)
      : 0.18;
    const amountTva = Math.round(amountHt * (tvaRateNum / (1 + tvaRateNum)));
    const amountHtExclTva = Math.round(amountHt - amountTva);
    const amountTs = hasInlineTsPreview ? tsInlineTotal : 0;
    const amountTtc = amountHt + amountTs;

    return {
      netTon: safeNetTon,
      amountHt: amountHtExclTva,
      amountTva,
      amountTtc,
      amountTs,
      isOuvrable,
      ceilingTon,
      minHt: Number(tarif.minimumHt || 0),
      rateHtPerTon: Number(tarif.rateHtPerTon || 0)
    };
  }, [
    formData.loadedWeight,
    effectiveEmptyWeightTon,
    formData.scheduleDate,
    formData.scheduleTime,
    formData.tareWeighDate,
    formData.tareWeighTime,
    tsInlineTotal,
    hasInlineTsPreview,
    resolvedTarifPoids,
    tarifConfig?.tvaRate,
    isPeseur
  ]);
  const modalTitle = isOperator()
    ? t('weighing.addTicketModalClient')
    : t('weighing.addTicketModal');
  const peseurWeighbridgeList = isPeseur() ? getPeseurWeighbridges() : [];
  const secondPassRoLabel = 'block text-sm font-medium text-gray-600 mb-1';
  const secondPassRoBox =
    'rounded-md border border-gray-200 bg-gray-100 px-3 py-2.5 text-sm text-gray-900';

  const continueSecondPassReadOnly = useMemo(() => {
    if (!formUnlockedForContinueSecondPass) return null;
    const truck = trucks.find((x) => x.id === formData.truckId);
    const product = products.find((x) => x.id === formData.productId);
    const driver = drivers.find((x) => x.id === formData.driverId);
    const ddp = selectedDdp || ddps.find((x) => x.id === formData.ddpId);
    const loadRow =
      continueLoadSnapshot?.id === formData.continueLoadId
        ? continueLoadSnapshot
        : inProgressLoads.find((l) => l.id === formData.continueLoadId);
    const truckDisplay = truck?.plateNumber || String(formData.truckPlateNumber || '').trim() || '—';
    const driverFromTruck =
      truck?.assignedDrivers?.length === 1 && truck.assignedDrivers[0]?.driver
        ? `${truck.assignedDrivers[0].driver.firstName || ''} ${truck.assignedDrivers[0].driver.lastName || ''}`.trim()
        : '';
    const loadDriverDisplay = (() => {
      const dEntity = loadRow?.driverEntity;
      if (dEntity) {
        const full = `${dEntity.firstName || ''} ${dEntity.lastName || ''}`.trim();
        if (full) return full;
      }
      const d = loadRow?.driver;
      if (d) {
        const full = `${d.firstName || ''} ${d.lastName || ''}`.trim();
        if (full) return full;
      }
      const truckDriverName = String(loadRow?.truck?.driverName || '').trim();
      return truckDriverName || '';
    })();
    const driverDisplay =
      (driver && `${driver.firstName || ''} ${driver.lastName || ''}`.trim()) ||
      loadDriverDisplay ||
      driverFromTruck ||
      '—';
    const continueSummary = loadRow
      ? `${loadRow.truck?.plateNumber || '—'}${loadRow.client?.companyName ? ` — ${loadRow.client.companyName}` : ''}`
      : `${truckDisplay}${product?.name ? ` — ${product.name}` : ''}`;
    const ponctuelClient = ponctuelClients.find((c) => c.id === formData.clientId);
    const ponctuelClientName =
      ponctuelClient?.companyName?.trim() ||
      ponctuelClient?.contactName?.trim() ||
      '—';
    const plateDisplay = String(formData.truckPlateNumber || '').trim() || truck?.plateNumber || '—';
    const wbs = isPeseur() ? getPeseurWeighbridges() : [];
    const wb = wbs.find((w) => w.id === formData.weighbridgeId);
    const weighbridgeDisplay = wb
      ? [wb.name, wb.location].filter(Boolean).join(' — ') || wb.id
      : '—';
    const emptyWeightDisplay = formatContinuePassTareDisplay(
      loadRow,
      formData.emptyWeight,
      formatWeight
    );
    return {
      ddpLabel: ddp ? formatDdpReadOnlyLabel(ddp, t) : '',
      productName: product?.name || '—',
      truckDisplay,
      driverDisplay,
      continueSummary,
      ponctuelClientName,
      plateDisplay,
      weighbridgeDisplay,
      emptyWeightDisplay,
    };
  }, [
    formUnlockedForContinueSecondPass,
    trucks,
    products,
    drivers,
    selectedDdp,
    ddps,
    formData.truckId,
    formData.productId,
    formData.driverId,
    formData.ddpId,
    formData.continueLoadId,
    formData.clientId,
    formData.truckPlateNumber,
    formData.weighbridgeId,
    formData.emptyWeight,
    continueLoadSnapshot,
    inProgressLoads,
    ponctuelClients,
    t,
    isPeseur,
    getPeseurWeighbridges,
  ]);

  /** Peseur: show tare weight + tare date/time on one row (weight left). */
  const emptyWeightInPeseurPairRow =
    isPeseur() && formSectionUnlocked && formData.weighingPass === 'TARE';
  /** Peseur: show loaded weight + loaded date/time on one row (weight left). */
  const loadedWeightInPeseurPairRow =
    isPeseur() && formSectionUnlocked && formData.weighingPass === 'MARCHANDISE';
  const showEmptyWeightInLowerGrid = !emptyWeightInPeseurPairRow;
  const showLoadedWeightInLowerGrid = formData.weighingPass !== 'TARE' && !loadedWeightInPeseurPairRow;
  const showNetWeightInLowerGrid = formData.weighingPass !== 'TARE';
  /** Walk-in (ponctuel): saving will create a WEIGHING_ONLY bill once marchandise is recorded (not on tare-only). */
  const showPonctuelBillOnSaveHint =
    isPeseur() &&
    formData.peseurPonctuelMode &&
    formSectionUnlocked &&
    formData.weighingPass === 'MARCHANDISE' &&
    Boolean(formData.continueLoadId);
  const useStackedLowerWeightsForSecondPass =
    formUnlockedForContinueSecondPass &&
    Boolean(continueSecondPassReadOnly) &&
    showEmptyWeightInLowerGrid &&
    showNetWeightInLowerGrid &&
    !showLoadedWeightInLowerGrid;
  const lowerWeightsColumnCount = [showEmptyWeightInLowerGrid, showLoadedWeightInLowerGrid, showNetWeightInLowerGrid].filter(Boolean).length;
  const lowerWeightsGridClass =
    useStackedLowerWeightsForSecondPass
      ? 'grid grid-cols-1 gap-5'
      : lowerWeightsColumnCount <= 1
      ? 'grid grid-cols-1 gap-4'
      : lowerWeightsColumnCount === 2
        ? 'grid grid-cols-1 gap-4 md:grid-cols-2'
        : 'grid grid-cols-1 gap-4 md:grid-cols-3';
  const selectedDdpClientName = selectedDdp ? getClientDisplayNameForDdp(selectedDdp) : '';
  const selectedDdpProductsSummary = Array.isArray(selectedDdp?.lineItems) && selectedDdp.lineItems.length > 0
    ? selectedDdp.lineItems
      .map((line) => `${line.product?.name || line.productName || '-'} / ${line.totalWeight || 0} t`)
      .join(', ')
    : (selectedDdp?.productsSummary || selectedDdp?.productName || '—');

  const pendingSecondPassLoad = useMemo(() => {
    if (!isPeseur() || !Array.isArray(inProgressLoads) || !inProgressLoads.length) {
      return null;
    }
    if (formData.peseurPonctuelMode) {
      const cid = String(formData.clientId || '').trim();
      const plate = normalizePlateForMatch(formData.truckPlateNumber);
      if (!plate) return null;
      const matches = inProgressLoads.filter((l) => {
        const loadPlate = normalizePlateForMatch(l.truck?.plateNumber);
        if (!loadPlate || loadPlate !== plate) return false;
        if (cid) return String(l.clientId || '') === cid;
        return isDeferredOpenLoadRow(l);
      });
      return matches.length ? matches[0] : null;
    }
    if (!formData.truckId) return null;
    const tid = String(formData.truckId);
    const sameTruck = inProgressLoads.filter((l) => l && String(l.truckId) === tid);
    if (!sameTruck.length) return null;
    const cid = String(formData.clientId || '').trim();
    if (cid) {
      const byClient = sameTruck.find((l) => String(l.clientId || '') === cid);
      if (byClient) return byClient;
    }
    return sameTruck[0];
  }, [
    isPeseur,
    formData.truckId,
    formData.clientId,
    formData.peseurPonctuelMode,
    formData.truckPlateNumber,
    inProgressLoads,
  ]);

  const secondPassPendingTareSummary = useMemo(() => {
    const load = pendingSecondPassLoad;
    if (!load) return { weight: '', dateTime: '' };
    let weight = '';
    if (load.emptyWeight != null && load.emptyWeight !== '') {
      weight = formatWeight(Number(load.emptyWeight), 'ton');
    }
    const raw = load.tareWeighedAt != null ? load.tareWeighedAt : load.loadDate;
    let dateTime = '';
    if (raw != null && raw !== '') {
      dateTime = formatDateTime(typeof raw === 'string' ? raw : raw);
    }
    return { weight, dateTime };
  }, [pendingSecondPassLoad]);

  const selectedTruckPlateForNudge = useMemo(() => {
    if (formData.peseurPonctuelMode) {
      return String(formData.truckPlateNumber || '').trim().toUpperCase() || '—';
    }
    const tr = trucks.find((x) => x.id === formData.truckId);
    return String(tr?.plateNumber || '').trim() || '—';
  }, [trucks, formData.truckId, formData.peseurPonctuelMode, formData.truckPlateNumber]);

  const secondPassTruckNudgeKey = (() => {
    if (!pendingSecondPassLoad?.id) return '';
    if (formData.peseurPonctuelMode) {
      const cid = String(formData.clientId || '').trim();
      return `ponctuel:${cid || 'open'}:${normalizePlateForMatch(formData.truckPlateNumber)}:${pendingSecondPassLoad.id}`;
    }
    if (!formData.truckId) return '';
    return `${formData.truckId}:${pendingSecondPassLoad.id}`;
  })();

  const showSecondPassTruckNudge =
    isPeseur() &&
    formSectionUnlocked &&
    formData.weighingPass === 'TARE' &&
    !continueLoadIdProp &&
    !String(formData.continueLoadId || '').trim() &&
    Boolean(pendingSecondPassLoad) &&
    Boolean(secondPassTruckNudgeKey) &&
    secondPassNudgeDismissedKey !== secondPassTruckNudgeKey;

  const handleSwitchToSecondPassForTruck = () => {
    const load = pendingSecondPassLoad;
    if (!load?.id) return;
    const resolvedDdpId = load.ddpId || load.ddp?.id || '';
    const base =
      load.tareWeighedAt != null
        ? new Date(load.tareWeighedAt)
        : load.loadDate != null
          ? new Date(load.loadDate)
          : null;
    const tParts = base && !Number.isNaN(base.getTime()) ? dateToInputParts(base) : null;
    const tDate = tParts?.dateStr ?? getTodayDateInputValue();
    const tTime = tParts?.timeStr ?? getCurrentTimeInputValue();
    const loadedParts = addHoursToDateTimeParts(tDate, tTime, 2);
    setFormData((prev) => ({
      ...prev,
      weighingPass: 'MARCHANDISE',
      continueLoadId: load.id,
      creationFlow: isDeferredOpenLoadRow(load) ? 'DEFERRED_TARE' : 'STANDARD',
      ddpId: resolvedDdpId,
      peseurPonctuelMode: !resolvedDdpId,
      truckPlateNumber: resolvedDdpId
        ? ''
        : String(load.truck?.plateNumber || prev.truckPlateNumber || '').trim().toUpperCase(),
      truckId: load.truckId || '',
      transporteurId: load.transporteurId || '',
      clientId: load.clientId || '',
      productId: load.productId || '',
      driverId: load.driverId || load.driverIdDriver || load.driverEntity?.id || load.driver?.id || prev.driverId || '',
      portId: load.portId || '',
      weighbridgeId: load.weighbridgeId || '',
      moleId: load.moleId || '',
      emptyWeight:
        load.emptyWeight != null ? String(Number(load.emptyWeight) / 1000) : prev.emptyWeight,
      loadedWeight: '',
      transactionType: load.transactionType || prev.transactionType,
      scheduleDate: tDate,
      scheduleTime: tTime,
      tareWeighDate: tDate,
      tareWeighTime: tTime,
      loadedWeighDate: loadedParts.dateStr,
      loadedWeighTime: loadedParts.timeStr,
    }));
  };

  const handleDismissSecondPassTruckNudge = () => {
    if (secondPassTruckNudgeKey) setSecondPassNudgeDismissedKey(secondPassTruckNudgeKey);
  };

  /** Infer pass type from selected truck/plate: pending tare => Marchandise, otherwise Tare. */
  useEffect(() => {
    if (!isOpen || !isPeseur() || continueLoadIdProp) return;
    const requestToTareSelected =
      String(selectedGateRequestedLoadId || '').trim() !== '' &&
      String(formData.continueLoadId || '').trim() === String(selectedGateRequestedLoadId || '').trim();
    if (requestToTareSelected) return;
    const inferred = pendingSecondPassLoad;
    const currentContinueId = String(formData.continueLoadId || '').trim();

    if (inferred?.id) {
      const inferredId = String(inferred.id);
      if (formData.weighingPass !== 'MARCHANDISE' || currentContinueId !== inferredId) {
        const resolvedDdpId = inferred.ddpId || inferred.ddp?.id || '';
        const base =
          inferred.tareWeighedAt != null
            ? new Date(inferred.tareWeighedAt)
            : inferred.loadDate != null
              ? new Date(inferred.loadDate)
              : null;
        const tParts = base && !Number.isNaN(base.getTime()) ? dateToInputParts(base) : null;
        const tDate = tParts?.dateStr ?? getTodayDateInputValue();
        const tTime = tParts?.timeStr ?? getCurrentTimeInputValue();
        const loadedParts = addHoursToDateTimeParts(tDate, tTime, 2);
        setFormData((prev) => ({
          ...prev,
          weighingPass: 'MARCHANDISE',
          continueLoadId: inferred.id,
          creationFlow: isDeferredOpenLoadRow(inferred) ? 'DEFERRED_TARE' : 'STANDARD',
          ddpId: resolvedDdpId,
          peseurPonctuelMode: !resolvedDdpId,
          truckPlateNumber: resolvedDdpId
            ? ''
            : String(inferred.truck?.plateNumber || prev.truckPlateNumber || '').trim().toUpperCase(),
          truckId: inferred.truckId || '',
          transporteurId: inferred.transporteurId || '',
          clientId: inferred.clientId || '',
          productId: inferred.productId || '',
          driverId: inferred.driverId || inferred.driverIdDriver || inferred.driverEntity?.id || inferred.driver?.id || prev.driverId || '',
          portId: inferred.portId || '',
          weighbridgeId: inferred.weighbridgeId || '',
          moleId: inferred.moleId || '',
          emptyWeight:
            inferred.emptyWeight != null ? String(Number(inferred.emptyWeight) / 1000) : prev.emptyWeight,
          loadedWeight: '',
          transactionType: inferred.transactionType || prev.transactionType,
          scheduleDate: tDate,
          scheduleTime: tTime,
          tareWeighDate: tDate,
          tareWeighTime: tTime,
          loadedWeighDate: loadedParts.dateStr,
          loadedWeighTime: loadedParts.timeStr
        }));
      }
      return;
    }

    if (formData.weighingPass === 'MARCHANDISE' && !currentContinueId) {
      setFormData((prev) => ({ ...prev, weighingPass: 'TARE', continueLoadId: '' }));
    }
  }, [
    isOpen,
    isPeseur,
    continueLoadIdProp,
    pendingSecondPassLoad,
    formData.weighingPass,
    formData.continueLoadId,
    selectedGateRequestedLoadId
  ]);

  const showPeseurEntryGate = isPeseur() && !continueLoadIdProp && !peseurEntryGatePassed;
  const requestToTareContext =
    isPeseur() &&
    ((Boolean(String(selectedGateRequestedLoadId || '').trim()) &&
      String(formData.continueLoadId || '').trim() === String(selectedGateRequestedLoadId || '').trim()) ||
      (Boolean(continueFromLoadTareFirst) &&
        formData.weighingPass === 'TARE' &&
        Boolean(String(formData.continueLoadId || '').trim())));
  const lockTruckSelectionToGateChoice =
    isPeseur() &&
    (Boolean(peseurGateBillingFilter) || requestToTareContext) &&
    !formUnlockedForContinueSecondPass &&
    !formUnlockedForDeferredSecondPassBind &&
    !formData.peseurPonctuelMode &&
    Boolean(String(formData.truckId || '').trim());

  const deferredPlateTypeaheadMinOk =
    String(deferredPlateLookup || '').trim().length >= DEFERRED_GATE_PLATE_MIN_CHARS;
  const generalErrorText = String(errors.general || '').trim();
  const prepaidBillingErrorCodes = new Set([
    'PAYING_INSUFFICIENT_BALANCE',
    'PAYING_INSUFFICIENT_BALANCE_CONFIRM_REQUIRED',
    'PAYING_ZERO_BALANCE',
    'PAYING_ZERO_BALANCE_CONFIRM_REQUIRED'
  ]);
  const isPrepaidBalanceError =
    prepaidBillingErrorCodes.has(String(errors.billingErrorCode || '')) ||
    generalErrorText.toLowerCase().includes('insufficient prepaid balance') ||
    generalErrorText.toLowerCase().includes('solde prépayé insuffisant');

  const knownGateSelectedTruck = useMemo(() => (
    knownGateTruckMatches.find((truck) => truck.id === knownGateSelectedTruckId) || null
  ), [knownGateTruckMatches, knownGateSelectedTruckId]);

  const knownGateDriverOptions = useMemo(() => {
    const truck = knownGateSelectedTruck;
    if (!truck) return [];
    const byId = new Map();
    (truck.drivers || []).forEach((d) => {
      if (d?.id && !byId.has(d.id)) byId.set(d.id, d);
    });
    (truck.assignedDrivers || []).forEach((row) => {
      const d = row?.driver;
      if (d?.id && !byId.has(d.id)) byId.set(d.id, d);
    });
    return Array.from(byId.values());
  }, [knownGateSelectedTruck]);

  const knownGateSelectedDriver = useMemo(() => (
    knownGateDriverOptions.find((d) => d.id === knownGateSelectedDriverId) || null
  ), [knownGateDriverOptions, knownGateSelectedDriverId]);

  const applyKnownGateTruckPrefill = (baseData = {}, mode = 'DDP') => {
    const selectedTruckId = String(
      knownGateSelectedTruckIdRef.current || knownGateSelectedTruckId || ''
    ).trim();
    const selectedDriverId = String(
      knownGateSelectedDriverIdRef.current || knownGateSelectedDriverId || ''
    ).trim();
    const selectedTruckResolved =
      knownGateTruckMatches.find((truck) => String(truck?.id || '').trim() === selectedTruckId) ||
      knownGateSelectedTruck;
    if (!selectedTruckResolved) return baseData;
    const next = {
      ...baseData,
      truckId: mode === 'DDP' ? selectedTruckResolved.id : (baseData.truckId || ''),
      transporteurId: mode === 'DDP'
        ? (selectedTruckResolved.transporteurs?.[0]?.id || baseData.transporteurId || '')
        : (baseData.transporteurId || ''),
      truckPlateNumber: mode === 'PONCTUEL'
        ? String(selectedTruckResolved.plateNumber || baseData.truckPlateNumber || '').trim()
        : (baseData.truckPlateNumber || '')
    };

    if (selectedDriverId && selectedDriverId !== '__NEW__') {
      if (mode === 'DDP') {
        next.driverId = selectedDriverId;
      } else if (mode === 'PONCTUEL') {
        const d = knownGateSelectedDriver;
        const dName = [d?.firstName, d?.lastName].filter(Boolean).join(' ').trim();
        next.driverFullName = dName || baseData.driverFullName || '';
        next.driverPhone = String(d?.phone || baseData.driverPhone || '').trim();
      }
    } else if (mode === 'DDP') {
      // Driver changed: keep truck, let user assign/create a different driver in the next step.
      next.driverId = '';
    }

    return next;
  };

  const openKnownGateDriverCreateModal = () => {
    if (!knownGateSelectedTruck) return;
    setKnownGateDriverError('');
    setKnownGateDriverForm({ firstName: '', lastName: '', phone: '' });
    setKnownGateDriverIdentityMatch(null);
    setKnownGateDriverIdentityMatchLoading(false);
    setShowKnownGateDriverModal(true);
  };

  const handleCreateAndAssignKnownGateDriver = async () => {
    if (!knownGateSelectedTruck) return;
    const firstName = String(knownGateDriverForm.firstName || '').trim();
    const lastName = String(knownGateDriverForm.lastName || '').trim();
    const phone = String(knownGateDriverForm.phone || '').trim();
    if (!firstName || !lastName) {
      setKnownGateDriverError(
        t('weighing.deferred.driverNameRequired', {
          defaultValue: 'Le prénom et le nom du chauffeur sont requis.'
        })
      );
      return;
    }

    setKnownGateDriverSaving(true);
    setKnownGateDriverError('');
    try {
      const created = await driversAPI.create({
        firstName,
        lastName,
        phone: phone || undefined,
        status: 'active'
      });
      const createdDriver = created?.data?.driver;
      const reusedExisting = created?.data?.reusedExisting === true;
      if (!createdDriver?.id) {
        throw new Error('Driver creation failed');
      }

      // `driverIds` on truck update expects Driver-entity IDs only (not legacy User IDs).
      const existingDriverEntityIds = (knownGateSelectedTruck.drivers || [])
        .map((d) => d?.id)
        .filter(Boolean);
      const mergedDriverIds = Array.from(new Set([...existingDriverEntityIds, createdDriver.id]));

      await trucksAPI.update(knownGateSelectedTruck.id, {
        driverIds: mergedDriverIds
      });

      setKnownGateTruckMatches((prev) =>
        prev.map((truck) => {
          if (truck.id !== knownGateSelectedTruck.id) return truck;
          const nextDriver = {
            id: createdDriver.id,
            firstName: createdDriver.firstName || firstName,
            lastName: createdDriver.lastName || lastName,
            phone: createdDriver.phone || phone || ''
          };
          const others = (truck.drivers || []).filter((d) => d?.id && d.id !== nextDriver.id);
          return {
            ...truck,
            drivers: [...others, nextDriver]
          };
        })
      );
      setKnownGateSelectedDriverId(createdDriver.id);
      knownGateSelectedDriverIdRef.current = createdDriver.id;
      setShowKnownGateDriverModal(false);
      toast.success(
        reusedExisting
          ? t('drivers.linkedExistingToTruck')
          : t('weighing.deferred.driverCreatedAssigned', {
              defaultValue: 'Nouveau chauffeur créé et assigné au camion.'
            })
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        t('weighing.deferred.driverCreateAssignFailed', {
          defaultValue: 'Impossible de créer/assigner le chauffeur.'
        });
      setKnownGateDriverError(msg);
    } finally {
      setKnownGateDriverSaving(false);
    }
  };

  const openClientTruckCreateModal = () => {
    setClientTruckCreateError('');
    setClientTruckForm({ plateNumber: '' });
    setShowClientTruckCreateModal(true);
  };

  const handleCreateClientTruck = async () => {
    const plateNumber = String(clientTruckForm.plateNumber || '').trim().toUpperCase();
    if (!plateNumber) {
      setClientTruckCreateError(t('trucks.validations.plateNumberRequired'));
      return;
    }
    setClientTruckCreateSaving(true);
    setClientTruckCreateError('');
    try {
      let transporteurId = String(formData.transporteurId || '').trim();
      if (!transporteurId && isOperator() && formData.clientId) {
        try {
          const tRes = await transporteursAPI.getAll({ clientId: formData.clientId, limit: 1 });
          const firstT = (tRes?.data?.transporteurs || [])[0];
          if (firstT?.id) transporteurId = firstT.id;
        } catch {
          // keep empty; backend can still create truck without explicit transporteur linkage
        }
      }

      const createdRes = await trucksAPI.create({
        plateNumber,
        ...(transporteurId ? { transporteurIds: [transporteurId] } : {}),
        status: 'active',
        requestContext: 'PRE_TARE_REQUEST'
      });
      const createdTruck = createdRes?.data?.truck;
      if (!createdTruck?.id) {
        throw new Error('Truck creation failed');
      }

      setTrucks((prev) => {
        const exists = prev.some((row) => row.id === createdTruck.id);
        if (exists) return prev;
        const normalized = {
          ...createdTruck,
          drivers: Array.isArray(createdTruck.drivers) ? createdTruck.drivers : [],
          transporteurs: Array.isArray(createdTruck.transporteurs) ? createdTruck.transporteurs : [],
          clients: Array.isArray(createdTruck.clients) ? createdTruck.clients : []
        };
        return [...prev, normalized];
      });
      setFormData((prev) => ({
        ...prev,
        truckId: createdTruck.id,
        transporteurId: transporteurId || prev.transporteurId || ''
      }));
      setErrors((prev) => ({ ...prev, truckId: '' }));
      setShowClientTruckCreateModal(false);
      toast.success(t('trucks.truckCreated'));
    } catch (err) {
      setClientTruckCreateError(
        err?.response?.data?.message || t('trucks.crud.createError')
      );
    } finally {
      setClientTruckCreateSaving(false);
    }
  };

  const openClientDriverCreateModal = () => {
    if (!formData.truckId) {
      toast.error(t('weighing.validations.truckRequired'));
      return;
    }
    setClientDriverCreateError('');
    setClientDriverForm({ firstName: '', lastName: '', phone: '' });
    setClientDriverIdentityMatch(null);
    setClientDriverIdentityMatchLoading(false);
    setShowClientDriverCreateModal(true);
  };

  const handleCreateClientDriver = async () => {
    const firstName = String(clientDriverForm.firstName || '').trim();
    const lastName = String(clientDriverForm.lastName || '').trim();
    const phone = String(clientDriverForm.phone || '').trim();
    if (!firstName || !lastName) {
      setClientDriverCreateError(t('drivers.validation.nameRequired'));
      return;
    }
    setClientDriverCreateSaving(true);
    setClientDriverCreateError('');
    try {
      const createdRes = await driversAPI.create({
        firstName,
        lastName,
        phone: phone || undefined,
        status: 'active',
        requestContext: 'PRE_TARE_REQUEST'
      });
      const createdDriver = createdRes?.data?.driver;
      if (!createdDriver?.id) {
        throw new Error('Driver creation failed');
      }

      const existingDriverIds = (drivers || []).map((d) => d?.id).filter(Boolean);
      const mergedDriverIds = Array.from(new Set([...existingDriverIds, createdDriver.id]));
      await trucksAPI.update(formData.truckId, {
        driverIds: mergedDriverIds,
        requestContext: 'PRE_TARE_REQUEST'
      });

      setDrivers((prev) => {
        const exists = prev.some((row) => row.id === createdDriver.id);
        if (exists) return prev;
        return [
          ...prev,
          {
            id: createdDriver.id,
            firstName: createdDriver.firstName || firstName,
            lastName: createdDriver.lastName || lastName,
            phone: createdDriver.phone || phone || ''
          }
        ];
      });
      setFormData((prev) => ({ ...prev, driverId: createdDriver.id }));
      setErrors((prev) => ({ ...prev, driverId: '' }));
      setShowClientDriverCreateModal(false);
      const reusedExisting = createdRes?.data?.reusedExisting === true;
      toast.success(reusedExisting ? t('drivers.linkedExistingToTruck') : t('drivers.created'));
    } catch (err) {
      setClientDriverCreateError(
        err?.response?.data?.message || t('drivers.messages.createError')
      );
    } finally {
      setClientDriverCreateSaving(false);
    }
  };

  const renderDeferredTareMatchDropdown = (onSelectLoad) => {
    if (!deferredPlateTypeaheadMinOk) {
      return (
        <p className="text-xs text-gray-500 mt-1.5 px-0.5">{t('weighing.deferred.plateTypeaheadHint')}</p>
      );
    }
    if (deferredLookupLoading) {
      return <p className="text-xs text-gray-600 mt-1.5 px-0.5">{t('common.loading')}</p>;
    }
    if (deferredTareMatches.length === 0) {
      return null;
    }
    return (
      <ul
        className="relative z-30 w-full mt-1 h-[300px] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
        role="listbox"
      >
        {deferredTareMatches.map((l) => {
          const plate = l.truck?.plateNumber || '—';
          const driver = (l.truck?.driverName && String(l.truck.driverName).trim()) || '';
          const phone = (l.truck?.driverPhone && String(l.truck.driverPhone).trim()) || '';
          const plateInlineDetails = [
            driver || null,
            phone ? `Tel: ${phone}` : null
          ].filter(Boolean).join(', ');
          const plateDisplay = plateInlineDetails ? `${plate} (${plateInlineDetails})` : plate;
          const wb = l.weighbridge?.name || '';
          return (
            <li key={l.id} className="border-b border-gray-100 last:border-b-0">
              <button
                type="button"
                className="w-full text-left px-3 py-2.5 hover:bg-teal-50/90 focus:bg-teal-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-teal-300"
                onClick={() => onSelectLoad(l)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 tracking-wide truncate">{plateDisplay}</div>
                    {wb ? <div className="text-xs text-gray-500 mt-0.5 truncate">{wb}</div> : null}
                  </div>
                  <span
                    className="inline-flex items-center justify-center text-teal-700"
                    title={t('loads.continueWeighing')}
                    aria-label={t('loads.continueWeighing')}
                  >
                    <ArrowRightCircleIcon className="h-5 w-5" />
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
    <Modal
      isOpen={isOpen && !showPonctuelCreateModal}
      onClose={handleClose}
      title={modalTitle}
      size={showPeseurEntryGate ? 'xl' : 'lg'}
      closeOnOverlayClick={false}
    >
      <form
        onSubmit={handleSubmit}
        className={`space-y-4 pb-6 ${showPeseurEntryGate ? 'h-[560px]' : ''}`}
      >
        {errors.general && !isPrepaidBalanceError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {errors.general}
          </div>
        )}

        {clientBillingInfo && (isPeseur() || isManagerOrAdmin()) && (
          <div className="space-y-2">
            {clientBillingInfo.billingType === 'PAYING' && !clientBillingInfo.canWeigh && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded text-sm">
                {t('clients.billing.payingNoBalance')}
                {renderOpenClientDetailsLink()}
              </div>
            )}
            {clientBillingInfo.creditWeighingHold && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
                {t('clients.billing.creditHold')}
                {renderOpenClientDetailsLink()}
              </div>
            )}
            {clientBillingInfo.warnCreditOverLimit && !clientBillingInfo.creditWeighingHold && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded text-sm">
                {t('clients.billing.creditOverLimit')}
                {renderOpenClientDetailsLink()}
              </div>
            )}
            {clientBillingInfo.warnCreditNearLimit && !clientBillingInfo.warnCreditOverLimit && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 px-4 py-3 rounded text-sm">
                {t('clients.billing.creditNearLimit')}
                {renderOpenClientDetailsLink()}
              </div>
            )}
          </div>
        )}

        {showPeseurEntryGate && (
          <div className="rounded-xl border border-teal-200 bg-gradient-to-b from-teal-50/95 to-white p-5 space-y-4 shadow-sm">
            <div className="flex flex-wrap gap-2 items-start">
              <div className="flex-1 min-w-[12rem] relative">
                <label className="block text-xs font-medium text-teal-900 mb-1" htmlFor="peseur-gate-plate">
                  {t('weighing.ponctuel.plateLabel')}
                </label>
                <input
                  id="peseur-gate-plate"
                  type="text"
                  value={deferredPlateLookup}
                  onChange={(e) => {
                    setDeferredPlateLookup(e.target.value);
                    setPeseurGatePlateMemory(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-teal-200 rounded-md tracking-wide focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder={t('weighing.deferred.plateLookupPlaceholder')}
                  disabled={loading}
                  autoComplete="off"
                />
                {renderDeferredTareMatchDropdown(pickDeferredOpenTareFromGate)}
              </div>
            </div>
            {peseurGateNoMatch && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/90 px-4 py-3 space-y-3 text-sm text-amber-950">
                <p className="leading-relaxed">{t('weighing.deferred.gateNoMatchBody')}</p>
                {knownGateTruckLoading && (
                  <p className="text-xs text-amber-900/90">
                    {t('weighing.deferred.knownTruckLoading', { defaultValue: 'Recherche du camion dans les enregistrements...' })}
                  </p>
                )}
                {requestedGateLookupLoading && (
                  <p className="text-xs text-amber-900/90">
                    {t('weighing.deferred.requestLookupLoading')}
                  </p>
                )}
                {!requestedGateLookupLoading && requestedGateMatches.length > 0 && (
                  <div className="rounded-md border border-emerald-300 bg-emerald-50/70 p-3 space-y-2.5">
                    <p className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">
                      {t('weighing.deferred.requestMatchesPrompt', { count: requestedGateMatches.length })}
                    </p>
                    <div className="space-y-2">
                      {requestedGateMatches.slice(0, 5).map((load) => (
                        <div
                          key={load.id}
                          className="rounded-md border border-emerald-200 bg-white/90 px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-emerald-950 truncate">
                              {load.truck?.plateNumber || '—'}
                            </p>
                            {(() => {
                              const driverLabel = String(load.truck?.driverName || '').trim();
                              const phoneLabel = String(load.truck?.driverPhone || '').trim();
                              if (!driverLabel && !phoneLabel) return null;
                              const driverText = driverLabel && phoneLabel
                                ? `${driverLabel} (${phoneLabel})`
                                : (driverLabel || phoneLabel);
                              return (
                                <p className="text-xs text-emerald-900/90 truncate">
                                  {driverText}
                                </p>
                              );
                            })()}
                            <p className="text-xs text-emerald-900/90 truncate">
                              {load.client?.companyName || '—'} • {load.product?.name || '—'}
                            </p>
                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                              <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-200/80 text-emerald-950 uppercase tracking-wide">
                                {String(load.status || '').toUpperCase() === 'ACCEPTED'
                                  ? t('loads.statuses.accepted')
                                  : t('loads.statuses.requested')}
                              </span>
                            </div>
                            <p className="text-xs text-emerald-900/75">
                              {load.loadDate ? formatDateTime(load.loadDate) : '—'}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border-emerald-300 text-emerald-900 hover:bg-emerald-100"
                            onClick={() => pickRequestedLoadFromGate(load)}
                          >
                            {t('weighing.deferred.useRequestAsTare')}
                          </Button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-emerald-900/90">
                      {t('weighing.deferred.requestAndNewTareHint')}
                    </p>
                  </div>
                )}
                {!knownGateTruckLoading && knownGateTruckMatches.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-white/70 p-3 space-y-2.5">
                    <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">
                      {t('weighing.deferred.knownTruckPrompt', {
                        defaultValue: 'Camion déjà vu au port: sélectionnez le camion et le chauffeur'
                      })}
                    </p>
                    <Select
                      label={t('weighing.truck')}
                      value={knownGateSelectedTruckId}
                      onChange={(e) => {
                        const truckId = String(e.target.value || '').trim();
                        setKnownGateSelectedTruckId(truckId);
                        knownGateSelectedTruckIdRef.current = truckId;
                        const selectedTruck = knownGateTruckMatches.find((truck) => truck.id === truckId);
                        const firstDriverId =
                          selectedTruck?.drivers?.[0]?.id || selectedTruck?.assignedDrivers?.[0]?.driver?.id || '';
                        setKnownGateSelectedDriverId(firstDriverId || '__NEW__');
                        knownGateSelectedDriverIdRef.current = firstDriverId || '__NEW__';
                      }}
                      options={knownGateTruckMatches.map((truck) => {
                        const transporteur = truck.transporteurs?.[0]?.companyName;
                        return {
                          value: truck.id,
                          label: transporteur ? `${truck.plateNumber} - ${transporteur}` : truck.plateNumber
                        };
                      })}
                      placeholder={t('weighing.validations.truckRequired')}
                    />
                    <Select
                      label={t('weighing.deferred.driverLabel', { defaultValue: 'Chauffeur' })}
                      value={knownGateSelectedDriverId}
                      onChange={(e) => {
                        const driverId = String(e.target.value || '').trim();
                        setKnownGateSelectedDriverId(driverId);
                        knownGateSelectedDriverIdRef.current = driverId;
                      }}
                      options={[
                        ...knownGateDriverOptions.map((driver) => {
                          const fullName = `${driver.firstName || ''} ${driver.lastName || ''}`.trim();
                          return {
                            value: driver.id,
                            label: driver.phone ? `${fullName || driver.id} - ${driver.phone}` : (fullName || driver.id)
                          };
                        }),
                        {
                          value: '__NEW__',
                          label: t('weighing.deferred.driverChangedOption', {
                            defaultValue: 'Nouveau chauffeur (chauffeur changé pour cette opération)'
                          })
                        }
                      ]}
                      placeholder={t('weighing.deferred.driverLabel', { defaultValue: 'Chauffeur' })}
                    />
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-amber-900/90">
                        {t('weighing.deferred.driverCreateAssignHint', {
                          defaultValue: 'Besoin d’un nouveau chauffeur ? Créez-le et assignez-le à ce camion.'
                        })}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={openKnownGateDriverCreateModal}
                        disabled={!knownGateSelectedTruck}
                      >
                        {t('weighing.deferred.driverCreateAssignAction', { defaultValue: 'Nouveau chauffeur' })}
                      </Button>
                    </div>
                    {knownGateSelectedDriverId === '__NEW__' && (
                      <p className="text-xs text-amber-900/90">
                        {t('weighing.deferred.driverChangedHint', {
                          defaultValue: 'Le camion sera repris et vous pourrez renseigner/assigner un nouveau chauffeur pour ce pesage.'
                        })}
                      </p>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="primary" size="sm" onClick={handlePeseurGateProceedPonctuel}>
                    {t('weighing.deferred.gateClientPonctuel')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-sky-300 bg-sky-100 text-sky-900 hover:bg-sky-200"
                    onClick={() => handlePeseurGateProceedDdpByBilling('PAYING_OR_CREDIT')}
                  >
                    {t('weighing.deferred.gateClientComptantCredit', { defaultValue: 'Client avec DDP' })}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {(!isPeseur() || peseurEntryGatePassed) && (
        <>
        {isPeseur() &&
          !continueLoadIdProp &&
          !formUnlockedForContinueSecondPass &&
          (ponctuelModeFromPeseurGate ||
            Boolean(peseurGateBillingFilter) ||
            Boolean(String(selectedGateRequestedLoadId || '').trim())) && (
          <div className="flex justify-end">
            <Button type="button" variant="outline" size="sm" onClick={handleBackToPeseurEntryGate}>
              {t('common.back', { defaultValue: 'Back' })}
            </Button>
          </div>
        )}
        {isPeseur() &&
          !String(formData.ddpId || '').trim() &&
          !formUnlockedForContinueSecondPass &&
          formData.creationFlow !== 'DEFERRED_TARE' &&
          !ponctuelModeFromPeseurGate &&
          !Boolean(peseurGateBillingFilter) && (
          <div className="checkbox-field flex items-center gap-2">
            <input
              id="peseurPonctuelMode"
              type="checkbox"
              checked={formData.peseurPonctuelMode}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData((prev) => ({
                  ...prev,
                  peseurPonctuelMode: checked,
                  creationFlow: checked ? 'STANDARD' : prev.creationFlow,
                  ddpId: checked ? '' : prev.ddpId,
                  truckId: checked ? '' : prev.truckId,
                  transporteurId: checked ? '' : prev.transporteurId,
                  truckPlateNumber: checked ? prev.truckPlateNumber : '',
                  ...(checked ? { transactionType: 'EXPORT' } : {})
                }));
                if (checked) {
                  setPeseurGateBillingFilter(null);
                  setDdps([]);
                  setTrucks([]);
                } else {
                  loadInitialData();
                }
              }}
              className="checkbox-app"
            />
            <label htmlFor="peseurPonctuelMode" className="text-sm text-gray-800 cursor-pointer">
              {t('weighing.ponctuel.modeLabel')}
            </label>
          </div>
        )}

        {isPeseur() &&
          !formData.peseurPonctuelMode &&
          !continueLoadIdProp &&
          !formUnlockedForContinueSecondPass &&
          !Boolean(peseurGateBillingFilter) &&
          !requestToTareContext && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
            <span className="block text-sm font-medium text-gray-800">
              {t('weighing.creationFlow.title')}
            </span>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="creationFlow"
                  value="STANDARD"
                  checked={formData.creationFlow === 'STANDARD'}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <span>{t('weighing.creationFlow.standard')}</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="creationFlow"
                  value="DEFERRED_TARE"
                  checked={formData.creationFlow === 'DEFERRED_TARE'}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <span>{t('weighing.creationFlow.deferredTare')}</span>
              </label>
            </div>
            <p className="text-xs text-gray-600 leading-snug">{t('weighing.creationFlow.hint')}</p>
          </div>
        )}

        {isPeseur() && formData.peseurPonctuelMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
            {formUnlockedForContinueSecondPass && continueSecondPassReadOnly ? (
              <>
                <div>
                  <span className={secondPassRoLabel}>{t('weighing.ponctuel.selectClient')}</span>
                  <div className={secondPassRoBox}>{continueSecondPassReadOnly.ponctuelClientName}</div>
                </div>
                <div>
                  <span className={secondPassRoLabel}>{t('weighing.ponctuel.plateLabel')}</span>
                  <div className={`${secondPassRoBox} uppercase tracking-wide`}>
                    {continueSecondPassReadOnly.plateDisplay}
                  </div>
                </div>
              </>
            ) : (
              <>
            {!hidePonctuelClientAndProductForTare && (
            <div>
              <SearchableSelect
                label={t('weighing.ponctuel.selectClient')}
                name="clientId"
                value={formData.clientId}
                onChange={handleInputChange}
                options={ponctuelClients.map((c) => {
                  const label = c.companyName || c.contactName || c.id;
                  const searchText = [
                    label,
                    c.contactName,
                    c.companyName,
                    c.contactEmail,
                    c.contactPhone,
                    c.firstName,
                    c.lastName
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return { value: c.id, label, searchText };
                })}
                placeholder={t('weighing.ponctuel.clientPlaceholder')}
                disabled={loadingPonctuelClients}
                required
                error={errors.clientId}
                dropdownFooter={
                  isPeseur()
                    ? renderPonctuelClientDropdownFooter('weighing.addPonctuelClient', {
                        disabled: loadingPonctuelClients
                      })
                    : null
                }
              />
            </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('weighing.ponctuel.plateLabel')} *
              </label>
              <input
                type="text"
                name="truckPlateNumber"
                value={formData.truckPlateNumber}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md ${
                  errors.truckPlateNumber ? 'border-red-300' : ''
                }`}
                placeholder={t('weighing.ponctuel.platePlaceholder')}
              />
              {errors.truckPlateNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.truckPlateNumber}</p>
              )}
            </div>
            {hidePonctuelClientAndProductForTare && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 mt-1 border-t border-amber-200/80">
                <p className="sm:col-span-2 text-xs text-amber-900/90">
                  {t('weighing.ponctuel.driverStoredOnTruckHint')}
                  {!forceDriverOnPonctualTare ? (
                    <span className="block mt-1">{t('weighing.ponctuel.driverPairOptionalHint')}</span>
                  ) : null}
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('weighing.deferred.driverFullName')}
                    {forceDriverOnPonctualTare ? <span className="text-red-500 ml-1">*</span> : null}
                  </label>
                  <input
                    type="text"
                    name="driverFullName"
                    value={formData.driverFullName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${
                      errors.driverFullName ? 'border-red-300' : ''
                    }`}
                    disabled={loading}
                    autoComplete="name"
                  />
                  {errors.driverFullName && (
                    <p className="text-red-500 text-sm mt-1">{errors.driverFullName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('weighing.deferred.driverPhone')}
                    {forceDriverOnPonctualTare ? <span className="text-red-500 ml-1">*</span> : null}
                  </label>
                  <input
                    type="tel"
                    name="driverPhone"
                    value={formData.driverPhone}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${
                      errors.driverPhone ? 'border-red-300' : ''
                    }`}
                    disabled={loading}
                    autoComplete="tel"
                  />
                  {errors.driverPhone && (
                    <p className="text-red-500 text-sm mt-1">{errors.driverPhone}</p>
                  )}
                </div>
              </div>
            )}
              </>
            )}
            {showSecondPassTruckNudge && (
              <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-3 text-sm text-indigo-950">
                <p className="mb-2 leading-snug">
                  {t('weighing.twoStep.pendingSecondPassForTruck', { plate: selectedTruckPlateForNudge })}
                </p>
                <p className="mb-3 border-t border-indigo-200/70 pt-2 leading-snug text-indigo-900/90">
                  {t('weighing.twoStep.pendingSecondPassTareDetail', {
                    weight: secondPassPendingTareSummary.weight || '—',
                    dateTime: secondPassPendingTareSummary.dateTime || '—',
                  })}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    onClick={handleSwitchToSecondPassForTruck}
                  >
                    {t('weighing.twoStep.switchToSecondPass')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleDismissSecondPassTruckNudge}
                  >
                    {t('weighing.twoStep.dismissSecondPassNudge')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {peseurWeighbridgeList.length > 1 &&
          (formData.peseurPonctuelMode ||
            (formData.creationFlow === 'DEFERRED_TARE' &&
              formData.weighingPass === 'TARE' &&
              !String(formData.continueLoadId || '').trim())) &&
          (formData.peseurPonctuelMode
            ? String(formData.clientId || '').trim() || formData.weighingPass === 'TARE'
            : true) && (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            {formUnlockedForContinueSecondPass && continueSecondPassReadOnly ? (
              <>
                <span className={secondPassRoLabel}>{t('weighing.selectWeighbridge')}</span>
                <div className={secondPassRoBox}>{continueSecondPassReadOnly.weighbridgeDisplay}</div>
              </>
            ) : (
              <>
            <SearchableSelect
              label={t('weighing.selectWeighbridge')}
              name="weighbridgeId"
              value={formData.weighbridgeId}
              onChange={handleInputChange}
              options={peseurWeighbridgeList.map((wb) => {
                const name = wb.name || '';
                const loc = wb.location || '';
                const label = [name, loc].filter(Boolean).join(' — ') || wb.id;
                return { value: wb.id, label };
              })}
              placeholder={t('weighing.placeholders.selectWeighbridge')}
              disabled={loading}
              required
              error={errors.weighbridgeId}
            />
            {errors.weighbridgeId && (
              <p className="text-red-500 text-sm mt-1">{errors.weighbridgeId}</p>
            )}
              </>
            )}
          </div>
        )}

        {/* DDP selection: hidden for PESEUR ponctuel / deferred tare (round 1) / deferred bind UI */}
        {!(isPeseur() && formData.peseurPonctuelMode) &&
          !selectedClientIsPonctuel &&
          !(
            isPeseur() &&
            formData.creationFlow === 'DEFERRED_TARE' &&
            (formData.weighingPass === 'TARE' ||
              (formData.weighingPass === 'MARCHANDISE' && secondPassIsDeferred))
          ) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          {formUnlockedForContinueSecondPass && continueSecondPassReadOnly && formData.ddpId ? (
            <>
              <span className={secondPassRoLabel}>{t('weighing.selectDDP')}</span>
              <div className={secondPassRoBox}>
                {continueSecondPassReadOnly.ddpLabel || '—'}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                {t('weighing.twoStep.frozenContextHint')}
              </p>
            </>
          ) : (
            <>
          <SearchableSelect
            label={t('weighing.selectDDP')}
            name="ddpId"
            value={formData.ddpId}
            onChange={handleInputChange}
            options={ddpsForSearchableSelect.map(ddp => {
              const vesselLabel = ddp.navire || ddp.vesselName || '-';
              const manutentionLabel = ddp.manutentionNumber || '-';
              const productLabel = ddp.productsSummary || ddp.productName || t('weighing.noProduct');
              const transactionLabel = ddp.transactionType
                ? t(`loads.transactionTypes.${String(ddp.transactionType).toLowerCase()}`)
                : '';
              const base = `${vesselLabel} - ${manutentionLabel} - ${productLabel}${transactionLabel ? ` - ${transactionLabel}` : ''}`;
              const clientName = getClientDisplayNameForDdp(ddp);
              const label = clientName ? `${base} (${clientName})` : base;

              return {
                value: ddp.id,
                label
              };
            })}
            placeholder={t('weighing.placeholders.selectDDP')}
            disabled={loadingData.ddps}
            required
            error={errors.ddpId}
          />
          {errors.ddpId && <p className="text-red-500 text-sm mt-1">{errors.ddpId}</p>}
          <p className="text-xs text-blue-600 mt-2">
            {t('weighing.ddpSelectionInfo')}
          </p>
            </>
          )}
          {selectedDdp && !formUnlockedForContinueSecondPass && (
            <div className="mt-3 rounded-lg border border-blue-200 bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setDdpDetailsExpanded((prev) => !prev)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-blue-50 transition-colors"
                aria-expanded={ddpDetailsExpanded}
              >
                <span className="text-sm font-medium text-blue-900">
                  {selectedDdp.navire || '-'} {selectedDdp.manutentionNumber ? `- ${selectedDdp.manutentionNumber}` : ''}
                </span>
                {ddpDetailsExpanded ? (
                  <ChevronUpIcon className="h-4 w-4 text-blue-700 shrink-0" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-blue-700 shrink-0" />
                )}
              </button>
              {ddpDetailsExpanded && (
                <div className="border-t border-blue-200 px-3 py-3">
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-blue-700 font-medium">{t('tables.client')}</dt>
                      <dd className="text-gray-900 mt-1">{selectedDdpClientName || '—'}</dd>
                    </div>
                    {!isPeseur() && (
                      <>
                        <div>
                          <dt className="text-blue-700 font-medium">{t('weighing.scheduleDate')}</dt>
                          <dd className="text-gray-900 mt-1">
                            {formData.scheduleDate || '—'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-blue-700 font-medium">
                            {t('loads.modal.dateTime', { defaultValue: 'Heure' })}
                          </dt>
                          <dd className="text-gray-900 mt-1">
                            {formData.scheduleTime || '—'}
                          </dd>
                        </div>
                      </>
                    )}
                    <div>
                      <dt className="text-blue-700 font-medium">{t('weighing.transactionType')}</dt>
                      <dd className="text-gray-900 mt-1">
                        {selectedDdp.transactionType
                          ? t(`loads.transactionTypes.${String(selectedDdp.transactionType).toLowerCase()}`)
                          : '—'}
                      </dd>
                    </div>
                    <div className="md:col-span-2">
                      <dt className="text-blue-700 font-medium">{t('weighing.selectWeighbridge')}</dt>
                      <dd className="text-gray-900 mt-1">
                        {selectedDdp.weighbridge?.name
                          ? `${selectedDdp.weighbridge.name}${selectedDdp.weighbridge.location ? ` - ${selectedDdp.weighbridge.location}` : ''}`
                          : (selectedDdp.port?.name || '—')}
                      </dd>
                    </div>
                    <div className="md:col-span-2">
                      <dt className="text-blue-700 font-medium">{t('tables.product')}</dt>
                      <dd className="text-gray-900 mt-1">{selectedDdpProductsSummary}</dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {formUnlockedForDeferredSecondPassBind && (
          <div className="rounded-lg border border-teal-200 bg-teal-50/90 p-4 mb-4 space-y-3">
            <p className="text-sm font-medium text-teal-950">{t('weighing.deferred.step2Title')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <span className={secondPassRoLabel}>{t('weighing.selectTruck')}</span>
                <div className={secondPassRoBox}>
                  {continueSecondPassReadOnly?.truckDisplay || String(formData.truckPlateNumber || '').trim() || '—'}
                </div>
              </div>
              <div>
                <span className={secondPassRoLabel}>{t('weighing.selectDriver')}</span>
                <div className={secondPassRoBox}>{continueSecondPassReadOnly?.driverDisplay || '—'}</div>
              </div>
            </div>
            <SearchableSelect
              label={t('weighing.selectClient')}
              name="clientId"
              value={formData.clientId}
              onChange={handleInputChange}
              options={deferredClients.map((c) => {
                const baseLabel = c.companyName || c.contactName || c.id;
                const phone = String(c.contactPhone || '').trim();
                const label = phone ? `${baseLabel} (${phone})` : baseLabel;
                const searchText = [label, c.contactName, c.companyName, c.contactEmail, c.contactPhone]
                  .filter(Boolean)
                  .join(' ');
                return { value: c.id, label, searchText };
              })}
              placeholder={t('weighing.placeholders.selectClient')}
              disabled={loading}
              required
              error={errors.clientId}
              dropdownFooter={
                isPeseur()
                  ? renderPonctuelClientDropdownFooter('weighing.addPonctuelClient', {
                      disabled: loading
                    })
                  : null
              }
            />
            {errors.clientId && <p className="text-red-500 text-sm mt-1">{errors.clientId}</p>}
            {String(formData.clientId || '').trim() && (
              <>
                {!selectedClientIsPonctuel && (
                  <>
                    <SearchableSelect
                      label={`${t('weighing.selectDDP')} *`}
                      name="ddpId"
                      value={formData.ddpId}
                      onChange={handleInputChange}
                      options={filteredDdpsDeferred.map((ddp) => {
                        const vesselLabel = ddp.navire || ddp.vesselName || '-';
                        const manutentionLabel = ddp.manutentionNumber || '-';
                        const productLabel = ddp.productsSummary || ddp.productName || t('weighing.noProduct');
                        const transactionLabel = ddp.transactionType
                          ? t(`loads.transactionTypes.${String(ddp.transactionType).toLowerCase()}`)
                          : '';
                        const base = `${vesselLabel} - ${manutentionLabel} - ${productLabel}${transactionLabel ? ` - ${transactionLabel}` : ''}`;
                        const clientName = getClientDisplayNameForDdp(ddp);
                        const label = clientName ? `${base} (${clientName})` : base;
                        return { value: ddp.id, label };
                      })}
                      placeholder={t('weighing.placeholders.selectDDP')}
                      disabled={loading || loadingData.ddps}
                      required
                      error={errors.ddpId}
                    />
                    {errors.ddpId && <p className="text-red-500 text-sm mt-1">{errors.ddpId}</p>}
                  </>
                )}
                {(selectedClientIsPonctuel || String(formData.ddpId || '').trim()) &&
                  (shouldShowProductSelector ? (
                    <SearchableSelect
                      ref={productSelectRef}
                      label={t('weighing.selectProduct')}
                      name="productId"
                      value={formData.productId}
                      inlineLabel={!isOperator()}
                      onChange={handleInputChange}
                      options={productSelectOptions.map((product) => ({
                        value: product.id,
                        label: product.name
                      }))}
                      placeholder={t('weighing.placeholders.selectProduct')}
                      disabled={loadingData.products || (!selectedClientIsPonctuel && !formData.ddpId)}
                      required
                      error={errors.productId}
                      dropdownFooter={
                        isPeseur()
                          ? ({ closeDropdown }) => (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full justify-center"
                              onClick={() => {
                                closeDropdown();
                                setShowQuickProductModal(true);
                              }}
                              disabled={loadingData.products}
                            >
                              <CubeIcon className="h-4 w-4 mr-1.5" />
                              {t('weighing.quickProduct.addProduct')}
                            </Button>
                            )
                          : null
                      }
                    />
                  ) : formData.productId ? (
                    <p className="text-sm text-teal-950">
                      <span className="font-medium">{t('weighing.selectProduct')}: </span>
                      {products.find((p) => p.id === formData.productId)?.name || '—'}
                    </p>
                  ) : null)}
              </>
            )}
          </div>
        )}
        
        <div className="relative">
          <div className={`${!formSectionUnlocked ? 'blur-[1.5px] opacity-75 select-none' : ''} transition-all duration-200`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formUnlockedForDeferredTareStep1 && (
                <div className="md:col-span-2 rounded-lg border border-slate-200 bg-white p-4 space-y-3">
                  <p className="text-sm font-medium text-gray-900">{t('weighing.deferred.step1Title')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('weighing.ponctuel.plateLabel')} *
                      </label>
                      <input
                        type="text"
                        name="truckPlateNumber"
                        value={formData.truckPlateNumber}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md tracking-wide ${
                          errors.truckPlateNumber ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder={t('weighing.ponctuel.platePlaceholder')}
                        disabled={loading}
                      />
                      {errors.truckPlateNumber && (
                        <p className="text-red-500 text-sm mt-1">{errors.truckPlateNumber}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('weighing.deferred.driverFullName')} *
                      </label>
                      <input
                        type="text"
                        name="driverFullName"
                        value={formData.driverFullName}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${
                          errors.driverFullName ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={loading}
                      />
                      {errors.driverFullName && (
                        <p className="text-red-500 text-sm mt-1">{errors.driverFullName}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('weighing.deferred.driverPhone')} *
                      </label>
                      <input
                        type="text"
                        name="driverPhone"
                        value={formData.driverPhone}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${
                          errors.driverPhone ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={loading}
                      />
                      {errors.driverPhone && (
                        <p className="text-red-500 text-sm mt-1">{errors.driverPhone}</p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <span className="block text-sm font-medium text-gray-700 mb-2">
                        {t('weighing.transactionType')}
                      </span>
                      <div className="flex flex-wrap gap-x-6 gap-y-2">
                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name="transactionType"
                            value="EXPORT"
                            checked={formData.transactionType === 'EXPORT'}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <span>{t('weighing.export')}</span>
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name="transactionType"
                            value="IMPORT"
                            checked={formData.transactionType === 'IMPORT'}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <span>{t('weighing.import')}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {(isOperator() || isManagerOrAdmin() || isPeseur()) &&
                !(isPeseur() && formUnlockedForDeferredTareStep1) &&
                !(isPeseur() && formUnlockedForDeferredSecondPassBind) && (
                <div
                  className={`md:col-span-2 grid grid-cols-1 gap-4 ${
                    shouldShowProductSelector && !hidePonctuelClientAndProductForTare
                      ? 'md:grid-cols-2 xl:grid-cols-3'
                      : 'md:grid-cols-2'
                  }`}
                >
                  {shouldShowProductSelector && !hidePonctuelClientAndProductForTare ? (
                    <div className="min-w-0">
                      {formUnlockedForContinueSecondPass && continueSecondPassReadOnly ? (
                        <>
                          <span className={secondPassRoLabel}>{t('weighing.selectProduct')}</span>
                          <div className={secondPassRoBox}>{continueSecondPassReadOnly.productName}</div>
                        </>
                      ) : (
                        <SearchableSelect
                          ref={productSelectRef}
                          label={`${t('weighing.selectProduct')}`}
                          name="productId"
                          value={formData.productId}
                          inlineLabel={!isOperator()}
                          onChange={handleInputChange}
                          options={productSelectOptions.map((product) => ({
                            value: product.id,
                            label: product.name
                          }))}
                          placeholder={t('weighing.placeholders.selectProduct')}
                          disabled={!formSectionUnlocked || loadingData.products}
                          required
                          error={errors.productId}
                          dropdownFooter={
                            isPeseur()
                              ? ({ closeDropdown }) => (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="w-full justify-center"
                                  onClick={() => {
                                    closeDropdown();
                                    setShowQuickProductModal(true);
                                  }}
                                  disabled={!formSectionUnlocked || loadingData.products}
                                >
                                  <CubeIcon className="h-4 w-4 mr-1.5" />
                                  {t('weighing.quickProduct.addProduct')}
                                </Button>
                                )
                              : null
                          }
                        />
                      )}
                    </div>
                  ) : isPeseur() && formUnlockedForContinueSecondPass && continueSecondPassReadOnly ? (
                    <div className="min-w-0">
                      <span className={secondPassRoLabel}>{t('weighing.selectProduct')}</span>
                      <div className={secondPassRoBox}>{continueSecondPassReadOnly.productName}</div>
                    </div>
                  ) : null}
                  {!isPeseur() && !isOperator() && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('weighing.scheduleDate')} *
                        </label>
                        <input
                          type="date"
                          name="scheduleDate"
                          value={formData.scheduleDate}
                          onChange={handleInputChange}
                          onInvalid={(e) =>
                            e.currentTarget.setCustomValidity(
                              t('common.required', { defaultValue: 'Ce champ est requis' })
                            )
                          }
                          onInput={(e) => e.currentTarget.setCustomValidity('')}
                          min={new Date().toISOString().split('T')[0]}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            errors.scheduleDate ? 'border-red-300' : ''
                          }`}
                          disabled={!formSectionUnlocked}
                          required
                        />
                        {errors.scheduleDate && <p className="text-red-500 text-sm mt-1">{errors.scheduleDate}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('loads.modal.dateTime', { defaultValue: 'Heure' })} *
                        </label>
                        <input
                          type="time"
                          name="scheduleTime"
                          value={formData.scheduleTime}
                          onChange={handleInputChange}
                          onInvalid={(e) =>
                            e.currentTarget.setCustomValidity(
                              t('common.required', { defaultValue: 'Ce champ est requis' })
                            )
                          }
                          onInput={(e) => e.currentTarget.setCustomValidity('')}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            errors.scheduleTime ? 'border-red-300' : ''
                          }`}
                          disabled={!formSectionUnlocked}
                          required
                        />
                        {errors.scheduleTime && <p className="text-red-500 text-sm mt-1">{errors.scheduleTime}</p>}
                      </div>
                    </>
                  )}
                </div>
              )}

              {!formData.peseurPonctuelMode &&
                !formUnlockedForDeferredTareStep1 &&
                !(isPeseur() && formUnlockedForDeferredSecondPassBind) && (
              <div className="md:col-span-1 w-full min-w-0">
                {formUnlockedForContinueSecondPass && continueSecondPassReadOnly ? (
                  <>
                    <span className={secondPassRoLabel}>{t('weighing.selectTruckOnly')}</span>
                    <div className={secondPassRoBox}>{continueSecondPassReadOnly.truckDisplay}</div>
                  </>
                ) : lockTruckSelectionToGateChoice ? (
                  <>
                    <span className={secondPassRoLabel}>{t('weighing.truck', { defaultValue: 'Camion' })}</span>
                    <div className={secondPassRoBox}>
                      {trucks.find((truck) => truck.id === formData.truckId)?.plateNumber || '—'}
                    </div>
                  </>
                ) : (
                <SearchableSelect
                  label={isPeseur() && Boolean(peseurGateBillingFilter)
                    ? `${t('weighing.deferred.driverLabel', { defaultValue: 'Chauffeur' })}`
                    : `${t('weighing.selectTruckOnly')}`}
                  name="truckId"
                  value={formData.truckId}
                  inlineLabel={!isOperator()}
                  options={trucks.map((truck) => {
                    const seenDriverKeys = new Set();
                    const driverNames = [];
                    (truck.drivers || []).forEach((driver) => {
                      const fullName = `${driver?.firstName || ''} ${driver?.lastName || ''}`.trim();
                      if (!fullName) return;
                      const phone = String(driver?.phone || '').trim();
                      const key = `${fullName.toLowerCase()}|${phone}`;
                      if (seenDriverKeys.has(key)) return;
                      seenDriverKeys.add(key);
                      driverNames.push(phone ? `${fullName} (${phone})` : fullName);
                    });
                    (truck.assignedDrivers || []).forEach((dt) => {
                      const fullName = `${dt.driver?.firstName || ''} ${dt.driver?.lastName || ''}`.trim();
                      if (!fullName) return;
                      const key = `${fullName.toLowerCase()}|`;
                      if (seenDriverKeys.has(key)) return;
                      seenDriverKeys.add(key);
                      driverNames.push(fullName);
                    });

                    let label = truck.plateNumber;
                    if (driverNames.length > 0) {
                      label += ` - ${driverNames.join(', ')}`;
                    }

                    return {
                      value: truck.id,
                      label
                    };
                  })}
                  placeholder={
                    isPeseur() && Boolean(peseurGateBillingFilter)
                      ? t('weighing.placeholders.selectDriver')
                      : t('weighing.placeholders.selectTruck')
                  }
                  disabled={!formSectionUnlocked || !formData.clientId || loadingData.trucks}
                  required
                  error={errors.truckId}
                  dropdownFooter={
                    isOperator()
                      ? ({ closeDropdown }) => (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full justify-center"
                          onClick={() => {
                            closeDropdown();
                            openClientTruckCreateModal();
                          }}
                          disabled={!formSectionUnlocked}
                        >
                          <UserPlusIcon className="h-4 w-4 mr-1.5" />
                          {t('trucks.addTruck')}
                        </Button>
                        )
                      : null
                  }
                  onChange={(e) => {
                    const truckId = e.target.value;
                    const selectedTruck = trucks.find((truck) => truck.id === truckId);
                    const matchingTransporteur = (selectedTruck?.transporteurs || []).find(() =>
                      (selectedTruck?.clients || []).some((client) => client.id === formData.clientId)
                    ) || selectedTruck?.transporteurs?.[0];

                    setFormData((prev) => ({
                      ...prev,
                      truckId,
                      transporteurId: matchingTransporteur?.id || prev.transporteurId || ''
                    }));
                    if (errors.truckId) {
                      setErrors((prev) => ({ ...prev, truckId: '' }));
                    }
                  }}
                />
                )}
              </div>
              )}

              {formData.truckId &&
                !formData.peseurPonctuelMode &&
                !(isPeseur() && formUnlockedForDeferredSecondPassBind) &&
                ((formUnlockedForContinueSecondPass && continueSecondPassReadOnly) || lockTruckSelectionToGateChoice || drivers.length > 1 || isOperator()) && (
                <div className="md:col-span-1">
                  {formUnlockedForContinueSecondPass && continueSecondPassReadOnly ? (
                    <>
                      <span className={secondPassRoLabel}>{t('weighing.selectDriver')}</span>
                      <div className={secondPassRoBox}>{continueSecondPassReadOnly.driverDisplay}</div>
                    </>
                  ) : (
                    <SearchableSelect
                      label={lockTruckSelectionToGateChoice
                        ? `${t('weighing.deferred.driverLabel', { defaultValue: 'Chauffeur' })}`
                        : `${t('weighing.selectDriver')}`}
                      labelAside={
                        showClientDriverCreateModal &&
                        (clientDriverIdentityMatchLoading || clientDriverIdentityMatch) ? (
                          clientDriverIdentityMatchLoading ? (
                            <span className="text-[11px] font-medium text-slate-500 normal-case">
                              {t('common.loading')}
                            </span>
                          ) : (
                            <span
                              className="inline-flex max-w-full items-center rounded-md border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal-900 normal-case"
                              title={t('weighing.driverIdentityExistsHintDetail')}
                            >
                              {drivers.some((d) => d.id === clientDriverIdentityMatch?.id)
                                ? t('weighing.driverIdentityExistsOnTruckBadge')
                                : t('weighing.driverIdentityExistsBadge')}
                            </span>
                          )
                        ) : undefined
                      }
                      name="driverId"
                      value={formData.driverId}
                      onChange={handleInputChange}
                      options={drivers.map((driver) => ({
                        value: driver.id,
                        label: `${driver.firstName} ${driver.lastName}${driver.username ? ` (@${driver.username})` : ''}${driver.phone ? ` - ${driver.phone}` : ''}`
                      }))}
                      placeholder={t('weighing.placeholders.selectDriver')}
                      disabled={!formSectionUnlocked || loadingData.drivers}
                      required
                      error={errors.driverId}
                      dropdownFooter={
                        isOperator()
                          ? ({ closeDropdown }) => (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full justify-center"
                              onClick={() => {
                                closeDropdown();
                                openClientDriverCreateModal();
                              }}
                              disabled={!formSectionUnlocked || !formData.truckId}
                            >
                              <UserPlusIcon className="h-4 w-4 mr-1.5" />
                              {t('drivers.addDriver')}
                            </Button>
                            )
                          : null
                      }
                    />
                  )}
                  {errors.driverId && <p className="text-red-500 text-sm mt-1">{errors.driverId}</p>}
                  {showClientDriverCreateModal &&
                    clientDriverIdentityMatch &&
                    !clientDriverIdentityMatchLoading && (
                      <p className="text-xs text-teal-900/95 mt-1.5 leading-snug">
                        {drivers.some((d) => d.id === clientDriverIdentityMatch.id)
                          ? t('weighing.driverIdentityExistsOnTruckDetail')
                          : t('weighing.driverIdentityExistsHintDetail')}
                      </p>
                    )}
                  <p className="text-xs text-gray-500 mt-1">{t('weighing.driverSelectionInfo')}</p>
                </div>
              )}

              {isOperator() &&
                !(isPeseur() && formUnlockedForDeferredTareStep1) &&
                !(isPeseur() && formUnlockedForDeferredSecondPassBind) && (
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('weighing.scheduleDate')} *
                    </label>
                    <input
                      type="date"
                      name="scheduleDate"
                      value={formData.scheduleDate}
                      onChange={handleInputChange}
                      onInvalid={(e) =>
                        e.currentTarget.setCustomValidity(
                          t('common.required', { defaultValue: 'Ce champ est requis' })
                        )
                      }
                      onInput={(e) => e.currentTarget.setCustomValidity('')}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.scheduleDate ? 'border-red-300' : ''
                      }`}
                      disabled={!formSectionUnlocked}
                      required
                    />
                    {errors.scheduleDate && <p className="text-red-500 text-sm mt-1">{errors.scheduleDate}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('loads.modal.dateTime', { defaultValue: 'Heure' })} *
                    </label>
                    <input
                      type="time"
                      name="scheduleTime"
                      value={formData.scheduleTime}
                      onChange={handleInputChange}
                      onInvalid={(e) =>
                        e.currentTarget.setCustomValidity(
                          t('common.required', { defaultValue: 'Ce champ est requis' })
                        )
                      }
                      onInput={(e) => e.currentTarget.setCustomValidity('')}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.scheduleTime ? 'border-red-300' : ''
                      }`}
                      disabled={!formSectionUnlocked}
                      required
                    />
                    {errors.scheduleTime && <p className="text-red-500 text-sm mt-1">{errors.scheduleTime}</p>}
                  </div>
                </div>
              )}

              {showSecondPassTruckNudge && !formData.peseurPonctuelMode && (
                <div className="md:col-span-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-3 text-sm text-indigo-950">
                  <p className="mb-2 leading-snug">
                    {t('weighing.twoStep.pendingSecondPassForTruck', { plate: selectedTruckPlateForNudge })}
                  </p>
                  <p className="mb-3 border-t border-indigo-200/70 pt-2 leading-snug text-indigo-900/90">
                    {t('weighing.twoStep.pendingSecondPassTareDetail', {
                      weight: secondPassPendingTareSummary.weight || '—',
                      dateTime: secondPassPendingTareSummary.dateTime || '—',
                    })}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      onClick={handleSwitchToSecondPassForTruck}
                    >
                      {t('weighing.twoStep.switchToSecondPass')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleDismissSecondPassTruckNudge}
                    >
                      {t('weighing.twoStep.dismissSecondPassNudge')}
                    </Button>
                  </div>
                </div>
              )}

            </div>

            {isPeseur() && formSectionUnlocked && (
              <div className="border-t pt-4 space-y-3">
                {formData.weighingPass === 'TARE' && (
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-x-4 sm:items-end">
                    <div className="sm:col-span-4 min-w-0">
                      <label htmlFor="emptyWeight" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('weighing.emptyWeight')}{' '}
                        {(isManagerOrAdmin() || isPeseur()) && formData.weighingPass !== 'MARCHANDISE' && '*'}
                      </label>
                      <NumericInput
                        id="emptyWeight"
                        ref={emptyWeightInputRef}
                        name="emptyWeight"
                        value={formData.emptyWeight}
                        onChange={handleInputChange}
                        placeholder={t('weighing.placeholders.emptyWeight')}
                        readOnly={!isPeseur() || formData.weighingPass === 'MARCHANDISE'}
                        disabled={!formSectionUnlocked}
                        className={`w-full px-3 py-2 border rounded-md ${
                          isPeseur()
                            ? 'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900'
                            : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                        } ${errors.emptyWeight ? 'border-red-300' : 'border-gray-300'}`}
                      />
                      {!isPeseur() && (
                        <p className="text-xs text-gray-500 mt-1">{t('weighing.emptyWeightAutoFilled')}</p>
                      )}
                      {errors.emptyWeight && <p className="text-red-500 text-sm mt-1">{errors.emptyWeight}</p>}
                    </div>
                    <div className="sm:col-span-4 min-w-0">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('weighing.twoStep.tarePassDate')}
                      </label>
                      <input
                        type="date"
                        name="tareWeighDate"
                        value={formData.tareWeighDate}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${
                          errors.tareWeighDate ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.tareWeighDate && (
                        <p className="text-red-500 text-sm mt-1">{errors.tareWeighDate}</p>
                      )}
                    </div>
                    <div className="sm:col-span-4 min-w-0">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('loads.modal.dateTime')}
                      </label>
                      <input
                        type="time"
                        name="tareWeighTime"
                        value={formData.tareWeighTime}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${
                          errors.tareWeighTime ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.tareWeighTime && (
                        <p className="text-red-500 text-sm mt-1">{errors.tareWeighTime}</p>
                      )}
                    </div>
                  </div>
                )}
                {formData.weighingPass === 'MARCHANDISE' && (
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-x-4 sm:items-end">
                    <div className="sm:col-span-4 min-w-0">
                      <label htmlFor="loadedWeight" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('weighing.loadedWeight')}{' '}
                        {(isManagerOrAdmin() || isPeseur()) && '*'}
                      </label>
                      <NumericInput
                        ref={loadedWeightInputRef}
                        id="loadedWeight"
                        name="loadedWeight"
                        value={formData.loadedWeight}
                        onChange={handleInputChange}
                        placeholder={t('weighing.placeholders.loadedWeight')}
                        readOnly={!formSectionUnlocked || isOperator()}
                        disabled={!formSectionUnlocked}
                        className={`w-full px-3 py-2 border rounded-md ${
                          (!formSectionUnlocked || isOperator())
                            ? 'bg-gray-200 border-gray-400 text-gray-800 cursor-not-allowed focus:outline-none'
                            : 'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        } ${errors.loadedWeight ? 'border-red-300' : 'border-gray-300'}`}
                      />
                      {isOperator() && (
                        <p className="text-xs text-gray-500 mt-1">{t('weighing.loadedWeightOperatorInfo')}</p>
                      )}
                      {errors.loadedWeight && <p className="text-red-500 text-sm mt-1">{errors.loadedWeight}</p>}
                      {loadedBelowTareWarning && !errors.loadedWeight && (
                        <p className="text-amber-700 text-sm mt-1 bg-amber-50 px-2 py-1 rounded" role="alert">
                          {loadedBelowTareWarning}
                        </p>
                      )}
                    </div>
                    <div className="sm:col-span-4 min-w-0">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('weighing.twoStep.loadedPassDate')}
                      </label>
                      <input
                        type="date"
                        name="loadedWeighDate"
                        value={formData.loadedWeighDate}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${
                          errors.loadedWeighDate ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.loadedWeighDate && (
                        <p className="text-red-500 text-sm mt-1">{errors.loadedWeighDate}</p>
                      )}
                    </div>
                    <div className="sm:col-span-4 min-w-0">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('loads.modal.dateTime')}
                      </label>
                      <input
                        type="time"
                        name="loadedWeighTime"
                        value={formData.loadedWeighTime}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md ${
                          errors.loadedWeighTime ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.loadedWeighTime && (
                        <p className="text-red-500 text-sm mt-1">{errors.loadedWeighTime}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isOperator() && lowerWeightsColumnCount > 0 && (
              <div className="border-t pt-4">
                <div className={lowerWeightsGridClass}>
                  {showEmptyWeightInLowerGrid && (
                    <div>
                      {formUnlockedForContinueSecondPass && continueSecondPassReadOnly ? (
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-x-4 sm:items-end">
                          <div className="sm:col-span-4 min-w-0">
                            <span className={secondPassRoLabel}>{t('weighing.emptyWeight')}</span>
                            <div className={secondPassRoBox}>
                              {continueSecondPassReadOnly.emptyWeightDisplay}
                            </div>
                          </div>
                          <div className="sm:col-span-4 min-w-0">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {t('weighing.twoStep.tarePassDate')}
                            </label>
                            <input
                              type="date"
                              value={formData.tareWeighDate}
                              readOnly
                              disabled
                              className="w-full px-3 py-2 border rounded-md border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed"
                            />
                          </div>
                          <div className="sm:col-span-4 min-w-0">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {t('loads.modal.dateTime')}
                            </label>
                            <input
                              type="time"
                              value={formData.tareWeighTime}
                              readOnly
                              disabled
                              className="w-full px-3 py-2 border rounded-md border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed"
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                      <label htmlFor="emptyWeight" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('weighing.emptyWeight')}{' '}
                        {(isManagerOrAdmin() || isPeseur()) && formData.weighingPass !== 'MARCHANDISE' && '*'}
                      </label>
                      <NumericInput
                        id="emptyWeight"
                        ref={emptyWeightInputRef}
                        name="emptyWeight"
                        value={formData.emptyWeight}
                        onChange={handleInputChange}
                        placeholder={t('weighing.placeholders.emptyWeight')}
                        readOnly={!isPeseur() || formData.weighingPass === 'MARCHANDISE'}
                        disabled={!formSectionUnlocked}
                        className={`w-full px-3 py-2 border rounded-md ${
                          isPeseur()
                            ? 'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900'
                            : 'bg-gray-50 text-gray-700 cursor-not-allowed'
                        } ${errors.emptyWeight ? 'border-red-300' : 'border-gray-300'}`}
                      />
                      {!isPeseur() && (
                        <p className="text-xs text-gray-500 mt-1">{t('weighing.emptyWeightAutoFilled')}</p>
                      )}
                        </>
                      )}
                      {errors.emptyWeight && <p className="text-red-500 text-sm mt-1">{errors.emptyWeight}</p>}
                    </div>
                  )}

                  {showLoadedWeightInLowerGrid && (
                    <div>
                      <label htmlFor="loadedWeight" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('weighing.loadedWeight')}{' '}
                        {(isManagerOrAdmin() || isPeseur()) && '*'}
                      </label>
                      <NumericInput
                        ref={loadedWeightInputRef}
                        id="loadedWeight"
                        name="loadedWeight"
                        value={formData.loadedWeight}
                        onChange={handleInputChange}
                        placeholder={t('weighing.placeholders.loadedWeight')}
                        readOnly={!formSectionUnlocked || isOperator()}
                        disabled={!formSectionUnlocked}
                        className={`w-full px-3 py-2 border rounded-md ${
                          (!formSectionUnlocked || isOperator())
                            ? 'bg-gray-200 border-gray-400 text-gray-800 cursor-not-allowed focus:outline-none'
                            : 'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        } ${errors.loadedWeight ? 'border-red-300' : 'border-gray-300'}`}
                      />
                      {isOperator() && (
                        <p className="text-xs text-gray-500 mt-1">{t('weighing.loadedWeightOperatorInfo')}</p>
                      )}
                      {errors.loadedWeight && <p className="text-red-500 text-sm mt-1">{errors.loadedWeight}</p>}
                      {loadedBelowTareWarning && !errors.loadedWeight && (
                        <p className="text-amber-700 text-sm mt-1 bg-amber-50 px-2 py-1 rounded" role="alert">
                          {loadedBelowTareWarning}
                        </p>
                      )}
                    </div>
                  )}

                  {showNetWeightInLowerGrid && (
                    <div className={useStackedLowerWeightsForSecondPass ? 'max-w-sm' : ''}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('weighing.netWeight')}</label>
                      <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-medium">
                        {calculateNetWeight()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(isManagerOrAdmin() || isPeseur()) && formData.weighingPass !== 'TARE' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setTaxesAuPoidsExpanded((prev) => !prev)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-blue-100/70 transition-colors"
                  aria-expanded={taxesAuPoidsExpanded}
                  disabled={!formSectionUnlocked}
                >
                  <span className="text-sm font-medium text-blue-900">
                    {taxesAuPoidsPreview.amountTs > 0
                      ? t('loads.taxesAuPoids.amountTtcWithTs', 'Total TTC (Taxes Au Poids + TVA + TS)')
                      : t('loads.taxesAuPoids.amountTtc', 'Total TTC (Taxes Au Poids + TVA)')}{' '}
                    <span className="font-bold">{taxesAuPoidsPreview.amountTtc.toLocaleString('fr-FR')} FCFA</span>
                  </span>
                  {taxesAuPoidsExpanded ? (
                    <ChevronUpIcon className="h-5 w-5 text-blue-600 shrink-0" aria-hidden />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-blue-600 shrink-0" aria-hidden />
                  )}
                </button>
                {taxesAuPoidsExpanded && (
                  <div className="border-t border-blue-200 px-4 py-3">
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.netWeight', 'Poids net')}</dt>
                        <dd className="text-blue-900 mt-1 font-semibold">{taxesAuPoidsPreview.netTon.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t</dd>
                      </div>
                      <div>
                        <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.appliedTariff', 'Tarif appliqué')}</dt>
                        <dd className="text-blue-900 mt-1 font-semibold">
                          {taxesAuPoidsPreview.isOuvrable
                            ? t('loads.taxesAuPoids.ouvrable', 'Ouvrable')
                            : t('loads.taxesAuPoids.special', 'Spécial (sam/dim/nuit/férié)')}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.rule', 'Règle de calcul')}</dt>
                        <dd className="text-blue-900 mt-1">
                          {taxesAuPoidsPreview.netTon <= 0
                            ? t('loads.taxesAuPoids.noWeight', 'Saisir des poids valides pour calculer.')
                            : taxesAuPoidsPreview.netTon <= taxesAuPoidsPreview.ceilingTon
                              ? `${t('loads.taxesAuPoids.minRule', '≤ plafond: montant minimum')} (${taxesAuPoidsPreview.minHt.toLocaleString('fr-FR')} FCFA)`
                              : `${t('loads.taxesAuPoids.rateRule', '> plafond: tarif par tonne')} (${taxesAuPoidsPreview.rateHtPerTon.toLocaleString('fr-FR')} FCFA/t)`}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.ceiling', 'Plafond')}</dt>
                        <dd className="text-blue-900 mt-1">{taxesAuPoidsPreview.ceilingTon.toLocaleString('fr-FR')} t</dd>
                      </div>
                      <div>
                        <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.amountHt', 'Montant HT')}</dt>
                        <dd className="text-blue-900 mt-1 font-semibold">{taxesAuPoidsPreview.amountHt.toLocaleString('fr-FR')} FCFA</dd>
                      </div>
                      <div>
                        <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.amountTva', 'TVA')}</dt>
                        <dd className="text-blue-900 mt-1">{taxesAuPoidsPreview.amountTva.toLocaleString('fr-FR')} FCFA</dd>
                      </div>
                      {taxesAuPoidsPreview.amountTs > 0 && (
                        <div>
                          <dt className="text-blue-700 font-medium">{t('loads.ts.detail.title', 'Taxes Supplémentaires (TS)')}</dt>
                          <dd className="text-blue-900 mt-1">{taxesAuPoidsPreview.amountTs.toLocaleString('fr-FR')} FCFA</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
              </div>
            )}

            {(isManagerOrAdmin() || isPeseur()) && formData.weighingPass !== 'TARE' && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">{t('loads.ts.detail.title')}</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                    <Select
                      className="min-w-0 flex-1 w-full"
                      label={t('loads.ts.optionLabel')}
                      value={tsOptionCode}
                      onChange={(e) => setTsOptionCode(e.target.value)}
                      options={tsSelectOptions}
                      placeholder={t('loads.ts.optionPlaceholder')}
                      disabled={!formSectionUnlocked}
                    />
                    <div className="w-full sm:w-44 shrink-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('loads.ts.hoursLabel')}</label>
                      <NumericInput
                        value={tsHours}
                        onChange={(e) => setTsHours(e.target.value)}
                        disabled={!formSectionUnlocked}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  {hasInlineTsPreview && (
                    <p className="text-sm text-gray-600">
                      {t('loads.ts.preview', {
                        option: (() => {
                          const option = getSupplementaryTaxOption(tsOptionCode, resolvedTsOptions);
                          return option?.description || (option?.labelKey && t(option.labelKey)) || tsOptionCode;
                        })(),
                        hours: tsParsedForPreview,
                        total: tsInlineTotal.toLocaleString('fr-FR')
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {!isOperator() && (
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('weighing.notes')}
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder={t('weighing.notesPlaceholder')}
                  disabled={!formSectionUnlocked}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="flex flex-wrap items-center justify-end gap-3">
                {showPonctuelBillOnSaveHint && (
                  <p className="text-sm text-amber-900 mr-auto max-w-md">
                    {t('weighing.ponctuel.billCreatedOnSaveHint')}
                  </p>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={loading}
                  disabled={loading || !formSectionUnlocked}
                >
                  {loading ? 'Creating...' : t('weighing.registerLoad')}
                </Button>
              </div>
              {errors.general && isPrepaidBalanceError && (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {errors.general}
                </div>
              )}
            </div>
          </div>

          {!formSectionUnlocked && (
            <div className="absolute inset-0 z-10 rounded-lg bg-white/35 backdrop-blur-[2px] pointer-events-none" aria-hidden="true" />
          )}
        </div>
        </>
        )}
      </form>
    </Modal>
    <Modal
      isOpen={isOpen && insufficientPrepaidConfirm.open}
      onClose={() => resolveInsufficientPrepaidBypassConfirmation(false)}
      title={t(
        insufficientPrepaidConfirm.variant === 'zero'
          ? 'weighing.messages.zeroPrepaidConfirmTitle'
          : 'weighing.messages.insufficientPrepaidBalanceTitle',
        { defaultValue: 'Solde prépayé insuffisant' }
      )}
      size="sm"
      closeOnOverlayClick={false}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => resolveInsufficientPrepaidBypassConfirmation(false)}
          >
            {t('common.stop', { defaultValue: 'Arrêter' })}
          </Button>
          <Button
            type="button"
            variant="warning"
            onClick={() => resolveInsufficientPrepaidBypassConfirmation(true)}
          >
            {t('common.continue', { defaultValue: 'Continuer' })}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-700 leading-relaxed">
          {insufficientPrepaidConfirm.message}
        </p>
        <p className="text-xs text-gray-500">
          {t(
            insufficientPrepaidConfirm.variant === 'zero'
              ? 'weighing.messages.zeroPrepaidConfirmHint'
              : 'weighing.messages.confirmInsufficientPrepaidProceedHint',
            'Vous pouvez arrêter pour corriger l’operation, ou continuer malgré le solde insuffisant.'
          )}
        </p>
      </div>
    </Modal>
    <Modal
      isOpen={isOpen && showKnownGateDriverModal}
      onClose={() => {
        if (knownGateDriverSaving) return;
        setKnownGateDriverIdentityMatch(null);
        setKnownGateDriverIdentityMatchLoading(false);
        setShowKnownGateDriverModal(false);
      }}
      title={t('weighing.deferred.driverCreateAssignAction', { defaultValue: 'Nouveau chauffeur' })}
      size="sm"
      closeOnOverlayClick={!knownGateDriverSaving}
    >
      <div className="space-y-3">
        {knownGateDriverError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {knownGateDriverError}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('drivers.firstName', { defaultValue: 'Prénom' })} *
          </label>
          <input
            type="text"
            value={knownGateDriverForm.firstName}
            onChange={(e) => setKnownGateDriverForm((prev) => ({ ...prev, firstName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            disabled={knownGateDriverSaving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('drivers.lastName', { defaultValue: 'Nom' })} *
          </label>
          <input
            type="text"
            value={knownGateDriverForm.lastName}
            onChange={(e) => setKnownGateDriverForm((prev) => ({ ...prev, lastName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            disabled={knownGateDriverSaving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('drivers.phone', { defaultValue: 'Téléphone' })}
          </label>
          <input
            type="text"
            value={knownGateDriverForm.phone}
            onChange={(e) => setKnownGateDriverForm((prev) => ({ ...prev, phone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            disabled={knownGateDriverSaving}
          />
        </div>
        {(knownGateDriverIdentityMatchLoading || knownGateDriverIdentityMatch) && (
          <div className="rounded-md border border-teal-200 bg-teal-50/90 px-3 py-2 text-xs text-teal-950 leading-snug">
            {knownGateDriverIdentityMatchLoading ? (
              t('common.loading')
            ) : knownGateSelectedTruck?.drivers?.some((d) => d.id === knownGateDriverIdentityMatch?.id) ? (
              <>
                <span className="font-semibold">{t('weighing.driverIdentityExistsOnTruckBadge')}</span>
                {' — '}
                {t('weighing.driverIdentityExistsOnTruckDetail')}
              </>
            ) : (
              <>
                <span className="font-semibold">{t('weighing.driverIdentityExistsBadge')}</span>
                {' — '}
                {t('weighing.driverIdentityExistsHintDetail')}
              </>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setKnownGateDriverIdentityMatch(null);
              setKnownGateDriverIdentityMatchLoading(false);
              setShowKnownGateDriverModal(false);
            }}
            disabled={knownGateDriverSaving}
          >
            {t('common.cancel', { defaultValue: 'Annuler' })}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleCreateAndAssignKnownGateDriver}
            loading={knownGateDriverSaving}
          >
            {t('weighing.deferred.driverCreateAssignConfirm', { defaultValue: 'Créer et assigner' })}
          </Button>
        </div>
      </div>
    </Modal>
    <Modal
      isOpen={isOpen && showClientTruckCreateModal}
      onClose={() => {
        if (clientTruckCreateSaving) return;
        setShowClientTruckCreateModal(false);
      }}
      title={t('trucks.addTruck')}
      size="sm"
      closeOnOverlayClick={!clientTruckCreateSaving}
    >
      <div className="space-y-3">
        {clientTruckCreateError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {clientTruckCreateError}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('trucks.plateNumber')} *
          </label>
          <input
            type="text"
            value={clientTruckForm.plateNumber}
            onChange={(e) => setClientTruckForm((prev) => ({ ...prev, plateNumber: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={clientTruckCreateSaving}
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowClientTruckCreateModal(false)}
            disabled={clientTruckCreateSaving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleCreateClientTruck}
            loading={clientTruckCreateSaving}
          >
            {t('common.create')}
          </Button>
        </div>
      </div>
    </Modal>
    <Modal
      isOpen={isOpen && showClientDriverCreateModal}
      onClose={() => {
        if (clientDriverCreateSaving) return;
        setClientDriverIdentityMatch(null);
        setClientDriverIdentityMatchLoading(false);
        setShowClientDriverCreateModal(false);
      }}
      title={t('drivers.addDriver')}
      size="sm"
      closeOnOverlayClick={!clientDriverCreateSaving}
    >
      <div className="space-y-3">
        {clientDriverCreateError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {clientDriverCreateError}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('drivers.firstName')} *
          </label>
          <input
            type="text"
            value={clientDriverForm.firstName}
            onChange={(e) => setClientDriverForm((prev) => ({ ...prev, firstName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={clientDriverCreateSaving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('drivers.lastName')} *
          </label>
          <input
            type="text"
            value={clientDriverForm.lastName}
            onChange={(e) => setClientDriverForm((prev) => ({ ...prev, lastName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={clientDriverCreateSaving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('drivers.phone')}
          </label>
          <input
            type="text"
            value={clientDriverForm.phone}
            onChange={(e) => setClientDriverForm((prev) => ({ ...prev, phone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={clientDriverCreateSaving}
          />
        </div>
        {(clientDriverIdentityMatchLoading || clientDriverIdentityMatch) && (
          <div className="rounded-md border border-teal-200 bg-teal-50/90 px-3 py-2 text-xs text-teal-950 leading-snug">
            {clientDriverIdentityMatchLoading ? (
              t('common.loading')
            ) : drivers.some((d) => d.id === clientDriverIdentityMatch?.id) ? (
              <>
                <span className="font-semibold">{t('weighing.driverIdentityExistsOnTruckBadge')}</span>
                {' — '}
                {t('weighing.driverIdentityExistsOnTruckDetail')}
              </>
            ) : (
              <>
                <span className="font-semibold">{t('weighing.driverIdentityExistsBadge')}</span>
                {' — '}
                {t('weighing.driverIdentityExistsHintDetail')}
              </>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setClientDriverIdentityMatch(null);
              setClientDriverIdentityMatchLoading(false);
              setShowClientDriverCreateModal(false);
            }}
            disabled={clientDriverCreateSaving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleCreateClientDriver}
            loading={clientDriverCreateSaving}
          >
            {t('common.create')}
          </Button>
        </div>
      </div>
    </Modal>
    <PeseurPonctuelClientModal
      isOpen={showPonctuelCreateModal}
      onClose={() => setShowPonctuelCreateModal(false)}
      onClientCreated={handlePonctuelClientCreated}
    />
    <PeseurQuickProductModal
      isOpen={showQuickProductModal}
      onClose={() => setShowQuickProductModal(false)}
      onProductCreated={handleQuickProductCreated}
    />
    </>
  );
};

export default AddTicketModal;